import React, {useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import { canReloadInPlace } from '../utils/previewReload';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// Loaded with the first diagram: mermaid is large and most pages have none.
const Mermaid = React.lazy(() => import('../Mermaid'));
import Agents from '../pages/Agents';
import MiaPlatformSelector from './MiaPlatformSelector';
import CreateProjectModal from './CreateProjectModal';
import NewProjectDialog from './sandbox/NewProjectDialog';
import ProjectLoading from './sandbox/ProjectLoading';
import { AddServiceChooser, useAddService } from './home/AddService';
import ProjectResourcesTree from './ProjectResourcesTree';
import PreviewLogs from './PreviewLogs';
import HelmPreviewPanel from './HelmPreviewPanel';
import FlowVisualization from './FlowVisualization';
import Modal from './Modal';
import ExternalLinkButton from './design/ExternalLinkButton';
import Icon from './icons/Icon';
import ExpoGoButton from './preview/ExpoGo';
// The old logs footer's unscoped classes other views still use (see the file).
import './legacyGlobalClasses.css';
import CodeViewWithLogger from './CodeViewWithLogger';
import { filesLineFromSearch, filesPathFromSearch, fullFilesPath, pathWithinFocus } from '../utils/filesPath';
import { filesScopeOf } from '../utils/scopeModel';
import { useFilesLinkToService, useRouteCodeFile, useSelectionUrlSync } from '../hooks/useCodeLinks';
import SplitViewToggle from './SplitViewToggle';
import DynamicTaskPanel from './DynamicTaskPanel';
import UnifiedDashboard from './UnifiedDashboard';
import UnifiedTopBar from './UnifiedTopBar';
import PushModal from './PushModal';
import WorkspaceView, { WorkspaceTitle } from './workspace/WorkspaceView';
import InThisProjectPanel from './workspace/InThisProjectPanel';
import useProjectPresence from '../hooks/useProjectPresence';
import usePreviewWatchdog from '../hooks/usePreviewWatchdog';
import useHomeViewUrl from '../hooks/useHomeViewUrl';
import { PREVIEW_UNREACHABLE_PREFIX, isAppOwnAnswer, isPreviewReady } from '../utils/previewReadiness';
import { loadProjectServices, refreshProjectServices } from '../hooks/useProjectServices';
import { decidePreviewOpen, previewRunStateFor } from '../utils/previewOpenState';
import { homeLeavesCodeTab, initialActiveTab, landsOnHome, servicePickKeepsCode } from '../utils/urlWins';
import { projectNeedsReload } from '../utils/projectReload';
import { sessionIdFor, startService } from '../services/SandboxService';
import PreviewStoppedState from './PreviewStoppedState';
import CliTryIt from './preview/CliTryIt';
import { cliServiceOf } from '../utils/cliServices';
import { useCompact, useNarrowElement } from '../hooks/useMediaQuery';
import useChangesCount from '../hooks/useChangesCount';
import useQualityGate, { blockedCount } from '../hooks/useQualityGate';
import useAppConfigSignal from '../hooks/useAppConfigSignal';
import useDataStoresSignal from '../hooks/useDataStoresSignal';
import useMcpServersSignal from '../hooks/useMcpServersSignal';
import { currentWorkspaceTab, searchWithView, viewFromHistory, viewFromSearch, viewKey } from '../utils/workspaceModel';

// Project Home's views in `?view=` (dashboardOverviewMode).
import { tabSignalsOf } from '../utils/projectTabsModel';
import usePlanViewGate from '../hooks/usePlanViewGate';
import { codeGate, hiddenTabNote, isTabHidden } from '../utils/projectTabsModel';
import FollowController from './collab/FollowController';
import useSharedPreview from '../hooks/useSharedPreview';
import NotesModal from './collab/NotesModal';
import { getSuggestions } from '../services/CollabService';
import { arrivedAt, markArrived, markLeft } from '../utils/handoffLeave';
import { pendingCount } from '../utils/collab3Model';
import { joinRoute, serviceNameOf } from '../utils/collab3Model';
import DashboardView from './DashboardView';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaPlatformAuth from '../auth/MiaPlatformAuth';
import { listAdkSessions, parseVfsSessionId } from '../services/AdkSessionsService';
import indexedDbFileStorage, {GIT_STATE} from '../utils/indexedDbFileStorage';
import * as vfsClient from '../utils/vfsClient';
import { connectProjectTreeSync } from '../utils/vfsSync';
import { drainPendingVfsResync } from '../utils/pendingVfsResync';
import { forceResyncServiceFromVfs } from '../utils/forceResyncServiceFromVfs';
import { DEFAULT_MICROSERVICE_BRANCH } from '../constants/gitConstants';
import indexedDbCollectionStorage from '../utils/indexedDbCollectionStorage';
import {usePipelineDeployment} from '../hooks/usePipelineDeployment';
import {usePortMappings} from '../hooks/usePortMappings';
import {useProjectConfiguration} from '../hooks/useProjectConfiguration';
import useProjectAccess from '../hooks/useProjectAccess';
import ShareDialog, {ReadOnlyNotice} from './ShareDialog';
import {applyUnifiedDiff, parseUnifiedDiff} from '../utils/diffParser';
import {scheduleAutoCapture, autoCaptureThumbnail, getProjectThumbnail} from '../utils/screenshotCapture';
import { shouldAutoCaptureThumbnail } from '../utils/thumbnailEligibility';
import {getBffHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import {injectErrorCapture as injectIframeErrorCapture} from '../utils/iframeErrorCapture';
import {
  detectServiceType,
  getContainerRoutingInfo
} from '../utils/frameworkDetection';
import { getGitRef, getResourceGitBranch } from '../utils/gitBranchStorage';
import { decodeServiceNameFromUrl } from '../utils/urlPathSegment';
import config from '../config';
import ServiceLogs from './sandbox/ServiceLogs';
import { runItems, startProject } from './sandbox/projectRun';
import useRunnablePreview from '../hooks/useRunnablePreview';
import { isRunnableTab, runnableNameOf } from '../utils/runnablePreview';
import { projectStartKey, runStart } from './sandbox/startRuns';
import { servicePath } from '../utils/projectOverview';
import { shortServiceName } from '../utils/gitRepoSource';
import './Canvas.css';

import PreviewRenderProgress from './PreviewRenderProgress';
import {
  getUserIdFromStorage,
  saveUserIdToStorage,
  getOrCreateUserId,
  buildSessionId,
  getSelectedSession,
  setSelectedSession,
  getSessionFromStorage,
  clearAllPreviewSessions,
  saveSessionToStorage,
  isProduction,
  getContainerApiUrl,
  pollRenderStatus,
  isEmptyServiceError,
  sessionIdFromPreviewUrl,
} from '../utils/previewSession';
import {
  getPipelineStatusIcon,
  getPipelineStatusClass,
  getPodStatusIcon,
  getPodStatusClass,
} from '../utils/pipelineStatus';
import { devLog } from '../utils/devLog';
import { isCorruptedStoredFilePath } from '../utils/codeRoute';
import { isServiceFolderName, openPreviewPlan, serviceKeyIn, withoutNonServices } from '../utils/projectLayout';
import { SERVICES_CHANGED_EVENT } from '../hooks/useServiceFolders';
import { previewResult, projectLeft, projectOpened } from '../analytics/journey';


/**
 * Helper function to extract GitLab project ID from repository URL
 */
const extractGitLabProjectId = (repoUrl) => {
  if (!repoUrl) {
    console.warn('extractGitLabProjectId: No repository URL provided');
    return null;
  }
  
  devLog('🔍 Extracting GitLab project ID from:', repoUrl);
  
  try {
    // Handle HTTPS URLs: https://gitlab.com/group/subgroup/project.git
    // Supports nested groups: https://gitlab.com/group/subgroup/nested/project.git
    const httpsMatch = repoUrl.match(/https?:\/\/[^/]+\/(.+?)(?:\.git)?$/);
    if (httpsMatch) {
      const projectPath = httpsMatch[1];
      devLog('✅ Extracted project path (HTTPS):', projectPath);
      return encodeURIComponent(projectPath);
    }
    
    // Handle SSH URLs: git@gitlab.com:group/subgroup/project.git
    // Supports nested groups: git@gitlab.com:group/subgroup/nested/project.git
    const sshMatch = repoUrl.match(/git@[^:]+:(.+?)(?:\.git)?$/);
    if (sshMatch) {
      const projectPath = sshMatch[1];
      devLog('✅ Extracted project path (SSH):', projectPath);
      return encodeURIComponent(projectPath);
    }
    
    console.warn('⚠️ URL format not recognized:', repoUrl);
  } catch (error) {
    console.error('❌ Error extracting GitLab project ID:', error);
  }
  
  return null;
};

/**
 * Canvas Component - Full-screen document and code viewer with Lovable-style tabs
 * Features: Preview, Code (file explorer + editor)
 */
/** `?view=files` opens Project Home on its Files view, `?view=logs` (sandbox-first) on its Logs view; anything else is the Dashboard. */
function homeViewFromSearch(search) {
  const view = new URLSearchParams(search || '').get('view');
  if (view === 'files') { return 'files'; }
  if (view === 'logs' && config.sandboxFirst) { return 'logs'; }
  return 'dashboard';
}

/** The preview loader posts at most one render per service in this window (see loadServicePreview). */
const RENDER_ONCE_MS = 20_000;

const Canvas = forwardRef(({content: rawContent, isVisible, onClose, generatedFiles, onProjectContextChange, chatSidebarWidth = 400, onNavigateHome, projectContext, initialMicroservice, initialBranch, initialTab, onSelectionChange, onNewConversation, onShowHistory, onAskAI}, ref) => {
  // `content` is a legacy, optional "AI-generated artifact" payload (an
  // image/video/document the assistant generated) — it has nothing to do
  // with whether a project's Code/Preview tabs should render. `canvasContent`
  // in App.jsx defaults to `null` whenever there's no such artifact, which
  // used to make the WHOLE Canvas shell (tabs, file tree, service picker —
  // everything) render as null, since the old top-level guard bailed the
  // entire component on it. A fresh vfs project has no such artifact
  // and never will unless the user asks for one — the shell must still show.
  const content = rawContent || {};
  // Hooks for navigation and URL management
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  // The file a Code-tab URL names (`/code/<tenant>/<project>/<service>/<branch>/code/<file>#L<n>`), see utils/codeRoute.js.
  const routeCodeFile = useRouteCodeFile();

  // Empty-project state: lets the "Empty project" panels below jump
  // straight to /chat, mirroring the Chat/Code Segmented switcher's own
  // navigation in App.jsx (same '/chat' target + conversationId carry-over,
  // read directly off the URL here since Canvas.jsx doesn't own that state).
  const handleSwitchToChat = () => {
    const conversationId = new URLSearchParams(location.search).get('conversationId');
    navigate(conversationId ? `/chat?conversationId=${encodeURIComponent(conversationId)}` : '/chat');
  };

  // Guards the "sync selection back to URL" effect against a race on initial
  // mount: that effect fires as soon as selectedMicroservice changes — which
  // includes the very FIRST render, where selectedResource is still null
  // because the URL-driven "Initialize selections from URL parameters" effect
  // below hasn't run yet (it needs `configuration` to be loaded first, which
  // isn't ready on mount). Left unguarded, the sync-to-URL effect fires with
  // a null microservice and immediately rewrites a deep link like
  // /code/:tenant/:project/:microservice/:branch/:tab down to just
  // /code/:tenant/:project — destroying the very URL params the other effect
  // was about to read, before it ever gets the chance. Stays true (blocking
  // sync-to-URL) only while there's a microservice in the URL still waiting
  // to be resolved into a selection; a 15s safety-net timeout prevents a
  // permanent block if `configuration` never loads for some reason.
  const urlInitPendingRef = useRef(Boolean(initialMicroservice));
  useEffect(() => {
    if (!urlInitPendingRef.current) return undefined;
    const timer = setTimeout(() => { urlInitPendingRef.current = false; }, 15000);
    return () => clearTimeout(timer);
  }, []);

  // Default to 'preview' tab on first load, or use initial tab from URL.
  // Falls back to the last-used tab from localStorage (not straight to
  // 'preview') when the URL carries none — real-live confirmed bug: a hard
  // refresh on `/code/:tenantId/:projectId` (no `:tab` segment at all)
  // always started this at 'preview' regardless of which tab the user was
  // actually on, flashing the Preview icon/spinner active before the
  // "Sync activeTab with initialTab from URL" effect below (which only
  // fires once a separate localStorage-driven effect rewrites the URL to
  // include a real `:tab` segment) corrected it a render or two later.
  // 'preview'/'code' is a generic, project-agnostic view-mode choice (not
  // project-specific data like a service name), so an unscoped localStorage
  // read here is safe even before `params.projectId` is known to match.
  // A Home link (`?view=logs|files|dashboard` in the arriving URL) opens on the Preview tab, where Home lives; a URL
  // naming a service keeps its own tab (utils/urlWins.js).
  const [activeTab, setActiveTab] = useState(() => initialActiveTab({
    search: location.search,
    serviceInUrl: Boolean(params.microservice),
    initialTab,
    rememberedTab: localStorage.getItem('mia_activeTab'),
  }));
  // task #146: Dashboard/Files switcher for the project overview screen
  // (the panel shown when a 'configuration' resource is selected on the
  // preview tab) — scoped per-project so switching projects doesn't leave
  // the user stuck looking at the previous project's Files view.
  // Project Home has two views (Dashboard / Files). The chosen one lives in the URL as `?view=dashboard|files`
  // so a reload, a shared link or the back button lands on the same view.
  const [dashboardOverviewMode, setDashboardOverviewMode] = useState(() => homeViewFromSearch(location.search));
  // The view the URL asked for when the page was opened; applied once, the first time a project is known.
  const initialHomeViewRef = useRef(new URLSearchParams(location.search).get('view') ? homeViewFromSearch(location.search) : null);
  // Set right before switching to 'files' from a Dashboard card (a Services
  // row, a folder card's "Browse files" link) so ProjectFilesBrowser opens
  // straight into that folder instead of the bare collapsed root.
  const [dashboardFilesFocusPath, setDashboardFilesFocusPath] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastOpenedFile, setLastOpenedFile] = useState(() => {
    // Restore last opened file from localStorage
    const key = `lastOpenedFile_${params.projectId}_${params.microservice ? decodeServiceNameFromUrl(params.microservice) : params.microservice}`;
    const storedPath = localStorage.getItem(key);
    
    // Validate: reject if it looks like a corrupted URL path
    // (A Dockerfile or Makefile has no extension and is a real file.)
    if (isCorruptedStoredFilePath(storedPath)) {
      devLog('🚨 Detected corrupted localStorage path, clearing:', storedPath);
      localStorage.removeItem(key);
      return null;
    }
    
    return storedPath || null;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const previewContainerRef = useRef(null);
  const loadingPreviewRef = useRef(new Set()); // Track services currently being loaded
  const lastLoadedServiceRef = useRef(null); // Track last successfully loaded service
  // BUG FIX (live-reported + confirmed in real preview-runner logs: two
  // `/api/render` calls for the same session landing ~1s apart, the second
  // killing/respawning the dev server the first one had JUST brought up —
  // "the updated app shows for less than a second, then goes not
  // available"): this used to be the SAME ref the file-loading effect below
  // uses for its own, differently-shaped dedup key
  // (`${projectId}|${serviceName}`, 2 parts) — every write from either
  // effect clobbered the other's guard value, so a check against the
  // WRONG key shape almost always missed, defeating both effects' dedup
  // most of the time. Split into its own ref so the two stop stepping on
  // each other.
  const lastLoadedFilesServiceRef = useRef(null); // Track last service the IndexedDB-files effect (below) loaded for
  const errorCountRef = useRef({}); // Track consecutive error count per service
  const autoTriggeredPullRef = useRef(new Set()); // Track services for which we've auto-triggered pull to prevent duplicates
  const lastProjectContextRef = useRef(null); // Track last sent project context to prevent redundant updates
  const receivingContextFromParentRef = useRef(false); // Track if we're currently receiving context from parent
  const handlingIframeErrorRef = useRef(new Set()); // Track if we're already handling an iframe error to prevent duplicate renders
  const previewLoadTimeoutRef = useRef(null); // Debounce timeout for preview loading
  const lastProjectIdRef = useRef(null); // Track last project ID to detect project switches
  // task #107 fix: two independent sites below both consume
  // sessionStorage's 'mia_pending_initial_prompt' (one fires after files are
  // pulled from git, the other after the preview finishes rendering) with no
  // shared guard — sessionStorage getItem+removeItem isn't atomic across two
  // separate React callbacks, so when both fire in the same window (routine
  // for a vfs-backed Home-created project) BOTH would see the prompt as
  // still-present and BOTH dispatch it, sending the same "build X" prompt to
  // the agent as two separate turns. Since vfs_create_project isn't
  // idempotent, two turns each calling it creates two real projects, and the
  // user lands on whichever tool-call response arrives last (task #107,
  // found live: Home-created projects sometimes landed on a second, empty
  // project instead of the one the agent actually wrote files to). This ref
  // makes "has the pending initial prompt already been consumed" a single,
  // synchronously-checked fact shared by both sites.
  const initialPromptConsumedRef = useRef(false);

    // Mia Platform tenant and project state
    const [tenants, setTenants] = useState([]);
    const [projects, setProjects] = useState([]);
    const [showDashboardTab, setShowDashboardTab] = useState(false);

    // Load dashboard tab visibility setting
    useEffect(() => {
    // Initial load
    const loadDashboardSetting = () => {
      const saved = localStorage.getItem('showDashboardTab');
      if (saved !== null) {
        setShowDashboardTab(saved === 'true');
      }
    };

    loadDashboardSetting();

    // Listen for changes
    const handleToggleChange = (event) => {
      if (event.detail && event.detail.showDashboardTab !== undefined) {
        setShowDashboardTab(event.detail.showDashboardTab);
      }
    };

    window.addEventListener('featureToggleChanged', handleToggleChange);
    return () => {
      window.removeEventListener('featureToggleChanged', handleToggleChange);
    };
    }, []);

    const [projectsPagination, setProjectsPagination] = useState(null);
  const [projectsCurrentPage, setProjectsCurrentPage] = useState(1);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  // Real live bug: these used to lazy-init from localStorage's LAST-EVER
  // selected tenant/project on every Canvas mount, completely independent
  // of the `projectContext` prop (App.jsx's `canvasProjectContext`, derived
  // from the actual URL/navigation this mount is for). Canvas is mounted
  // unconditionally (not gated on `projectContext` being resolved yet — see
  // its render site in App.jsx), so this guess fired FIRST and kicked off
  // its own real fetch chain (revisions, full configuration) for a STALE,
  // possibly totally unrelated project, before the "Load project context
  // from parent" effect below got a chance to correct it a render or two
  // later. Confirmed live: starting a brand-new chat from Home visibly
  // loaded an old abandoned project's config/tree (several wasted requests,
  // a couple of real 404s) before switching to the actual new project.
  // `projectContext` is this component's ONLY real caller-provided source
  // of truth for the initial project (single render site, no other
  // consumer) — the effect below already owns writing `selectedTenant`/
  // `selectedProject` from it; starting at `null` here removes the second,
  // stale writer instead of racing it.
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  // Analytics: the project view is open on this project (a return on a later day, the start of a working session), until it
  // is left. Only the id, as an opaque key, and the creation date are used — see analytics/journey.js.
  const selectedProjectCreatedAt = selectedProject?.raw?.createdAt || selectedProject?.raw?.created_at || null;
  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId) { return undefined; }
    projectOpened(projectId, { createdAt: selectedProjectCreatedAt });
    return () => projectLeft(projectId);
  }, [selectedProject?.id, selectedProjectCreatedAt]);

  // Project sharing (docs/project-sharing.md): the caller's role on this project. Only a definite
  // 'viewer' makes the view read-only; unknown (loading, no vfs-service, legacy project) stays editable.
  const {access: projectAccess, setAccess: setProjectAccess} = useProjectAccess(selectedProject?.id);
  const isViewOnly = projectAccess?.role === 'viewer';

  // Task #217: "no service selected AND no chat has ever touched this
  // project" should land on the Dashboard tab instead of the (still
  // correct, unchanged) "Empty project" preview message that a project
  // WITH chat activity already gets. `null` = not yet resolved for the
  // current project (treated the same as `false` below — the SAFER of the
  // two behaviors, matching this file's established "loaded AND empty, not
  // just empty" pattern elsewhere — so a slow check never flashes Dashboard
  // prematurely for a project that does have history). Reset to `null`
  // whenever the selected project changes; see the effect right below that
  // resolves it via a real ADK session count for this project. Declared
  // here (right after `selectedProject`, not near the top of the component)
  // because its effect below reads `selectedProject?.id` — declaring it
  // earlier hit `selectedProject`'s temporal dead zone and crashed the
  // whole Canvas render ("Cannot access 'selectedProject' before
  // initialization"), live-confirmed the moment this landed.
  const [projectHasNoConversations, setProjectHasNoConversations] = useState(null);

  // Task #217: resolve projectHasNoConversations for the current project via
  // a real ADK session count — every vfs-backed chat session id is
  // `vfs-{projectId}` (see AdkSessionsService.js's parseVfsSessionId; #211
  // collapsed it to project-scoped-only, no per-service suffix), so
  // filtering this user's full session list down to ones whose parsed
  // projectId matches is the same "does this project have any conversation
  // history" signal the History page itself is built on — no new backend
  // endpoint needed. `listAdkSessions` has no server-side project filter
  // (it's scoped by app/agent, not project), so this fetches everything and
  // filters client-side, same as Memories.jsx already does.
  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId) {
      setProjectHasNoConversations(null);
      return undefined;
    }
    setProjectHasNoConversations(null); // reset while resolving for the new project
    let cancelled = false;
    (async () => {
      try {
        const userInfo = miaPlatformAuth.getStoredUserInfo();
        const userId = userInfo?.sub || userInfo?.id || userInfo?.email;
        if (!userId) {
          if (!cancelled) setProjectHasNoConversations(false); // unknown user — stay on the safe/existing behavior
          return;
        }
        const sessions = await listAdkSessions(userId);
        if (cancelled) return;
        const hasAny = (sessions || []).some(s => parseVfsSessionId(s.id)?.projectId === projectId);
        setProjectHasNoConversations(!hasAny);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Failed to resolve conversation history for project (defaulting to existing empty-state behavior):', error);
        if (!cancelled) setProjectHasNoConversations(false); // fetch failure — stay on the safe/existing behavior, never guess Dashboard
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState(null);
  const [projectsError, setProjectsError] = useState(null);
  
  // Create project modal state
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isCreateServiceModalOpen, setIsCreateServiceModalOpen] = useState(false);
  
  // Mia Platform revisions and microservices state
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState(() => {
    const saved = localStorage.getItem('mia_selectedRevision');
    return saved ? JSON.parse(saved) : null;
  });
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  
  const [configuration, setConfiguration] = useState(null); // Full project configuration
  const [selectedResourceGitBranch, setSelectedResourceGitBranch] = useState(null); // Current git branch for selected resource
  const [configLoading, setConfigLoading] = useState(false);
  const [pendingResourceReselect, setPendingResourceReselect] = useState(null); // Track resource to re-select after refresh
  
  // Full project configuration from Console API (includes dashboards, links, etc.)
  const [fullProjectConfig, setFullProjectConfig] = useState(() => {
    // Try to load from localStorage on init
    const saved = localStorage.getItem('mia_fullProjectConfig');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Collections state
  const [collections, setCollections] = useState({}); // Merged collections (config + localStorage)
  const [selectedCollection, setSelectedCollection] = useState(null); // Selected collection for editing
  
  // Frontend preview state - Multi-tab support
  const [previewTabs, setPreviewTabs] = useState([]); // Array of {serviceName, url, sessionId, containerPort, framework, loading, error, iframeKey, service}

  // Owns selectedResource + its two writers (auto-select-first-service effect,
  // stuck-tab retrigger effect) — see useProjectConfiguration.js doc comment
  // for why the two writers are co-located here but not merged into one guard.
  // Whether this project lands on its Home (utils/urlWins.js landsOnHome: a project URL naming no service, or a Home
  // `?view=`), decided once when its URL first arrives: from then on the URL carries the view whatever is selected, so
  // it no longer says how the project was opened. Opening a project never starts or renders a preview by itself.
  const homeRequestRef = useRef({projectId: null, value: false});
  if (homeRequestRef.current.projectId !== params.projectId) {
    homeRequestRef.current = {projectId: params.projectId, value: landsOnHome({search: location.search, serviceInUrl: Boolean(params.microservice)})};
  }
  const {selectedResource, setSelectedResource, selectHome} = useProjectConfiguration({
    preferHome: homeRequestRef.current.value,
    configuration,
    fullProjectConfig,
    selectedProject,
    activeTab,
    initialMicroservice,
    previewTabs,
    loadingPreviewRef,
    projectIdFromUrl: params.projectId,
    serviceFromUrl: params.microservice ? decodeServiceNameFromUrl(params.microservice) : null,
  });
  const [activePreviewTab, setActivePreviewTab] = useState(0); // Index of active preview tab
  const [previewNeedsRefresh, setPreviewNeedsRefresh] = useState(false); // FIX #3: Track if preview needs refresh for NEW files
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewSessionId, setPreviewSessionId] = useState(null);
  const [previewContainerPort, setPreviewContainerPort] = useState(3001); // Track container port (Frontend: 3001=React, 3002=Next.js, 3003=ReactNative, 3004=Angular, 3005=Vue, 3006=jQuery, 3007=ASP.NET; Backend: 3011=Node, 3012=Go, 3013=Python, 3014=Java, 3015=.NET, 3016=Rust, 3017=PHP, 3018=Kotlin, 3019=C++, 3020=Q#, 3021=Elixir, 3030=CRUD)
  
  // Track currently selected session
  const [selectedSession, setSelectedSessionState] = useState(() => {
    return getSelectedSession();
  });
  const [containerLogs, setContainerLogs] = useState({}); // Store logs for each container by sessionId
  const [expandedContainer, setExpandedContainer] = useState(null); // Track which container's logs are expanded
  const [logsExpanded, setLogsExpanded] = useState(() => {
    // Initialize from localStorage, default to false (minimized)
    try {
      const saved = localStorage.getItem('mia_logsExpanded');
      return saved !== null ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  }); // Track if preview logs panel is expanded
  const [previewLoading, setPreviewLoading] = useState(false);
  // Mirrors previewTabs[].isEmptyService for the single/no-tabs preview
  // path (previewTabs.length === 0) — that path had no equivalent flag,
  // so a genuinely empty service fell through to the generic loading/idle
  // render with no explicit "empty project" message. See the PRE-FLIGHT
  // check in loadServicePreview below (vfsClient.isServiceEmpty).
  const [previewIsEmptyService, setPreviewIsEmptyService] = useState(false);
  const [compilationStatus, setCompilationStatus] = useState(null); // 'syncing', 'compiling', 'ready'
  const [previewError, setPreviewError] = useState(null);
  const [isCapturingThumbnail, setIsCapturingThumbnail] = useState(false); // Track thumbnail capture state
  const [buildErrorModal, setBuildErrorModal] = useState({ isOpen: false, error: null, serviceName: null }); // Build error modal state (still used for the "could not start" manual-click fallback)
  // Auto-detected build/runtime errors (vite overlay, runtime crash, log-scanned
  // build failure) no longer pop the blocking modal — per explicit user request,
  // they just highlight PreviewLogs' existing "Ask AI" button so the fix stays
  // one click away without interrupting the user.
  const [pendingFixError, setPendingFixError] = useState(null); // { error, serviceName } | null
  // Tracks which "serviceName:sessionId" keys have already shown the build error modal.
  // Lives in Canvas (not in PreviewLogs) so it survives PreviewLogs unmount/remount
  // when the user switches tabs, preventing the modal from immediately reopening.
  const buildErrorShownRef = useRef(new Set());

  // Tracks which service names `showExternalPreview` has already forced the
  // user onto the Preview tab for, in THIS Canvas instance's lifetime. Real,
  // live-reported regression: the agent's `vfs_get_preview_url` tool is
  // documented to be called AGAIN, possibly several times, while a long
  // build is still in progress ("call again in a moment") — every one of
  // those SUCCESSFUL calls (not just the first) invoked this same handler,
  // which unconditionally forced `setSelectedResource`/`setActiveTab('preview')`
  // — yanking the user back to Preview on every single check-in, even after
  // they had deliberately navigated to the Dashboard (or anywhere else) in
  // the meantime. Only the FIRST notification for a given service should
  // grab the user's attention like that; every later one just keeps the tab's
  // own data (url/session) fresh in the background, same as any other
  // preview update — see showExternalPreview below.
  const externalPreviewShownRef = useRef(new Set());

  // When a tab starts a new build (loading → true), clear its shown-key so the
  // error modal can appear again if the new build also has errors.
  useEffect(() => {
    previewTabs.forEach(tab => {
      if (tab.loading) {
        buildErrorShownRef.current.delete(`${tab.serviceName}:${tab.sessionId || 'no-session'}`);
      }
    });
  }, [previewTabs]);
  
  // Note: userId is now persisted in localStorage (see helper functions above)
  // This ensures consistent session IDs across page reloads and eliminates session mismatch warnings
  const [configError, setConfigError] = useState(null);
  const [forceGitFetch, setForceGitFetch] = useState(false); // Flag to force git fetch
  const [iframeKey, setIframeKey] = useState(Date.now()); // Key to force iframe reload
  const [previewUrlInput, setPreviewUrlInput] = useState(''); // Editable preview URL
  const [isEditingPreviewUrl, setIsEditingPreviewUrl] = useState(false); // URL editing mode
  const [tabUrlInputs, setTabUrlInputs] = useState({}); // Editable URLs per tab
  const [editingTabUrl, setEditingTabUrl] = useState(null); // Which tab URL is being edited
  const [activeBackendService, setActiveBackendService] = useState(null); // Track backend service for incremental updates
  

  // Helper function to extract path from full URL (excluding container preview base path)
  const getUrlPath = (fullUrl) => {
    try {
      const url = new URL(fullUrl);
      const fullPath = url.pathname + url.search + url.hash;
      // Remove container preview base path (e.g., /preview-50000)
      const match = fullPath.match(/^\/preview-\d+(\/.*)?$/);
      if (match) {
        return match[1] || '/';
      }
      return fullPath || '/';
    } catch (e) {
      return '/';
    }
  };

  // Helper function to get base URL (protocol + host + container path)
  const getBaseUrl = (fullUrl) => {
    try {
      const url = new URL(fullUrl);
      const fullPath = url.pathname;
      // Extract container preview base path (e.g., /preview-50000)
      const match = fullPath.match(/^(\/preview-\d+)/);
      if (match) {
        return url.origin + match[1];
      }
      return url.origin;
    } catch (e) {
      return fullUrl;
    }
  };

  // Helper function to construct full URL from base and path
  const constructFullUrl = (baseUrl, path) => {
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    return baseUrl + path;
  };
  const iframeRef = useRef(null);

  // One service state for the header, the bottom bar and the Preview: see hooks/usePreviewWatchdog.js.
  const previewTabsRef = useRef(previewTabs);
  previewTabsRef.current = previewTabs;
  const { sharedReadyFor, canRun: canRunServices } = usePreviewWatchdog({
    projectId: activeTab === 'preview' && previewTabs.length > 0 ? (selectedProject?.id || null) : null,
    previewTabs,
    setPreviewTabs,
    onShown: (serviceName) => loadingPreviewRef.current.delete(serviceName),
  });
  // Pinned runnables (a compose file or Dockerfile pinned in a service, its own runner session) are Preview entries of
  // their own, under their owner: hooks/useRunnablePreview.js, utils/runnablePreview.js.
  const runnablePreview = useRunnablePreview({
    projectId: selectedProject?.id && vfsClient.isVfsBackedProject(selectedProject.id) ? selectedProject.id : null,
    owners: Object.values(configuration?.services || {}).map((service) => service?.name).filter(Boolean),
    setPreviewTabs,
    setActivePreviewTab,
  });
  // Shows a pinned runnable's preview (`entry`, runnablePreview.resolve's): selected as a service of its own (its owner
  // as the resource's `name`, so the configuration keeps the selection), its tab with the session's previewUrl.
  // `instead`: shown in place of its owner (the owner's own session does not serve its app): the owner's tab that shows
  // nothing yet goes away.
  const openRunnablePreview = (entry, route = '/', { instead = false } = {}) => {
    if (!entry) { return; }
    setWorkspaceView(null);
    if (instead) { setPreviewTabs((tabs) => tabs.filter((tab) => !(tab.serviceName === entry.owner && !tab.url))); }
    if (selectedResource?.data?.name !== entry.serviceName) {
      setSelectedResource({
        type: 'service',
        name: entry.owner,
        data: { name: entry.serviceName, displayName: entry.label, vfsBacked: true, pinnedRunnable: true, owner: entry.owner, runnable: entry.runnable },
      });
    }
    if (activeTab !== 'preview' && splitViewMode !== 'split') { setActiveTab('preview'); }
    runnablePreview.show(entry, { route });
  };
  // A runnable opened before its pinned file was known (a link, a reload): its file, label and payload once they are.
  useEffect(() => {
    const data = selectedResource?.data;
    if (!data?.pinnedRunnable || data.runnable) { return; }
    const entry = runnablePreview.entries.find((item) => item.serviceName === data.name && item.runnable);
    if (!entry) { return; }
    setSelectedResource({ ...selectedResource, data: { ...data, displayName: entry.label, runnable: entry.runnable } });
    patchPreviewTab(entry.serviceName, { displayName: entry.label, runnable: entry.runnable });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runnablePreview.entries, selectedResource?.data?.name]);
  const pollRenderStatusShared = (serviceName, containerBaseUrl, sessionId, onProgress) => (
    pollRenderStatus(containerBaseUrl, sessionId, onProgress, undefined, { isReady: sharedReadyFor(serviceName) })
  );
  // Where a service is, from the shared store, WAITING for its first answer: on a cold page load the store has no
  // answer yet, and "not known yet" used to be read as "no session", so the Preview posted /api/render and started an
  // app the user had stopped. `state`: ready | stopped | building | running | crashed | failed | absent, or null
  // when vfs-service cannot tell (then the caller does what it did before).
  // Always a fresh answer (the same request every caller shares): a snapshot from before a Stop on the dashboard still
  // said running, and the Preview then rendered.
  const previewRunState = (serviceName) => previewRunStateFor(selectedProject?.id, serviceName, { maxAgeMs: 0 });
  // When the preview loader last posted a render per `<project>|<service>` (see RENDER_ONCE_MS).
  const renderPostedRef = useRef(new Map());
  const STOPPED_TAB_PATCH = {
    loading: false, stopped: true, starting: false, error: null,
    renderPhase: undefined, renderProgress: undefined, renderLogs: undefined,
  };
  const patchPreviewTab = (serviceName, patch) => {
    setPreviewTabs((tabs) => tabs.map((t) => (t.serviceName === serviceName ? { ...t, ...patch } : t)));
  };
  // The Preview's own Start (the stopped state): start the service the way the header does; the watchdog reloads the
  // iframe once the shared service state says it is ready, whoever started it.
  const handleStartStoppedService = async (serviceName) => {
    const projectId = selectedProject?.id;
    if (!projectId || !serviceName) { return; }
    // A pinned runnable: only its own session, with its runnable payload (Apps' Start), never the owner's build.
    const runnableTab = previewTabsRef.current.find((tab) => tab.serviceName === serviceName && isRunnableTab(tab));
    if (runnableTab) { runnablePreview.start(runnableTab); return; }
    patchPreviewTab(serviceName, { stopped: false, starting: true, startRequestedAt: Date.now(), startError: null });
    try {
      const answer = await loadProjectServices(projectId).catch(() => null);
      if (answer) {
        // Every service of the project, as the header's Start all does: the app behind this Preview needs its backend too.
        const key = projectStartKey(projectId);
        const started = await runStart(key, (onProgress) => startProject(projectId, runItems(answer), {
          onEach: () => refreshProjectServices(projectId), onProgress,
        }));
        if (started?.error) { throw new Error(started.error); }
      } else {
        await startService(projectId, serviceName);
      }
      refreshProjectServices(projectId);
    } catch (error) {
      patchPreviewTab(serviceName, { stopped: true, starting: false, startRequestedAt: null, startError: error?.message || 'The app could not be started.' });
    }
  };
  
  // Ref for CodeViewWithLogger to call git operations
  const codeViewRef = useRef(null);
  
  // Deployment / pods / pipeline state + handlers — see hooks/usePipelineDeployment.js
  const {
    showDeploymentModal, setShowDeploymentModal,
    selectedEnvironment, setSelectedEnvironment,
    deploymentLoading,
    deploymentError,
    deploymentPipelineId,
    pipelineStatus,
    pipelinePolling,
    handleDeployClick,
    handleDeploymentConfirm,
    handleDeploymentClose,
    showPodsModal,
    podsEnvironment,
    podsData,
    podsLoading,
    podsError,
    handleViewPodsClick,
    handlePodsModalClose,
    showPipelineModal, setShowPipelineModal,
    currentPipelineStatus, setCurrentPipelineStatus,
    servicePipelineStatus,
    showServicePipelineList, setShowServicePipelineList,
    servicePipelines, setServicePipelines,
    loadingServicePipelines, setLoadingServicePipelines,
  } = usePipelineDeployment(selectedProject, selectedRevision);

  // Push modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);

  // The workspace views (components/workspace/WorkspaceView.jsx, utils/workspaceModel.js): Changes (By person, with
  // revert; Timeline, checkpoints and rewind), Activity and Sessions, shown in place of the page, one place each.
  // `{ tab, sub, focus }` or null (the page). Opened by the workspace tabs, the `collab:openHistory` event (the project
  // Home's History card, an activity item) and links: `?tab=changes|activity|sessions`, the old `?history=…` too.
  const [workspaceView, setWorkspaceView] = useState(() => viewFromSearch(window.location.search));
  useEffect(() => {
    const onOpen = (event) => setWorkspaceView(viewFromHistory(event?.detail?.tab || null, event?.detail?.focus || null));
    window.addEventListener('collab:openHistory', onOpen);
    return () => window.removeEventListener('collab:openHistory', onOpen);
  }, []);
  // A view the person's plan hides (the sandbox admin's Plans › tabs) lands on the project Home, with a short note.
  const planHiddenTabs = usePlanViewGate(workspaceView, setWorkspaceView, selectedProject?.id || null);
  // The open view in the URL (`?tab=`), without a router navigation: a reload or a shared link lands on it. A view the
  // person opens is a history entry (Back returns to the tab before); the first sync (a link, a reload) only fills it in.
  const viewSyncedRef = useRef(false);
  useEffect(() => {
    const next = searchWithView(window.location.search, workspaceView);
    if (next !== window.location.search) {
      const url = `${window.location.pathname}${next}${window.location.hash}`;
      if (viewSyncedRef.current) { window.history.pushState(window.history.state, '', url); } else { window.history.replaceState(window.history.state, '', url); }
    }
    viewSyncedRef.current = true;
  }, [workspaceView]);
  // Back / Forward between tabs: the view follows the address.
  useEffect(() => {
    const onPop = () => setWorkspaceView((now) => {
      const fromUrl = viewFromSearch(window.location.search);
      // The same tab and sub-view (and the same file in Files) keeps the view as it is (its focus).
      return viewKey(fromUrl) === viewKey(now) ? now : fromUrl;
    });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Port mappings state + handlers — see hooks/usePortMappings.js
  const {
    portMappings,
    portMappingsLoading,
    portMappingsError,
    killingPort,
    fetchPortMappings,
    killProcessOnPort,
  } = usePortMappings();

  // Split view mode state
  const [splitViewMode, setSplitViewMode] = useState('editor'); // 'editor' | 'split' | 'preview'
  
  // Resizable panels state
  const [treeWidth, setTreeWidth] = useState(() => {
    const saved = localStorage.getItem('canvas-tree-width');
    return saved ? parseInt(saved, 10) : 250;
  });
  const [splitRatio, setSplitRatio] = useState(() => {
    const saved = localStorage.getItem('canvas-split-ratio');
    return saved ? parseFloat(saved) : 50;
  });
  const [isResizingTree, setIsResizingTree] = useState(false);
  const [isResizingSplit, setIsResizingSplit] = useState(false);

  // Tree refresh key for forcing ProjectResourcesTree re-render
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  // filePath -> content this TAB itself most recently wrote to IndexedDB —
  // fed by the 'monacoFileChanged' event (dispatched by
  // indexedDbFileStorage.saveFile on every write, content included). Lets
  // the vfs-service live-sync effect below tell "this WS event is just my
  // own edit echoing back" apart from "this WS event is a genuinely
  // different tab's edit" WITHOUT comparing against IndexedDB — IndexedDB is
  // shared storage across every tab of the same origin, not per-tab, so by
  // the time a second tab's onFileChanged handler runs, the first tab's
  // write may already be sitting in that shared IndexedDB even though this
  // tab's own Monaco buffer was never told about it. See the live-sync
  // effect's own comment for the bug this caused.
  const recentSelfWritesRef = useRef(new Map());

  // Explicit, eager vfs-service -> IndexedDB reconciliation — separate from
  // (and NOT a replacement for) the WS live-sync effect below, which is
  // best-effort/background and races real UI actions on a fresh Canvas
  // mount (see its own FIXME). Extracted to a shared, module-scoped utility
  // (forceResyncServiceFromVfs.js) so VfsChatPanel.jsx — alive throughout
  // the plain /chat door, unlike this component, which isn't even mounted
  // there — can call it directly too, to gate the Code button until a
  // fresh build's IndexedDB copy is actually trustworthy (App.jsx's own
  // chatCodeReady gate). Bump treeRefreshKey whenever ANY caller's resync
  // actually changed something, via the shared util's own
  // 'vfsForceResyncApplied' dispatch (only fires on a real change, not a
  // no-op resync).
  useEffect(() => {
    const handleApplied = () => setTreeRefreshKey((prev) => prev + 1);
    window.addEventListener('vfsForceResyncApplied', handleApplied);
    return () => window.removeEventListener('vfsForceResyncApplied', handleApplied);
  }, []);

  // Resolves the real service name(s) to force-resync for `projectId` and
  // runs forceResyncServiceFromVfs on each. BUG FIX: the caller (a live
  // 'vfsForceResync' event, or the pending-queue drain below) can't
  // reliably know the real service name at the moment vfs_get_preview_url
  // completes — VfsChatPanel.jsx's own `resolvedServiceName` falls back to
  // '.' (vfs-service's internal "project root" alias used by the
  // preview-url endpoint's own default), which is NOT the same key the
  // file tree/Monaco use (e.g. "app", matching the real `services/app/`
  // folder) — live-confirmed: syncing under '.' left Monaco (reading under
  // "app") still showing the scaffold placeholder, completely untouched by
  // the resync. Resolve the real service name(s) from
  // `configuration.services` instead, where they're reliably known, rather
  // than trusting the event's guess. Falls back to the given hint only if
  // configuration hasn't loaded yet (better than syncing nothing).
  const forceResyncRealServices = useCallback((projectId, serviceNameHint) => {
    if (!projectId) return;
    const realServices = Object.values(configuration?.services || {})
      .filter((service) => service.vfsBacked && service.name !== '.' && service.type !== 'custom-resource');
    if (realServices.length > 0) {
      realServices.forEach((service) => forceResyncServiceFromVfs(projectId, service.name));
    } else if (serviceNameHint) {
      forceResyncServiceFromVfs(projectId, serviceNameHint);
    }
  }, [configuration, forceResyncServiceFromVfs]);

  // 'vfsForceResync' is dispatched by VfsChatPanel.jsx the moment
  // vfs_get_preview_url succeeds and the FE shows the resulting preview —
  // see forceResyncServiceFromVfs's own comment for why this specific
  // moment matters. Only reaches this listener if Canvas.jsx already
  // happens to be mounted (Code mode already open) when it fires — the
  // far more common case (still on Chat, Canvas isn't mounted at all yet)
  // is covered by the pending-queue drain effect below instead.
  useEffect(() => {
    const handleForceResync = (event) => {
      const { projectId, serviceName } = event.detail || {};
      forceResyncRealServices(projectId, serviceName);
    };
    window.addEventListener('vfsForceResync', handleForceResync);
    return () => window.removeEventListener('vfsForceResync', handleForceResync);
  }, [forceResyncRealServices]);

  // Drains any 'vfsForceResync' request that arrived while Canvas.jsx
  // wasn't mounted yet (see pendingVfsResync.js) — runs once
  // `selectedProject`/`configuration` are actually available, i.e. right
  // after switching from Chat to Code for a project the agent just built.
  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId || !configuration) return;
    if (drainPendingVfsResync(projectId)) {
      forceResyncRealServices(projectId, null);
    }
  }, [selectedProject?.id, configuration, forceResyncRealServices]);

  // FIXME: this was briefly refactored to open one WS connection per
  // vfs-backed service (keyed off `configuration.services`, a data
  // concept) instead of gating on `selectedResource` (a UI-state concept)
  // — the goal was real: a service the agent writes to via chat, before
  // the UI ever selects it, went unsynced under the old gate. That
  // refactor introduced a live regression instead — live-confirmed: a
  // fresh Home-created project showed the correct running app in the
  // Preview iframe (proxied straight from the real dev server, unaffected)
  // but Monaco/the Files tab showed only the default scaffold placeholder
  // ("App scaffold ready — replace this with your real UI"), i.e.
  // IndexedDB never actually received the agent's real generated files —
  // rolled back to the original, known-working selectedResource-gated
  // single-connection version below rather than risk shipping it broken.
  // Root cause not yet isolated — candidates: `configuration.services`
  // for a brand-new vfs-backed service may not carry `vfsBacked: true`
  // immediately (the `services.length === 0` guard silently skipping
  // start), or multiple simultaneous onFullResync calls (one per service)
  // racing each other's IndexedDB writes. Re-attempt the multi-service
  // version only after reproducing and fixing this locally — don't just
  // reapply the same diff.
  //
  // Live sync between vfs-service and IndexedDB for a vfs-backed project's
  // currently-selected service — background, not gated on which tab is
  // active, so files stay synced whether or not they're open in Monaco.
  // Reconnecting (including the very first connect) always triggers a full
  // reconciliation, which is what makes this also cover "browser was closed
  // while a file changed server-side" — see vfsSync.js's module doc.
  useEffect(() => {
    const projectId = selectedProject?.id;
    const serviceName = selectedResource?.data?.name || selectedResource?.name;
    if (!projectId || !serviceName || !vfsClient.isVfsBackedProject(projectId)) {
      // eslint-disable-next-line no-console
      devLog('🔌 vfs-service live sync: not starting', {
        projectId, serviceName,
        isVfsBacked: projectId ? vfsClient.isVfsBackedProject(projectId) : null,
        reason: !projectId ? 'no projectId' : !serviceName ? 'no serviceName (selectedResource not a service yet)' : 'project not marked vfs-backed',
      });
      return undefined;
    }
    if (!config.vfsServiceUrl) {
      // eslint-disable-next-line no-console
      console.warn('🔌 vfs-service live sync: config.vfsServiceUrl is not set yet — cannot connect', { projectId, serviceName });
      return undefined;
    }
    // eslint-disable-next-line no-console
    devLog('🔌 vfs-service live sync: connecting', { projectId, serviceName, vfsServiceUrl: config.vfsServiceUrl });

    // The file trees of this service list again only the folders these service paths are in (RepositoryTree's lazy
    // tree); no `paths`: every folder they show.
    const announceTreePaths = (paths) => {
      window.dispatchEvent(new CustomEvent('vfsTreePathsChanged', { detail: { projectId, serviceName, paths } }));
    };

    // A command that says which paths it changed (vfs-service's `tree:bash_executed` `paths`): only those files are
    // read again into the local cache, instead of every file of the service.
    const onPathsChangedByCommand = async (projectPaths) => {
      const prefix = `${serviceName}/`;
      const relativePaths = projectPaths
        .filter((path) => path.startsWith(prefix) && !vfsClient.isSyncExcludedPath(path))
        .map((path) => path.slice(prefix.length))
        .filter((path) => path && path !== '.mia' && !path.startsWith('.mia/'));
      if (relativePaths.length === 0) { return; }
      announceTreePaths(relativePaths);
      const local = new Map((await indexedDbFileStorage.getAllFiles(projectId, serviceName)).map((f) => [f.filePath, f]));
      let changed = false;
      for (const relativePath of relativePaths) {
        try {
          const content = await vfsClient.readFile(projectId, `${prefix}${relativePath}`);
          if (local.get(relativePath)?.content !== content) {
            await indexedDbFileStorage.saveFile(projectId, serviceName, relativePath, content, { fromVfs: true });
            changed = true;
          }
        } catch (error) {
          // Gone (deleted or moved by the command): dropped from the cache, unless it holds unsaved local work.
          const record = local.get(relativePath);
          if (/\(404\)/.test(error.message || '') && record && record.gitState !== 'new' && record.gitState !== 'modified') {
            await indexedDbFileStorage.deleteFile(projectId, serviceName, relativePath);
            changed = true;
          }
        }
      }
      if (changed) {
        setTreeRefreshKey((prev) => prev + 1);
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { projectId, serviceName, fromAutoRender: false, paths: relativePaths },
        }));
      }
    };

    const onFullResync = async (type, data) => {
      if (type === 'tree:bash_executed' && Array.isArray(data?.paths)) {
        try {
          await onPathsChangedByCommand(data.paths);
        } catch (error) {
          console.warn('⚠️ vfs-service sync: failed to apply a command\'s changes:', error);
        }
        return;
      }
      try {
        const [vfsFiles, localFiles] = await Promise.all([
          vfsClient.fetchServiceFiles(projectId, serviceName),
          indexedDbFileStorage.getAllFiles(projectId, serviceName),
        ]);
        const localByPath = new Map(localFiles.map((f) => [f.filePath, f.content]));

        let changed = false;
        for (const [filePath, content] of Object.entries(vfsFiles)) {
          if (localByPath.get(filePath) !== content) {
            await indexedDbFileStorage.saveFile(projectId, serviceName, filePath, content, { fromVfs: true });
            changed = true;
          }
          localByPath.delete(filePath);
        }
        // Anything left in localByPath no longer exists on vfs-service — dropped, unless it holds unsaved local work.
        const dirty = new Set(localFiles.filter((f) => f.gitState === 'new' || f.gitState === 'modified').map((f) => f.filePath));
        for (const filePath of localByPath.keys()) {
          if (dirty.has(filePath)) continue;
          await indexedDbFileStorage.deleteFile(projectId, serviceName, filePath);
          changed = true;
        }
        // eslint-disable-next-line no-console
        devLog('🔄 vfs-service live sync: full resync done', { projectId, serviceName, changed, fileCount: Object.keys(vfsFiles).length });
        if (changed) {
          setTreeRefreshKey((prev) => prev + 1);
          // MonacoFileEditor only refreshes an already-open file's displayed
          // content in response to 'gitPullCompleted' (see its own
          // handleGitPull listener) — saveFile's 'monacoFileChanged' dispatch
          // updates the tree/other state but was never wired to re-pull the
          // editor's OWN buffer for a change it didn't originate itself.
          // Reusing that existing event here (rather than inventing a new
          // one) is what makes an open file live-update instead of only
          // refreshing on next open/page reload.
          window.dispatchEvent(new CustomEvent('gitPullCompleted', {
            detail: { projectId, serviceName, fromAutoRender: false },
          }));
        }
      } catch (error) {
        console.warn('⚠️ vfs-service full resync failed:', error);
      }
    };

    // Real live bug (cross-tab sync): this used to compare the incoming
    // content against what's already in IndexedDB and skip everything
    // (including the gitPullCompleted dispatch) when they matched, meant as
    // a self-echo guard — a client's OWN edit round-trips back to itself
    // over the WS broadcast (vfs-service doesn't exclude the originating
    // connection). That guard is correct for a SINGLE tab, but IndexedDB is
    // shared storage across every open tab of this origin, not per-tab: with
    // the same file open in two tabs, tab A's save lands in the one shared
    // IndexedDB immediately, so by the time tab B's onFileChanged runs,
    // "already matches IndexedDB" is true for tab B too — even though tab
    // B's own Monaco buffer was never told the file changed. Confirmed live:
    // editing in one tab silently updated the OTHER tab's IndexedDB (visible
    // after a manual reload) but never its on-screen Monaco content. Track
    // this tab's own recent writes in memory instead (recentSelfWritesRef,
    // fed by 'monacoFileChanged' below) so only a REAL self-echo is skipped;
    // any other tab's change always dispatches gitPullCompleted.
    const onFileChanged = async (relativePath) => {
      announceTreePaths([relativePath]);
      try {
        const content = await vfsClient.readFile(projectId, `${serviceName}/${relativePath}`);
        if (recentSelfWritesRef.current.get(relativePath) === content) {
          recentSelfWritesRef.current.delete(relativePath);
          return;
        }
        await indexedDbFileStorage.saveFile(projectId, serviceName, relativePath, content, { fromVfs: true });
        setTreeRefreshKey((prev) => prev + 1);
        // See onFullResync's comment above — this is what makes the file
        // live-update in Monaco if it's the one currently open.
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { projectId, serviceName, fromAutoRender: false, paths: [relativePath] },
        }));
      } catch (error) {
        console.warn(`⚠️ vfs-service sync: failed to pull ${relativePath}:`, error);
      }
    };

    // Records this tab's own writes (see recentSelfWritesRef's own comment)
    // — indexedDbFileStorage.saveFile dispatches this with `content`
    // included on every write, whether typed locally or applied from a sync
    // event above, so a duplicate/late WS broadcast for a change this tab
    // already has is also correctly recognized as a no-op.
    const handleLocalFileWrite = (event) => {
      const detail = event.detail || {};
      if (detail.projectId === projectId && detail.serviceName === serviceName && detail.filePath && detail.content !== undefined) {
        recentSelfWritesRef.current.set(detail.filePath, detail.content);
      }
    };
    window.addEventListener('monacoFileChanged', handleLocalFileWrite);

    const onFileDeleted = async (relativePath) => {
      announceTreePaths([relativePath]);
      try {
        await indexedDbFileStorage.deleteFile(projectId, serviceName, relativePath);
        setTreeRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.warn(`⚠️ vfs-service sync: failed to delete ${relativePath} locally:`, error);
      }
    };

    const disconnect = connectProjectTreeSync(config.vfsServiceUrl, projectId, serviceName, {
      onFullResync,
      onFileChanged,
      onFileDeleted,
    });

    return () => {
      window.removeEventListener('monacoFileChanged', handleLocalFileWrite);
      disconnect();
    };
  }, [selectedProject?.id, selectedResource?.data?.name, selectedResource?.name]);

  // Track if there are any modified or new files for enabling/disabling Push button
  const [hasModifications, setHasModifications] = useState(false);

  // Memoize selectedProject with environments to prevent unnecessary re-renders in DashboardView
  const memoizedSelectedProject = useMemo(() => {
    if (!selectedProject) return null;
    return {
      ...selectedProject,
      environments: fullProjectConfig?.environments || configuration?.environments || []
    };
  }, [selectedProject, fullProjectConfig?.environments, configuration?.environments]);

  // Dashboard display metadata (name/description/technologies) written by the
  // agent to a vfs-backed project's `.mia/project.json` — see vfsClient's
  // readProjectMetadata. Only vfs-backed projects have this file; anything
  // else falls back to `null` and DashboardView keeps using its existing
  // Console-derived/placeholder values.
  const [vfsProjectMetadata, setVfsProjectMetadata] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setVfsProjectMetadata(null);
    if (!selectedProject?.id || !vfsClient.isVfsBackedProject(selectedProject.id)) {
      return undefined;
    }
    vfsClient.readProjectMetadata(selectedProject.id).then((metadata) => {
      if (!cancelled) setVfsProjectMetadata(metadata);
    }).catch(() => {
      if (!cancelled) setVfsProjectMetadata(null);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id]);

  // "Go to Project Home" (UnifiedTopBar's ProjectBreadcrumb) showed the
  // stale placeholder name a vfs-backed project is registered under at
  // creation time ("Untitled Project <timestamp>") even long after the
  // agent renamed it via vfs_set_project_metadata — DashboardView's own
  // title already reflected the real name correctly (it reads
  // `vfsProjectMetadata` directly), but the breadcrumb reads
  // `selectedProject.name`, the separate Console-registered field that
  // vfs_set_project_metadata never touches. Prefer the fresher vfs name
  // for display wherever `selectedProject` reaches the top bar.
  const topBarSelectedProject = useMemo(() => {
    if (!selectedProject) return selectedProject;
    if (!vfsProjectMetadata?.name) return selectedProject;
    return { ...selectedProject, name: vfsProjectMetadata.name };
  }, [selectedProject, vfsProjectMetadata?.name]);
  // The project page on, as the header's tab row says it (Home, Code, Preview or Logs): Code, Preview and Logs get the
  // tab's title line.
  const pageTab = currentWorkspaceTab({
    view: null, atHome: selectedResource?.type !== 'service', activeTab, homeMode: dashboardOverviewMode,
  });

  // The header's Logs (and Home's Logs view) open the service being looked at, else the first one.
  const [logsService, setLogsService] = useState(null);
  const changeHomeMode = (mode) => {
    if (mode === 'logs') {
      if (selectedResource?.type === 'service' && selectedResource.data?.name) { setLogsService(servicePath(selectedResource.data.name)); }
      setActiveTab('preview');
    }
    setDashboardOverviewMode(mode);
  };

  // task #146: reset the Dashboard/Files switch back to Dashboard whenever
  // the project changes, so navigating between projects never leaves the
  // user stuck on a previous project's Files view.
  useEffect(() => {
    if (!selectedProject?.id) { return; }
    // The first project of a page load keeps the view the URL asked for (?view=files); every later project
    // change starts on Dashboard.
    if (initialHomeViewRef.current) {
      setDashboardOverviewMode(initialHomeViewRef.current);
      initialHomeViewRef.current = null;
      return;
    }
    setDashboardOverviewMode('dashboard');
  }, [selectedProject?.id]);

  // Project Home's view and the URL's `?view=` are kept in step by useHomeViewUrl (below).
  // A Files-view link to a file inside a service (`?view=files&path=services/app/server.js&line=12`: the chat's file
  // names, "Open the code" from Traces and Quality) opens that service's Code tab on the file and line, from a cold load
  // or from inside the app. Only a path in no service (the project root, `brain/`) stays in Project Home's Files view.
  const filesLinkToService = useFilesLinkToService({
    configuration,
    selectedRevision,
    tenantId: params.tenantId || selectedTenant?.tenantId,
    projectId: params.projectId || selectedProject?.id,
    onOpenService: (name, service) => {
      setWorkspaceView(null);
      setSelectedResource({type: 'service', data: service, name});
      setActiveTab('code');
    },
  });
  useHomeViewUrl({
    selectedResource,
    mode: dashboardOverviewMode,
    setMode: setDashboardOverviewMode,
    filesLinkToService,
    serviceInUrl: Boolean(params.microservice),
    onFileLink: () => setWorkspaceView(null),
    onFileLinkFromService: () => {
      selectHome({
        type: 'configuration',
        data: {
          name: 'Project Configuration',
          repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
          repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
          type: 'configuration',
        },
      });
      setPreviewUrl(null);
      setPreviewTabs([]);
      setActivePreviewTab(-1);
      setActiveTab('preview');
      if (onProjectContextChange) {
        onProjectContextChange({
          tenant: selectedTenant,
          project: selectedProject,
          projectId: selectedProject?.id,
          revision: selectedRevision,
          selectedResource: null,
          selectedMicroservice: null,
          activeTab: 'preview',
        });
      }
    },
  });

  // Handle tree resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingTree) {
        // BUG FIX: this used to subtract the chatSidebarWidth JS state
        // value straight from e.clientX (raw viewport mouse X) to get a
        // position relative to .canvas-container's own left edge — only
        // correct if .main-content (canvas-container's positioning
        // ancestor, see Canvas.css) itself sits flush against the
        // viewport's left edge. That stopped being true once .main-content
        // gained its own margin-left (the catalog-style-shell change in
        // App.css) — every drag became offset by however much the sidebar
        // + that margin add up to, breaking this resize the same way the
        // chat/canvas split handle broke (see App.jsx's own fix). Read the
        // container's real left edge live instead of reconstructing it
        // from state, so this self-corrects for any future layout change.
        const containerLeft = !isFullscreen
          ? (document.querySelector('.canvas-container')?.getBoundingClientRect().left ?? chatSidebarWidth)
          : 0;
        const newWidth = Math.max(180, Math.min(500, e.clientX - containerLeft));
        setTreeWidth(newWidth);
        localStorage.setItem('canvas-tree-width', newWidth.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizingTree(false);
    };

    if (isResizingTree) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingTree, isFullscreen, chatSidebarWidth]);

  // Persist logs expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('mia_logsExpanded', JSON.stringify(logsExpanded));
    } catch (e) {
      console.error('Failed to save logsExpanded state:', e);
    }
  }, [logsExpanded]);

  // Handle split view resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingSplit) {
        const container = document.querySelector('.code-layout.split-layout');
        if (container) {
          const rect = container.getBoundingClientRect();
          const newRatio = Math.max(20, Math.min(80, ((e.clientX - rect.left) / rect.width) * 100));
          setSplitRatio(newRatio);
          localStorage.setItem('canvas-split-ratio', newRatio.toString());
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizingSplit(false);
    };

    if (isResizingSplit) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingSplit]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setPreviewFullscreen(false);
      }
    };

    const handleEscKey = (e) => {
      if (e.key === 'Escape' && previewFullscreen) {
        exitPreviewFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [previewFullscreen]);

  // The person's plan hides Preview: a service opens on Code instead (its Home stays where it is). Code hidden too:
  // the Code gate below takes it to the project Home.
  useEffect(() => {
    if (activeTab === 'preview' && selectedResource?.type === 'service' && isTabHidden(planHiddenTabs, 'preview') && !isTabHidden(planHiddenTabs, 'code')) { setActiveTab('code'); }
  }, [activeTab, selectedResource?.type, planHiddenTabs]);

  // FIX #3: Refresh preview when switching to preview tab to include NEW files from IndexedDB
  useEffect(() => {
    if (activeTab === 'preview' && selectedResource?.type === 'service' && previewNeedsRefresh) {
      // eslint-disable-next-line no-console
      devLog('🔄 Preview tab activated, checking for NEW files in IndexedDB...');
      
      const refreshPreviewFiles = async () => {
        try {
          const serviceName = selectedResource.data.name;
          const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
            selectedProject?.id,
            serviceName
          );
          
          if (changedFiles.length > 0) {
            // eslint-disable-next-line no-console
            devLog(`📝 Found ${changedFiles.length} pending changes (including NEW files), triggering sync...`);
            
            // Trigger immediate sync for any pending files
            window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
              detail: {
                projectId: selectedProject?.id,
                serviceName: serviceName,
                filePath: 'multiple' // Indicates multiple files
              }
            }));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking for NEW files:', error);
        }
        
        // Reset refresh flag
        setPreviewNeedsRefresh(false);
      };
      
      refreshPreviewFiles();
    }
  }, [activeTab, selectedResource, selectedProject, previewNeedsRefresh]);

  // Listen for file creation events to mark preview for refresh
  useEffect(() => {
    const handleFileOperation = (event) => {
      // FIX #3: Mark that preview needs refresh when a file operation occurs
      if (event.detail?.operation === 'create') {
        setPreviewNeedsRefresh(true);
      }
    };

    window.addEventListener('fileOperation', handleFileOperation);
    
    return () => {
      window.removeEventListener('fileOperation', handleFileOperation);
    };
  }, []);
  
  // Listen for explicit context refresh requests from parent (when AI calls request_project_context)
  useEffect(() => {
    const handleContextRefresh = async () => {
      // eslint-disable-next-line no-console
      devLog('🔄 Explicit context refresh requested - forcing update');
      
      // Clear the receivingContextFromParentRef flag to allow update
      receivingContextFromParentRef.current = false;
      
      // Force context key to be different by adding timestamp
      lastProjectContextRef.current = null;
      
      // Trigger update by modifying a dependency (just toggle a counter)
      setTreeRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('requestContextRefresh', handleContextRefresh);
    
    // Removed periodic auto-refresh interval to prevent event loops causing excessive tree fetching
    // Context will be refreshed on explicit requests via 'requestContextRefresh' event only
    
    return () => {
      window.removeEventListener('requestContextRefresh', handleContextRefresh);
    };
  }, [selectedProject, selectedResource, onProjectContextChange]);
  
  // Initialize selections from URL parameters
  useEffect(() => {
    if (!configuration || !initialMicroservice) return;

    // Find and select microservice from URL
    const services = configuration.services || {};
    const service = Object.values(services).find(s => s.name === decodeURIComponent(initialMicroservice));

    const decodedInitialName = decodeURIComponent(initialMicroservice);

    // Skip when the user is already, explicitly on the Home/configuration
    // pseudo-resource: `initialMicroservice` is this prop's own URL-parsed
    // read of the route's service segment, which for one render after a
    // "Go to Project Home" click still reflects the OLD service (the
    // Home-triggered navigate() hasn't committed a new URL yet) — without
    // this guard, this effect treated that stale prop as "the URL wants a
    // service selected" and re-selected it, undoing Home every time
    // (live-confirmed via a services/app→Home click that never actually
    // left the service's preview).
    // decodedInitialName === 'brain' is excluded here (and routed to the
    // 'Project Configuration' branch below instead) — see that branch's own
    // comment for why "brain" must never be selected as a real peer
    // service, deep-linked or not.
    if (service && decodedInitialName !== 'brain' && selectedResource?.type !== 'configuration' &&
        (!selectedResource || selectedResource.data?.name !== service.name)) {
      devLog('🔗 Initializing microservice from URL:', service.name);
      setSelectedResource({type: 'service', data: service, name: service.name});

      // If initialTab is 'preview', ensure activeTab is also set to 'preview'
      // This triggers the preview load effect
      if (initialTab === 'preview' && activeTab !== 'preview') {
        devLog('🔗 Setting activeTab to preview to trigger load');
        setActiveTab('preview');
      }
    } else if (decodedInitialName === 'Project Configuration' || decodedInitialName === 'brain') {
      // The URL's microservice segment is the sentinel name used for the
      // project-configuration pseudo-resource (see the "Auto-select project
      // configuration" fallback below, and Canvas's own handleResourceClick
      // 'project-configuration' branch — both build a `{name: 'Project
      // Configuration', type: 'configuration'}` resource). Deep-linking or
      // reloading a URL that points here used to fall through to the
      // vfs-backed synthetic-*service* branch below instead, because "Project
      // Configuration" never matches a real entry in configuration.services
      // — that produced a bogus `type: 'service'` resource with no matching
      // service data, breaking the Configuration dashboard/markdown view for
      // this exact URL. Restore the real `type: 'configuration'` resource
      // instead, matching the in-app auto-select fallback.
      //
      // 'brain' joins this branch (was previously matched-and-selected as a
      // real peer service, since vfs-service DOES list it in
      // configuration.services) per explicit, repeated instruction: the
      // shared brain/docs/memory folder is not an independently selectable
      // service the same way "app"/"backend" are — it has no build/preview
      // of its own — and treating it as one left the Code view stuck
      // showing "brain" as the active service (URL and dropdown both) once
      // the agent finished writing docs there, instead of returning to
      // Home/Dashboard. UnifiedTopBar's own service-picker dropdown
      // excludes it the same way for the same reason (see its serviceItems
      // filter). Still fully browsable via Home's "Files" pill.
      if (!selectedResource || selectedResource.type !== 'configuration') {
        devLog('🔗 Initializing project configuration from URL');
        const configurationRepo = {
          name: 'Project Configuration',
          repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
          repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
          type: 'configuration'
        };
        setSelectedResource({type: 'configuration', data: configurationRepo});
        if (initialTab === 'preview' && activeTab !== 'preview') {
          setActiveTab('preview');
        }
      }
    } else if (!service && vfsClient.isVfsBackedProject(selectedProject?.id)) {
      // No Console-registered service for this name (a vfs-backed/git-free
      // project has none — see App.jsx's project-creation flow) — synthesize
      // one so the rest of Canvas's service-shaped machinery (file tree,
      // Preview) has something to work with. `vfsBacked: true` is read below
      // (loadPreview) to skip every git-repo requirement and pull content
      // from vfs-service instead.
      // A pinned runnable's preview (`services/app--compose-…`, a link to it, a reload): its own entry, not a code build.
      if (runnableNameOf(decodedInitialName)) {
        if (selectedResource?.data?.name !== runnableNameOf(decodedInitialName)) { openRunnablePreview(runnablePreview.resolve(decodedInitialName)); }
      } else if (!selectedResource || selectedResource.data?.name !== decodedInitialName) {
        devLog('🔗 No Console service for vfs-backed project — synthesizing one:', decodedInitialName);
        const syntheticService = {name: decodedInitialName, vfsBacked: true};
        setSelectedResource({type: 'service', data: syntheticService, name: decodedInitialName});
        if (initialTab === 'preview' && activeTab !== 'preview') {
          setActiveTab('preview');
        }
      }
    }

    // Either way, we've now taken our one shot at resolving the URL's
    // microservice segment into a selection — safe to let the sync-to-URL
    // effect run from here on (see urlInitPendingRef's own comment).
    urlInitPendingRef.current = false;
  }, [configuration, initialMicroservice, initialTab, activeTab, selectedProject?.id]);
  
  // Initialize revision/branch from URL parameters
  useEffect(() => {
    if (!revisions || revisions.length === 0 || !initialBranch) return;
    
    const revision = revisions.find(r => r.name === decodeURIComponent(initialBranch));
    
    if (revision && (!selectedRevision || selectedRevision.name !== revision.name)) {
      devLog('🔗 Initializing branch from URL:', revision.name);
      setSelectedRevision(revision);
    }
  }, [revisions, initialBranch, selectedRevision]);
  
  // Load git branch from localStorage when selectedResource changes
  useEffect(() => {
    if (!selectedResource || selectedResource.type !== 'service' || !selectedProject?.id) {
      setSelectedResourceGitBranch(null);
      return;
    }

    const serviceName = selectedResource.data?.name || selectedResource.name;
    if (!serviceName) {
      setSelectedResourceGitBranch(null);
      return;
    }

    // Use utility to get git ref from localStorage
    const gitBranch = getGitRef(selectedProject.id, serviceName, 'main');
    setSelectedResourceGitBranch(gitBranch);

    // eslint-disable-next-line no-console
    devLog(`🌿 Loaded git branch for ${serviceName}:`, gitBranch);
  }, [selectedResource, selectedProject?.id]);

  // Sync activeTab with initialTab from URL
  useEffect(() => {
    if (initialTab && activeTab !== initialTab) {
      devLog('🔗 Syncing activeTab from URL:', initialTab);
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  /**
   * Handle file selection from file tree
   * Note: URL updates are now handled by RepositoryTree itself
   */
  const handleFileSelect = useCallback((filePathOrData, fileDataIfTwo) => {
    // Handle both calling conventions:
    // 1. handleFileSelect(fileData) - direct call with file object
    // 2. handleFileSelect(filePath, fileData) - call from CodeViewWithLogger
    let fileData;
    if (typeof filePathOrData === 'string' && fileDataIfTwo) {
      // Called with (filePath, fileData)
      fileData = fileDataIfTwo;
    } else if (typeof filePathOrData === 'object') {
      // Called with (fileData)
      fileData = filePathOrData;
    } else {
      return;
    }
    
    // Normalize: fileData may have 'path' or 'filePath' property
    const normalizedPath = fileData?.path || fileData?.filePath;
    if (!fileData || !normalizedPath) {
      return;
    }
    
    // Ensure fileData has 'path' property for consistency
    const normalizedFileData = {
      ...fileData,
      path: normalizedPath
    };
    
    setSelectedFile(normalizedFileData);
    
    // Store as last opened file
    if (selectedProject && selectedResource) {
      const projectId = selectedProject.id || selectedProject._id;
      const serviceName = selectedResource.data?.name;
      const key = `lastOpenedFile_${projectId}_${serviceName}`;
      localStorage.setItem(key, normalizedPath);
      setLastOpenedFile(normalizedPath);
    }
  }, [selectedProject, selectedResource]);

  /**
   * Load file from path (used for URL-based file opening and restoring last opened file)
   */
  const loadFileFromPath = useCallback(async (filePath) => {
    if (!filePath || !selectedProject || !selectedResource) return;
    
    try {
      const projectId = selectedProject.id || selectedProject._id;
      const serviceName = selectedResource.data?.name;
      
      // Check IndexedDB first for cached changes
      let fileContent = null;
      try {
        const cachedFile = await indexedDbFileStorage.getFile(projectId, serviceName, filePath);
        if (cachedFile) {
          fileContent = cachedFile.content;
          // eslint-disable-next-line no-console
          devLog('📂 Loaded file from IndexedDB:', filePath);
        }
      } catch (dbError) {
        // eslint-disable-next-line no-console
        console.warn('Could not load from IndexedDB, will try GitLab:', dbError);
      }
      
      // If not in IndexedDB, fetch from GitLab
      if (!fileContent) {
        const repoUrl = selectedResource.data?.repositoryUrl || selectedResource.data?.repoUrl;
        if (!repoUrl) {
          console.error('No repository URL found');
          return;
        }
        
        // Extract GitLab project ID
        const gitlabProjectId = extractGitLabProjectId(repoUrl);
        if (!gitlabProjectId) {
          console.error('Could not extract GitLab project ID');
          return;
        }
        
        const branch = selectedRevision?.name || params.branch || 'main';
        
        // Fetch file from GitLab
        const unifiedGitDataService = (await import('../services/UnifiedGitDataService.js')).default;
        const fileData = await unifiedGitDataService.getFileContent(
          gitlabProjectId,
          repoUrl,
          filePath,
          branch
        );
        
        fileContent = atob(fileData.content);
        // eslint-disable-next-line no-console
        devLog('📂 Loaded file from GitLab:', filePath);
      }
      
      // Detect language from file extension
      const ext = filePath.split('.').pop()?.toLowerCase();
      const languageMap = {
        'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
        'py': 'python', 'json': 'json', 'html': 'html', 'css': 'css', 'scss': 'scss',
        'md': 'markdown', 'yml': 'yaml', 'yaml': 'yaml', 'xml': 'xml', 'sql': 'sql',
        'sh': 'shell', 'bash': 'shell', 'java': 'java', 'go': 'go', 'rs': 'rust',
        'cpp': 'cpp', 'c': 'c', 'h': 'cpp', 'php': 'php', 'rb': 'ruby', 'swift': 'swift'
      };
      const language = languageMap[ext] || 'text';
      
      // Create file data object
      const fileData = {
        path: filePath,
        filePath: filePath,  // Include both for compatibility
        content: fileContent,
        language: language,
        name: filePath.split('/').pop()
      };
      
      // Use handleFileSelect to properly update state and URL
      handleFileSelect(fileData);
      
      // Scroll to line number if specified in hash
      const lineNumber = location.hash ? parseInt(location.hash.replace('#L', '')) : null;
      if (lineNumber) {
        // Monaco editor will handle scroll via lineNumber prop
        // Dispatch event for CodeViewWithLogger to scroll to line
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('scrollToLine', {
            detail: { lineNumber }
          }));
        }, 100);
      }
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load file:', error);
    }
  }, [selectedProject, selectedResource, selectedRevision, params.branch, location.hash, handleFileSelect]);

  // URL-based file loading is now handled by RepositoryTree component

  // CRITICAL FIX: Listen for AI content updates even when Monaco is not open
  // This ensures diffs are applied to IndexedDB regardless of editor visibility
  useEffect(() => {
    const handleAIContentUpdate = async (event) => {
      const {filePath, updateType, diffContent, projectId: eventProjectId, serviceName: eventServiceName} = event.detail || {};
      
      // Determine the correct service name to use
      // If in preview mode with active tab, use that tab's service name
      let targetServiceName = eventServiceName;
      if (activeTab === 'preview' && previewTabs.length > 0 && activePreviewTab >= 0) {
        const activeTab = previewTabs[activePreviewTab];
        if (activeTab && activeTab.serviceName) {
          targetServiceName = activeTab.serviceName;
          // eslint-disable-next-line no-console
          devLog(`📋 Using preview tab service name: ${targetServiceName}`);
        }
      }
      
      // CRITICAL FIX: If projectId or serviceName is missing, try to get from context
      let finalProjectId = eventProjectId;
      let finalServiceName = targetServiceName;
      
      if (!finalProjectId && selectedProject?.id) {
        finalProjectId = selectedProject.id;
        // eslint-disable-next-line no-console
        devLog(`📌 Canvas: Using projectId from context: ${finalProjectId}`);
      }
      
      // Get service name from selectedResource (the actual state variable, not the derived one)
      if (!finalServiceName) {
        // Try localStorage first - but parse it correctly if it's JSON
        const storedResource = localStorage.getItem('mia_selectedResource');
        if (storedResource) {
          try {
            // Try to parse as JSON first (in case it's an object)
            const parsed = JSON.parse(storedResource);
            if (parsed && typeof parsed === 'object') {
              // Extract the name from the object structure
              finalServiceName = parsed.data?.name || parsed.name || null;
            } else {
              // It's already a string
              finalServiceName = storedResource;
            }
          } catch (e) {
            // Not JSON, use as-is (it's a plain string)
            finalServiceName = storedResource;
          }
          
          if (finalServiceName) {
            // eslint-disable-next-line no-console
            devLog(`📌 Canvas: Using serviceName from localStorage: ${finalServiceName}`);
          }
        }
        
        // Fall back to selectedResource state if localStorage didn't work
        if (!finalServiceName && selectedResource?.type === 'service' && selectedResource?.data?.name) {
          finalServiceName = selectedResource.data.name;
          // eslint-disable-next-line no-console
          devLog(`📌 Canvas: Using serviceName from selectedResource: ${finalServiceName}`);
        } else if (!finalServiceName && selectedResource?.name) {
          finalServiceName = selectedResource.name;
          // eslint-disable-next-line no-console
          devLog(`📌 Canvas: Using serviceName from selectedResource.name: ${finalServiceName}`);
        }
      }
      
      // eslint-disable-next-line no-console
      devLog('🔔 Canvas: Received aiContentUpdate event:', {
        filePath,
        updateType,
        hasDiff: !!diffContent,
        diffLength: diffContent?.length || 0,
        projectId: finalProjectId,
        serviceName: finalServiceName,
        originalProjectId: eventProjectId,
        originalServiceName: eventServiceName,
        timestamp: new Date().toISOString()
      });
      
      // Only handle diffs when Monaco editor is NOT handling them
      // (Monaco handles open files, we handle background files)
      // eslint-disable-next-line no-console
      devLog('🔍 Canvas: Checking condition for diff processing:', {
        updateTypeIsDiff: updateType === 'diff',
        hasDiffContent: !!diffContent,
        hasProjectId: !!finalProjectId,
        hasServiceName: !!finalServiceName,
        willProcess: updateType === 'diff' && diffContent && finalProjectId && finalServiceName
      });
      
      if (updateType === 'diff' && diffContent && finalProjectId && finalServiceName) {
        // Add timeout protection to prevent hanging
        const timeoutId = setTimeout(() => {
          // eslint-disable-next-line no-console
          console.error(`⏱️ Canvas: Diff application timeout for ${filePath} after 30s`);
        }, 30000);
        
        try {
          // Parse the diff to check operation type
          const parsed = parseUnifiedDiff(diffContent);
          
          if (!parsed.isValid) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️ Invalid diff for ${filePath}, skipping background processing`);
            return;
          }
          
          // Handle file deletion
          if (parsed.isFileDeletion) {
            // eslint-disable-next-line no-console
            devLog(`🗑️ Canvas: Deleting file from IndexedDB: ${filePath}`);
            
            await indexedDbFileStorage.deleteFile(finalProjectId, finalServiceName, filePath);
            
            // eslint-disable-next-line no-console
            devLog(`✅ Canvas: File deleted from IndexedDB: ${filePath}`);
            return;
          }
          
          // Get current content from IndexedDB
          const stored = await indexedDbFileStorage.getFile(finalProjectId, finalServiceName, filePath);
          const currentContent = stored?.content || '';
          
          // Apply the diff
          const result = await applyUnifiedDiff(diffContent, currentContent);
          
          if (result.success) {
            // Save to IndexedDB
            const originalContentToSave = stored?.originalContent || currentContent;
            await indexedDbFileStorage.saveFile(
              finalProjectId,
              finalServiceName,
              filePath,
              result.newContent,
              {
                originalContent: originalContentToSave,
                fileName: filePath.split('/').pop(),
                appliedInBackground: true,
                isNew: parsed.operation === 'create'
              }
            );
            
            // Dispatch monacoFileChanged to trigger auto-sync
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {
                projectId: finalProjectId,
                serviceName: finalServiceName,
                filePath: filePath
              }
            }));
            
            // No manual broadcast needed: if this file is open live anywhere,
            // vfs-service's own Yjs doc (shared between /patch writes and any
            // connected y-websocket session, see useCollaboration.js) already
            // propagates the change in real time.
            
            // eslint-disable-next-line no-console
            devLog(`✅ Canvas: Applied diff in background for ${filePath}`);
            
            // If it's a new file, trigger immediate sync
            if (parsed.operation === 'create') {
              window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
                detail: {
                  projectId: finalProjectId,
                  serviceName: finalServiceName,
                  filePath: filePath
                }
              }));
            }
          } else {
            // eslint-disable-next-line no-console
            console.error(`❌ Canvas: Failed to apply diff for ${filePath}:`, result.error);
            // eslint-disable-next-line no-console
            console.error(`   Diff length: ${diffContent?.length || 0} chars`);
            // eslint-disable-next-line no-console
            console.error(`   ProjectId: ${finalProjectId}, Service: ${finalServiceName}`);
            
            // Dispatch diffApplicationFailed event for AI notification
            window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
              detail: {
                projectId: finalProjectId,
                serviceName: finalServiceName,
                filePath: filePath,
                fileName: filePath.split('/').pop(),
                currentContent: currentContent,
                currentLineCount: currentContent.split('\n').length,
                failedDiff: diffContent,
                error: result.error,
                isBackgroundDiff: true
              }
            }));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`❌ Canvas: Error processing AI content update for ${filePath}:`, error);
          // eslint-disable-next-line no-console
          console.error(`   Stack trace:`, error.stack);
        } finally {
          clearTimeout(timeoutId);
          // eslint-disable-next-line no-console
          devLog(`✓ Canvas: Finished processing aiContentUpdate for ${filePath}`);
        }
      }
    };

    window.addEventListener('aiContentUpdate', handleAIContentUpdate);

    return () => {
      window.removeEventListener('aiContentUpdate', handleAIContentUpdate);
    };
  }, [activeTab, previewTabs, activePreviewTab]);

  // Fullscreen management functions
  const togglePreviewFullscreen = async () => {
    if (!previewContainerRef.current) {return;}

    try {
      if (!previewFullscreen) {
        await previewContainerRef.current.requestFullscreen();
        setPreviewFullscreen(true);
      } else {
        await document.exitFullscreen();
        setPreviewFullscreen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fullscreen error:', error);
    }
  };

  const exitPreviewFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setPreviewFullscreen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Exit fullscreen error:', error);
    }
  };

  // Port mappings fetch/kill handlers now come from usePortMappings() above.

  // Function to refresh preview iframe after file updates
  const refreshPreview = useCallback(async () => {
    // Force-resync IndexedDB from vfs-service before anything else — see
    // forceResyncServiceFromVfs's own comment. No-ops if already done for
    // this (project, service) pair this Canvas mount.
    if (previewTabs.length > 0 && activePreviewTab >= 0) {
      const activeServiceName = previewTabs[activePreviewTab]?.serviceName;
      if (activeServiceName && !isRunnableTab(previewTabs[activePreviewTab])) {
        await forceResyncServiceFromVfs(selectedProject?.id, activeServiceName);
      }
    }
    // Check if there was a compilation error on the active tab
    // If so, trigger a full re-render instead of just refreshing the iframe
    if (previewTabs.length > 0 && activePreviewTab >= 0) {
      const activeTab = previewTabs[activePreviewTab];
      
      // Check if the tab has an error or if buildErrorModal is open for this service
      const hasError = activeTab?.error || 
                      (buildErrorModal.isOpen && buildErrorModal.serviceName === activeTab?.serviceName);
      
      if (hasError) {
        // eslint-disable-next-line no-console
        devLog('🔄 Compilation error detected, triggering full re-render instead of refresh');
        
        // Close the build error modal
        setBuildErrorModal({ isOpen: false, error: null, serviceName: null });
        
        // Clear the error from the tab
        const updatedTabs = [...previewTabs];
        updatedTabs[activePreviewTab] = {
          ...updatedTabs[activePreviewTab],
          error: null,
          loading: true
        };
        setPreviewTabs(updatedTabs);
        
        // Trigger autoRenderOnPreviewNotFound which will do a full rebuild
        try {
          await autoRenderOnPreviewNotFound(activeTab, { explicit: isRunnableTab(activeTab) });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to re-render after error fix:', error);
        }
        return;
      }
    }
    
    // No error - just refresh the iframe normally
    // Update key to force iframe remount
    setIframeKey(Date.now());
    // Also update the active tab's iframe key
    if (previewTabs.length > 0 && activePreviewTab >= 0) {
      const updatedTabs = [...previewTabs];
      updatedTabs[activePreviewTab] = {
        ...updatedTabs[activePreviewTab],
        iframeKey: Date.now()
      };
      setPreviewTabs(updatedTabs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTabs, activePreviewTab, buildErrorModal, forceResyncServiceFromVfs, selectedProject?.id]);

  // Function to fetch logs for a container
  const fetchContainerLogs = useCallback(async (sessionId, containerPort) => {
    try {
      const apiUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/logs`);
      const response = await getAuthorizedFetch()(apiUrl, {
        headers: getBffHeaders(),
      });
      const data = await response.json();
      
      if (data.success) {
        setContainerLogs(prev => ({
          ...prev,
          [sessionId]: {
            stdout: data.logs.stdout || [],
            stderr: data.logs.stderr || [],
            lastFetch: new Date().toISOString()
          }
        }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch container logs:', error);
    }
  }, []);

  // Function to control container (stop/start/restart)
  const controlContainer = useCallback(async (sessionId, containerPort, action) => {
    try {
      const apiUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/${encodeURIComponent(action)}`);
      const response = await getAuthorizedFetch()(apiUrl, {
        method: 'POST',
        headers: getBffHeaders(),
      });
      const data = await response.json();
      
      if (data.success) {
        // Update the preview tab status
        setPreviewTabs(prev => prev.map(tab => (
          tab.sessionId === sessionId ? { 
            ...tab, 
            loading: action === 'start' || action === 'restart',
            url: action === 'stop' ? null : tab.url
          } : tab
        )));

        // If starting/restarting, wait a bit and check if URL is available
        if (action === 'start' || action === 'restart') {
          setTimeout(() => {
            setPreviewTabs(prev => prev.map(tab => (
              tab.sessionId === sessionId ? { 
                ...tab, 
                loading: false,
                url: data.previewUrl || `http://localhost:${tab.containerPort}`
              } : tab
            )));
          }, 2000);
        }

        return true;
      }
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to ${action} container:`, error);
      return false;
    }
  }, []);
  
  /**
   * Auto-render when preview session is not found
   * This handles the case when the container restarts and loses session state
   * 1. Gets files from IndexedDB
   * 2. If no files, pulls from Git and saves to IndexedDB
   * 3. Calls /api/render with all files
   * 4. Updates Monaco editor with the code via gitPullCompleted event
   */
  // `explicit`: the user asked for it (Retry, Refresh). Otherwise (an iframe that failed to load, files landing) a
  // service someone stopped is not started behind their back: the Preview shows the stopped state instead.
  const autoRenderOnPreviewNotFound = async (tab, { explicit = false } = {}) => {
    const serviceName = tab.serviceName;
    // A pinned runnable is no code build: Retry renders its own session again with its payload; nothing on its own.
    if (isRunnableTab(tab)) {
      if (explicit) { await runnablePreview.retry(tab); }
      return;
    }

    // The "Project Configuration" pseudo-resource (type: 'configuration',
    // see the `project-configuration` branch of the service-select handler
    // below) is never a real vfs/git service — it has no vfs path of its
    // own, so `vfsClient.fetchServiceFiles(projectId, 'Project Configuration')`
    // 404s with "Path does not exist: Project Configuration" (same class of
    // leak as task #144, just reached through a stale/leftover preview tab
    // here instead of a chat header). Bail out before any IndexedDB/git/vfs
    // call rather than surfacing that error.
    if (serviceName === 'Project Configuration' || serviceName === 'project-configuration') {
      return;
    }

    if (!explicit) {
      const { state } = await previewRunState(serviceName);
      if (state === 'stopped') {
        devLog('⏹️ [AutoRender]', serviceName, 'is stopped: showing the stopped state, not starting it');
        patchPreviewTab(serviceName, STOPPED_TAB_PATCH);
        return;
      }
    }

    const sessionKey = `${selectedProject?.id}-${serviceName}`;
    const pullKey = `${selectedProject?.id}-${serviceName}`;

    // ── PRE-FLIGHT: check tool enablement & authentication FIRST ──────────────
    // Do this before any IndexedDB or git API calls so we never hit 401s
    {
      const svc = configuration?.services?.[serviceName];
      const repoUrlForCheck = svc?.repositoryUrl || svc?.repoUrl || svc?.sshUrl || tab?.repoUrl;
      if (repoUrlForCheck) {
        const gitStatus = await checkGitToolStatus(repoUrlForCheck);
        if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
          const blockedError = !gitStatus.toolEnabled
            ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
            : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
          // eslint-disable-next-line no-console
          console.warn(`🚫 [AutoRender] Pre-flight blocked (${blockedError}) for`, serviceName);
          setPreviewTabs(prevTabs => prevTabs.map(t =>
            t.serviceName === serviceName
              ? { ...t, loading: false, pullingCode: false, error: blockedError }
              : t
          ));
          return;
        }
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Same "nothing to build yet" short-circuit as the main preview loader
    // above — a retry off a stale/expired session for a service with no
    // real content should land on the friendly empty state too, not a
    // doomed rebuild attempt with a noisy log stream. See vfsClient.isServiceEmpty.
    {
      const svcForEmptyCheck = configuration?.services?.[serviceName];
      if (svcForEmptyCheck?.vfsBacked && await vfsClient.isServiceEmpty(selectedProject?.id, serviceName)) {
        setPreviewTabs(prevTabs => prevTabs.map(t =>
          t.serviceName === serviceName
            ? { ...t, loading: false, error: null, isEmptyService: true }
            : t
        ));
        return;
      }
    }

    // Prevent duplicate render attempts
    if (handlingIframeErrorRef.current.has(sessionKey)) {
      // eslint-disable-next-line no-console
      devLog('⏸️ Already handling iframe error for', serviceName, '- skipping duplicate');
      return;
    }

    handlingIframeErrorRef.current.add(sessionKey);

    try {
      // eslint-disable-next-line no-console
      devLog('🔄 Preview session not found - initiating auto-render for', serviceName);
      
      // Get files from IndexedDB
      let allFiles = await indexedDbFileStorage.getAllFilesForPreview(
        selectedProject?.id,
        serviceName,
        {}
      );
      
      // If no files in IndexedDB, try to pull from Git
      if (Object.keys(allFiles).length === 0) {
        // Check if another process is already pulling this service
        if (autoTriggeredPullRef.current.has(pullKey)) {
          // eslint-disable-next-line no-console
          devLog('⏸️ Another process is already pulling', serviceName, '- skipping duplicate');
          // Wait a bit and try to get files again
          await new Promise(resolve => setTimeout(resolve, 2000));
          allFiles = await indexedDbFileStorage.getAllFilesForPreview(
            selectedProject?.id,
            serviceName,
            {}
          );
          if (Object.keys(allFiles).length === 0) {
            throw new Error('Pull in progress but no files available yet');
          }
        } else {
          // Mark that we're pulling this service
          autoTriggeredPullRef.current.add(pullKey);
          
          // eslint-disable-next-line no-console
          devLog('📥 No files in IndexedDB - pulling from Git...');
        
          const service = configuration?.services?.[serviceName];
          if (!service) {
            throw new Error(`Service ${serviceName} not found in configuration`);
          }

          const isVfsBackedService = Boolean(service.vfsBacked) || vfsClient.isVfsBackedProject(selectedProject?.id);

          if (isVfsBackedService) {
            // FIXME: this per-file vfs-service pull was briefly removed
            // (the intent was real: the WS live-sync effect above should be
            // the SOLE owner of keeping vfs-service and IndexedDB in sync,
            // and refresh/auto-render should only ever READ IndexedDB, not
            // duplicate that sync itself) but that removal shipped ON TOP
            // OF a live-sync refactor that turned out to be broken — see
            // that effect's own FIXME for the regression (fresh Home-built
            // project: Preview iframe showed the real app, Monaco/Files
            // showed only the scaffold placeholder). With no fallback AND
            // a broken sync, IndexedDB could be permanently empty for a
            // service with real committed files. Restored as a safety net
            // until the sync-ownership refactor is redone and actually
            // verified live — remove this again only once that's true, not
            // before.
            // No git repository at all — pull straight from vfs-service instead.
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceName
                ? {...t, pullingCode: true, renderPhase: 'Pulling code from vfs-service', renderProgress: 0}
                : t
            ));

            const vfsFiles = await vfsClient.fetchServiceFiles(selectedProject?.id, serviceName, (progress) => {
              setPreviewTabs(prevTabs => prevTabs.map(t =>
                t.serviceName === serviceName
                  ? {
                      ...t,
                      renderPhase: `Pulling code from vfs-service (${progress.percentage}%)`,
                      renderProgress: progress.percentage,
                      renderLogs: [
                        ...(t.renderLogs || []).slice(-10),
                        `Downloading ${progress.current}/${progress.total}: ${progress.currentFile}`
                      ]
                    }
                  : t
              ));
            });

            // eslint-disable-next-line no-console
            devLog(`💾 Saving ${Object.keys(vfsFiles).length} files to IndexedDB...`);
            for (const [filePath, content] of Object.entries(vfsFiles)) {
              await indexedDbFileStorage.saveFile(
                selectedProject?.id,
                serviceName,
                filePath,
                content,
                {
                  originalContent: content,
                  fromVfs: true // These files come from vfs-service, so they're ORIGINAL
                }
              );
            }

            allFiles = vfsFiles;

            window.dispatchEvent(new CustomEvent('gitPullCompleted', {
              detail: {
                projectId: selectedProject?.id,
                serviceName,
                fromAutoRender: true
              }
            }));
          } else {
            const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
            if (!repoUrl) {
              throw new Error(`No repository URL found for service ${serviceName}`);
            }

            // ── PRE-FLIGHT: check tool enablement & authentication ─────────────
            const gitStatus = await checkGitToolStatus(repoUrl);
            if (!gitStatus.toolEnabled) {
              setPreviewTabs(prevTabs => prevTabs.map(t =>
                t.serviceName === serviceName
                  ? { ...t, loading: false, pullingCode: false, error: `__GIT_TOOL_DISABLED__:${gitStatus.providerName}` }
                  : t
              ));
              return;
            }
            if (!gitStatus.authenticated) {
              setPreviewTabs(prevTabs => prevTabs.map(t =>
                t.serviceName === serviceName
                  ? { ...t, loading: false, pullingCode: false, error: `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}` }
                  : t
              ));
              return;
            }
            // ──────────────────────────────────────────────────────────────────

            const projectId = extractProjectIdFromUrl(repoUrl);
            if (!projectId) {
              throw new Error('Could not extract project ID from repository URL');
            }

            // Update preview tab to show pulling state
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceName
                ? {...t, pullingCode: true, renderPhase: 'Pulling code from repository', renderProgress: 0}
                : t
            ));

            // Fetch files from Git with progress tracking
            const gitFiles = await fetchAllRepositoryFiles(projectId, repoUrl, (progress) => {
              // Update preview tab with progress
              setPreviewTabs(prevTabs => prevTabs.map(t =>
                t.serviceName === serviceName
                  ? {
                      ...t,
                      renderPhase: `Pulling code from repository (${progress.percentage}%)`,
                      renderProgress: progress.percentage,
                      renderLogs: [
                        ...(t.renderLogs || []).slice(-10), // Keep last 10 logs
                        `Downloading ${progress.current}/${progress.total}: ${progress.currentFile}`
                      ]
                    }
                  : t
              ));
            });

            // Save to IndexedDB for future use
            // eslint-disable-next-line no-console
            devLog(`💾 Saving ${Object.keys(gitFiles).length} files to IndexedDB...`);
            for (const [filePath, content] of Object.entries(gitFiles)) {
              await indexedDbFileStorage.saveFile(
                selectedProject?.id,
                serviceName,
                filePath,
                content,
                {
                  originalContent: content,
                  fromGit: true // These files come from Git, so they're ORIGINAL
                }
              );
            }

            allFiles = gitFiles;

            // Dispatch event to refresh Monaco editor and repository tree
            window.dispatchEvent(new CustomEvent('gitPullCompleted', {
              detail: {
                projectId: selectedProject?.id,
                serviceName,
                fromAutoRender: true
              }
            }));
          }
        } // Close the else block for autoTriggeredPullRef check
      } // Close the if (Object.keys(allFiles).length === 0) block
      
      if (Object.keys(allFiles).length === 0) {
        throw new Error('No source code files found for rendering');
      }
      
      // eslint-disable-next-line no-console
      devLog(`📤 Auto-rendering with ${Object.keys(allFiles).length} files`);
      
      // Get routing info using centralized utility
      const routingInfo = getContainerRoutingInfo(
        tab.service || { name: serviceName },
        allFiles,
        { previewSessionId: tab.sessionId }
      );
      
      if (!routingInfo) {
        throw new Error('Failed to resolve preview routing for auto-render');
      }
      
      const { containerPort, containerName, isBackend: isBackendService } = routingInfo;
      
      // eslint-disable-next-line no-console
      devLog(`🎯 Auto-render routing ${isBackendService ? 'backend' : 'frontend'} (${containerName})`);
      
      const userId = getOrCreateUserId(selectedProject?.id, serviceName);
      
      // Show loading state
      const tabIndex = previewTabs.findIndex(t => t.serviceName === serviceName);
      if (tabIndex >= 0) {
        setPreviewTabs(currentTabs => {
          const updatedTabs = [...currentTabs];
          updatedTabs[tabIndex] = {
            ...updatedTabs[tabIndex],
            loading: true,
            error: null,
            renderPhase: 'starting',
            renderProgress: 0,
            renderLogs: ['🔄 Auto-rendering after session loss...']
          };
          return updatedTabs;
        });
      }
      
      // Call /api/render
      const renderApiUrl = getContainerApiUrl(containerPort, '/api/render');
      const isVfsBackedService = Boolean(configuration?.services?.[serviceName]?.vfsBacked);
      const renderResponse = await getAuthorizedFetch()(renderApiUrl, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          tenantId: selectedTenant?.tenantId || selectedTenant?.id,
          serviceName: serviceName,
          ...(isVfsBackedService
            ? vfsClient.buildVfsSourcePayload(selectedProject?.id, serviceName)
            : { sourceCode: allFiles }),
          projectId: selectedProject?.id,
          revisionId: selectedRevision?.id,
          userId: userId,
        }),
      });
      
      if (!renderResponse.ok) {
        throw new Error(`Failed to call /api/render: ${renderResponse.status}`);
      }
      
      const renderResult = await renderResponse.json();
      if (!renderResult.success || !renderResult.sessionId) {
        throw new Error(renderResult.message || 'Failed to start render job');
      }
      
      // eslint-disable-next-line no-console
      devLog(`🎬 Auto-render job started with ID: ${renderResult.sessionId}`);
      
      // Poll for completion
      const containerBaseUrl = renderApiUrl.replace('/api/render', '');
      const pollResult = await pollRenderStatusShared(
        serviceName,
        containerBaseUrl,
        renderResult.sessionId,
        (phase, progress, logs) => {
          // Debug: Log what we're about to set on the tab
          devLog('[onProgress callback] Updating tab with:', {
            phase,
            progress,
            logsCount: logs?.length,
            logs
          });
          
          // Use functional setState to avoid stale closure
          setPreviewTabs(currentTabs => {
            const currentTabIndex = currentTabs.findIndex(t => t.serviceName === serviceName);
            if (currentTabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              // Replace logs entirely (API returns complete log history, not incremental)
              updatedTabs[currentTabIndex] = {
                ...updatedTabs[currentTabIndex],
                loading: true,
                renderPhase: phase,
                renderProgress: progress,
                renderLogs: logs || []
              };
              return updatedTabs;
            }
            return currentTabs;
          });
        }
      );
      
      // Success - update tab with new session
      if (tabIndex >= 0) {
        // The runner's id (it answered with it); else built the way the runner builds it, never `<project>-services/app-<user>`.
        const expectedSessionId = pollResult?.sessionId || renderResult.sessionId || buildSessionId(selectedProject?.id, serviceName, userId);
        // Use the previewUrl from pollResult instead of tab.url which is null
        const basePreviewUrl = pollResult?.previewUrl || `http://localhost:${containerPort}/preview-${containerPort}`;
        const finalPreviewUrl = basePreviewUrl.split('?')[0] + `?sessionId=${expectedSessionId}`;
        
        // eslint-disable-next-line no-console
        devLog('🔄 Updating tab after auto-render completion:', {
          serviceName,
          tabIndex,
          basePreviewUrl,
          finalPreviewUrl,
          expectedSessionId,
          pollResultPreviewUrl: pollResult?.previewUrl
        });
        
        setPreviewTabs(currentTabs => {
          const updatedTabs = [...currentTabs];
          const oldTab = updatedTabs[tabIndex];
          updatedTabs[tabIndex] = {
            ...updatedTabs[tabIndex],
            url: finalPreviewUrl,
            sessionId: expectedSessionId,
            userId: userId,
            loading: false,
            error: null,
            isEmptyService: false,
            // Clear render progress state to hide the loading bar
            renderPhase: undefined,
            renderProgress: undefined,
            renderLogs: undefined,
            iframeKey: Date.now()
          };
          // eslint-disable-next-line no-console
          devLog('✅ Tab state updated:', {
            serviceName: updatedTabs[tabIndex].serviceName,
            wasLoading: oldTab.loading,
            nowLoading: updatedTabs[tabIndex].loading,
            hadUrl: !!oldTab.url,
            hasUrl: !!updatedTabs[tabIndex].url,
            url: updatedTabs[tabIndex].url
          });
          return updatedTabs;
        });
        
        // Save session to localStorage
        saveSessionToStorage(selectedProject?.id, serviceName, userId, {
          sessionId: expectedSessionId,
          previewUrl: finalPreviewUrl,
          containerPort: containerPort,
          language: tab.language,
          framework: tab.framework,
          tenantId: selectedTenant?.id || selectedTenant?.tenantId,
          projectId: selectedProject?.id
        });
        
        // Log the URL that was set
        // eslint-disable-next-line no-console
        devLog('✅ Auto-render URL set:', finalPreviewUrl);
      }
      
      // eslint-disable-next-line no-console
      devLog('✅ Auto-render completed successfully - Preview is now 100% ready');
      
      // Check if there's a pending initial prompt from new project creation
      // Dispatch it NOW after render is complete (100%)
      // task #107: guarded by initialPromptConsumedRef — see its declaration.
      const pendingPrompt = initialPromptConsumedRef.current ? null : sessionStorage.getItem('mia_pending_initial_prompt');
      const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');
      const pendingFiles = sessionStorage.getItem('mia_pending_initial_files');

      if (pendingPrompt) {
        initialPromptConsumedRef.current = true;
        // eslint-disable-next-line no-console
        devLog('🚀 [Preview] Render complete at 100%, triggering initial prompt:', pendingPrompt.substring(0, 100));

        // Clear the pending flags
        sessionStorage.removeItem('mia_pending_initial_prompt');
        sessionStorage.removeItem('mia_pending_initial_mode');
        sessionStorage.removeItem('mia_pending_initial_files');
        
        // Wait for selectedResource AND send context directly before prompt
        const waitForContextWithService = async () => {
          // eslint-disable-next-line no-console
          devLog('⏳ [Auto-render] Checking context state... selectedResource:', selectedResource?.data?.name, 'receivingFromParent:', receivingContextFromParentRef.current);
          
          // Check if we have selectedResource and we're not in the middle of receiving from parent
          if (selectedResource && selectedResource.data && selectedResource.data.name && !receivingContextFromParentRef.current) {
            // eslint-disable-next-line no-console
            devLog('✅ [Auto-render] Service selected and context ready:', selectedResource.data.name);
            
            // FORCE context send - critical for home-created projects
            // Temporarily clear the receiving flag to ensure context is sent
            const wasReceiving = receivingContextFromParentRef.current;
            receivingContextFromParentRef.current = false;
            
            // Directly send context to parent now (no waiting for useEffect)
            const contextSent = await sendContextToParent();
            
            // Restore flag
            receivingContextFromParentRef.current = wasReceiving;
            
            if (contextSent) {
              // eslint-disable-next-line no-console
              devLog('✅ [Auto-render] Context FORCED and sent directly to App');
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ [Auto-render] Context send failed, retrying...');
              setTimeout(waitForContextWithService, 150);
              return;
            }
            
            // Wait briefly for React state updates to propagate
            setTimeout(() => {
              // Dispatch custom event to populate chat input and auto-send (for home-created projects)
              const populateInputEvent = new CustomEvent('populate-chat-input', {
                detail: {
                  message: pendingPrompt,
                  autoSend: true, // Auto-send for home-created projects
                  setAskAboutComponents: true
                }
              });
              
              window.dispatchEvent(populateInputEvent);
              // eslint-disable-next-line no-console
              devLog('✅ [Auto-render] Dispatched populate-chat-input event with autoSend after direct context send');
            }, 300); // Shorter delay since we sent context directly
          } else {
            // Not ready yet, check again in 150ms
            setTimeout(waitForContextWithService, 150);
          }
        };
        
        // Start checking
        waitForContextWithService();
      }
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Auto-render failed:', error);

      const unreachable = error?.name === 'PreviewStatusError';
      const autoRenderErrorMessage = unreachable ? `${PREVIEW_UNREACHABLE_PREFIX}${error.message}` : `Auto-render failed: ${error.message}`;
      // Same "nothing to build yet" case as the main preview loader below —
      // a service with no files isn't a build failure, see isEmptyServiceError.
      const isEmptyService = Boolean(error.isEmptyService) || isEmptyServiceError(error.message);

      if (!isEmptyService && !unreachable) {
        // Highlight the Ask AI button instead of popping the blocking modal.
        setPendingFixError({ error: autoRenderErrorMessage, serviceName });
      }

      // Also mark the tab as errored (or empty) so it doesn't stay in loading state (by name, on the current tabs:
      // an index from this call's start may point elsewhere by now).
      setPreviewTabs(currentTabs => currentTabs.map(t => (t.serviceName === serviceName ? {
        ...t,
        loading: false,
        error: isEmptyService ? null : autoRenderErrorMessage,
        isEmptyService,
      } : t)));
    } finally {
      // Remove from processing set after a delay to prevent rapid re-triggers
      setTimeout(() => {
        handlingIframeErrorRef.current.delete(sessionKey);
      }, 5000);
    }
  };
  
  /**
   * Handle iframe load event to detect "Preview session not found" errors
   * When the container returns this error, automatically trigger a full re-render
   */
  const handleIframeLoad = async (event, tab) => {
    try {
      const iframe = event.target;
      const iframeWindow = iframe.contentWindow;

      // eslint-disable-next-line no-console
      devLog('📡 iframe onLoad triggered for:', {
        url: iframe.src,
        serviceName: tab?.serviceName
      });

      // Real bug: this used to only be called from inside the same-origin
      // content-sniffing branch below — but the real preview iframe is
      // cross-origin (preview-runner's own port vs this app's), so reading
      // iframe.contentWindow.document there throws a SecurityError
      // synchronously, jumping straight to the cross-origin catch handler
      // further down and skipping this call entirely. Server-side capture
      // (POST /api/thumbnails/capture, Puppeteer-based) only needs
      // iframe.src, not same-origin document access — it works fine
      // regardless of origin, it just was never actually being called.
      // Confirmed live: some Projects-grid cards had a real thumbnail
      // (captured before this coupling existed / via manual capture),
      // most had a flat placeholder gradient forever. Hoisted here and
      // called from BOTH the same-origin-success and
      // cross-origin-but-reachable success paths below.
      // Frontends only (utils/thumbnailEligibility.js): a backend's API Portal or plain-text answer, a worker or a
      // database has nothing to screenshot, so no capture is posted for it. `doc` is what the iframe shows, null when
      // it cannot be read.
      const triggerAutoCapture = (doc) => {
        if (!shouldAutoCaptureThumbnail(doc, { serviceName: tab?.serviceName, service: tab?.service })) {
          devLog('📸 No thumbnail for a non-frontend preview:', tab?.serviceName);
          return;
        }
        const autoCaptureProjectId = selectedProject?.id || selectedProject?.projectId || selectedProject?._id;
        if (autoCaptureProjectId && selectedTenant?.tenantId) {
          // eslint-disable-next-line no-console
          devLog('📸 Scheduling auto-capture for project:', selectedProject.name);
          scheduleAutoCapture(
            iframe,
            autoCaptureProjectId,
            selectedTenant.tenantId,
            3000 // Wait 3 seconds after load for content to fully render
          );
        }
      };

      // Try to read the iframe content to detect error responses
      try {
        const iframeDoc = iframeWindow.document;
        const bodyText = iframeDoc.body?.textContent || '';
        const bodyHTML = iframeDoc.body?.innerHTML || '';
        const documentTitle = iframeDoc.title || '';
        
        // Inject error capture script into iframe
        try {
          // eslint-disable-next-line no-use-before-define
          injectIframeErrorCapture(iframe);
        } catch (injectError) {
          // eslint-disable-next-line no-console
          devLog('Could not inject error capture (cross-origin?):', injectError.message);
        }
        
        // eslint-disable-next-line no-console
        devLog('📄 iframe content:', {
          bodyLength: bodyText.length,
          htmlLength: bodyHTML.length,
          title: documentTitle,
          preview: bodyText.substring(0, 300),
          htmlPreview: bodyHTML.substring(0, 300)
        });
        
        // Check for various error indicators:
        // 1. JSON error about preview session not found
        // 2. Chrome's "temporarily down" error page
        // 3. Empty or minimal content (< 100 chars likely means error)
        // 4. Session not found in error messages
        // 5. Chrome error pages
        // The runner's (or the sandbox gateway's) "This app is stopped" page: stopped on purpose, so no render (that
        // would start it behind the user's back); the Preview shows its stopped state with Start instead, and reloads
        // once the service runs again, whoever starts it (usePreviewWatchdog).
        if (bodyText.includes('This app is stopped')) {
          devLog('⏹️ [Preview] the iframe shows the stopped page for', tab.serviceName);
          patchPreviewTab(tab.serviceName, { stopped: true, starting: false, loading: false });
          return;
        }

        // The app's own answer (JSON `{"ok":true,…}`, or a worker's plain "Mongo consumer is running") is the app
        // working, not a failed load: short, no <div id=>, it used to match the "minimal content" rule below and was
        // logged as a failure on every load. Only the runner's real error pages count (previewReadiness.isAppOwnAnswer).
        const isAppAnswer = isAppOwnAnswer({ bodyText, bodyHTML, contentType: iframeDoc.contentType, title: documentTitle });
        const isPreviewSessionError = !isAppAnswer && (
          bodyText.includes('Preview session not found') ||
          bodyText.includes('"success":false') ||
          bodyText.includes('might be temporarily down') ||
          bodyText.includes('may have moved permanently') ||
          (bodyText.includes('Session') && bodyText.includes('not found')) ||
          (bodyText.length < 100 && !bodyHTML.includes('<div id=')) ||
          bodyHTML.includes('ERR_CONNECTION') ||
          bodyHTML.includes('chrome-error://') ||
          documentTitle.includes('Error') ||
          // BUG FIX: this exact error shape — the preview-runner proxy
          // itself failing to reach the just-starting dev server, e.g.
          // "Failed to proxy request to preview server: error sending
          // request for url (http://127.0.0.1:<port>/api/preview/.../
          // proxy/)" — matched NONE of the patterns above (not JSON, not
          // short, no "Session"/"not found" pair, no chrome-error), so it
          // fell through to the "success" branch and was displayed as if
          // it were the real, working app — live-confirmed via screenshot,
          // with a manual "Refresh" click being the only thing that
          // "fixed" it (the container had simply finished starting up by
          // then; nothing here was actually retrying automatically).
          bodyText.includes('Failed to proxy request to preview server') ||
          bodyText.includes('error sending request for url'));
        
        if (isPreviewSessionError) {
          // eslint-disable-next-line no-console
          devLog('🚨 Detected preview error in iframe:', {
            bodyLength: bodyText.length,
            preview: bodyText.substring(0, 200),
            serviceName: tab.serviceName,
            triggers: {
              hasSessionNotFound: bodyText.includes('Preview session not found'),
              hasSuccessFalse: bodyText.includes('"success":false'),
              hasTempDown: bodyText.includes('might be temporarily down'),
              hasMovedPermanently: bodyText.includes('may have moved permanently'),
              hasSessionError: bodyText.includes('Session') && bodyText.includes('not found'),
              isEmptyContent: bodyText.length < 100 && !bodyHTML.includes('<div id='),
              hasErrConnection: bodyHTML.includes('ERR_CONNECTION'),
              hasChromeError: bodyHTML.includes('chrome-error://'),
              hasErrorTitle: documentTitle.includes('Error')
            }
          });

          // eslint-disable-next-line no-console
          console.error(
            `Preview for "${tab.serviceName}" failed to load: ${bodyText.substring(0, 500) || documentTitle || 'empty response'}`
          );

          // Trigger auto-render
          await autoRenderOnPreviewNotFound(tab);
        } else {
          // eslint-disable-next-line no-console
          devLog('✅ Preview loaded successfully (has content)');
          
          // Clear render logs now that iframe has successfully loaded
          const tabIndex = previewTabs.findIndex(t => t.serviceName === tab.serviceName);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                renderLogs: undefined,
                renderPhase: undefined,
                renderProgress: undefined,
                iframeLoaded: true, // Mark iframe as loaded to hide loading overlay
                loading: false // Ensure loading is false
              };
              return updatedTabs;
            });
            // eslint-disable-next-line no-console
            devLog('🧹 Cleared render logs after successful iframe load');
          }
          
          // Auto-capture thumbnail after successful load. vfs-backed
          // (vfs/catalog-tier) projects have no Mongo _id — use the
          // same id fallback the manual capture path already uses below.
          triggerAutoCapture({ contentType: iframeDoc.contentType, title: documentTitle, bodyText, bodyHTML });
        }
      } catch (crossOriginError) {
        // Cross-origin iframe - cannot read its content to sniff for a JSON
        // error body the way the same-origin branch above does. This used to
        // just assume "cross-origin means the preview is working", which is
        // wrong: a proxy failure response (e.g. `{"success":false,...,"error":
        // "fetch failed"}`) served from a different origin/port than the BFF
        // is *also* cross-origin, and was silently treated as success — the
        // raw JSON stayed on screen with no error card and nothing logged.
        // Instead, probe reachability directly: a `no-cors` fetch resolves
        // for ANY completed HTTP response (regardless of status or missing
        // CORS headers) and only rejects on a genuine network failure (DNS,
        // connection refused, timeout) — which is exactly the "fetch failed"
        // case we need to catch.
        // eslint-disable-next-line no-console
        devLog('🔒 Cross-origin iframe — probing reachability before assuming success:', crossOriginError.message);

        try {
          await fetch(iframe.src, {
            mode: 'no-cors',
            cache: 'no-store',
            signal: AbortSignal.timeout(6000),
          });
          // Network request completed — treat as a working preview, same as before.
          const tabIndex = previewTabs.findIndex(t => t.serviceName === tab.serviceName);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                renderLogs: undefined,
                renderPhase: undefined,
                renderProgress: undefined,
                iframeLoaded: true,
                loading: false
              };
              return updatedTabs;
            });
          }
          // This IS the common case in practice (the real preview iframe is
          // almost always cross-origin) — see triggerAutoCapture's own
          // comment above for why this call was previously unreachable.
          triggerAutoCapture(null);
        } catch (unreachableError) {
          const errorMessage = `Preview server unreachable for "${tab.serviceName}": ${unreachableError.message || 'fetch failed'}`;
          // eslint-disable-next-line no-console
          console.error('❌', errorMessage);

          const tabIndex = previewTabs.findIndex(t => t.serviceName === tab.serviceName);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                loading: false,
                iframeLoaded: false,
                error: errorMessage,
              };
              return updatedTabs;
            });
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error handling iframe load:', error);
    }
  };
  
  /**
   * Manually capture thumbnail from current preview
   */
  const handleCaptureThumbnail = async () => {
    // Debug logging
    // eslint-disable-next-line no-console
    devLog('🔍 Capture thumbnail debug:');
    // eslint-disable-next-line no-console
    devLog('  iframeRef.current:', iframeRef.current);
    // eslint-disable-next-line no-console
    devLog('  selectedProject:', selectedProject);
    // eslint-disable-next-line no-console
    devLog('  selectedProject._id:', selectedProject?._id);
    // eslint-disable-next-line no-console
    devLog('  selectedProject.id:', selectedProject?.id);
    // eslint-disable-next-line no-console
    devLog('  selectedTenant:', selectedTenant);
    // eslint-disable-next-line no-console
    devLog('  selectedTenant.tenantId:', selectedTenant?.tenantId);
    // eslint-disable-next-line no-console
    devLog('  previewTabsCount:', previewTabs.length);
    // eslint-disable-next-line no-console
    devLog('  activePreviewTab:', activePreviewTab);

    // Try to get iframe from ref first, then from DOM
    let iframe = iframeRef.current;
    
    if (!iframe) {
      // Fallback: find the active iframe from DOM
      const iframes = document.querySelectorAll('.frontend-preview-iframe, .split-preview-iframe');
      // eslint-disable-next-line no-console
      devLog('🔍 Found iframes in DOM:', iframes.length);
      
      if (iframes.length > 0) {
        // Get the visible iframe (not display:none)
        for (const iframeElement of iframes) {
          const style = window.getComputedStyle(iframeElement.parentElement);
          if (style.display !== 'none') {
            iframe = iframeElement;
            // eslint-disable-next-line no-console
            devLog('✅ Found visible iframe');
            break;
          }
        }
      }
    }

    // Use selectedProject.id instead of selectedProject._id
    const projectId = selectedProject?.id || selectedProject?._id;
    
    if (!iframe || !projectId || !selectedTenant?.tenantId) {
      // eslint-disable-next-line no-console
      console.warn('⚠️  Cannot capture thumbnail - missing:');
      // eslint-disable-next-line no-console
      console.warn('  hasIframe:', !!iframe);
      // eslint-disable-next-line no-console
      console.warn('  hasProjectId:', !!projectId);
      // eslint-disable-next-line no-console
      console.warn('  hasTenant.tenantId:', !!selectedTenant?.tenantId);
      return;
    }

    setIsCapturingThumbnail(true);
    try {
      // eslint-disable-next-line no-console
      devLog('📸 Manually capturing thumbnail for project:', selectedProject.name);
      
      const result = await autoCaptureThumbnail(
        iframe,
        projectId,
        selectedTenant.tenantId,
        { delay: 500, quality: 0.9 } // Short delay, high quality for manual capture
      );
      
      if (result.success) {
        // eslint-disable-next-line no-console
        devLog('✅ Thumbnail captured:', result.thumbnailUrl);
        
        // Trigger a custom event to notify other components (like Projects page)
        window.dispatchEvent(new CustomEvent('thumbnail-updated', { 
          detail: { projectId, thumbnailUrl: result.thumbnailUrl } 
        }));
      } else {
        // eslint-disable-next-line no-console
        console.error('❌ Thumbnail capture failed:', result.error);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error capturing thumbnail:', error);
    } finally {
      setIsCapturingThumbnail(false);
    }
  };
  
  /**
   * Extract project ID from GitLab repository URL
   */
  const extractProjectIdFromUrl = (repositoryUrl) => {
    try {
      const url = new URL(repositoryUrl.replace('.git', ''));
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2) {
        return pathParts.join('/');
      }
      return null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse repository URL:', err);
      return null;
    }
  };

  /**
   * Check if the git provider tool is enabled on the backend and if the user is authenticated.
   * Used as a pre-flight check before any git fetch (preview, code tab, etc.)
   * @param {string} repoUrl - Repository URL
   * @returns {Promise<{toolEnabled: boolean, authenticated: boolean, providerName: string}>}
   */
  const checkGitToolStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    try {
      const { default: unifiedGitSvc } = await import('../services/UnifiedGitDataService');
      const provider = unifiedGitSvc.detectProvider(repoUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // 1. Check tool enablement on the server
      let toolEnabled = true;
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const bffUrl = config.bffUrl || window.location.origin;
        const tenantId = selectedTenant?.tenantId || selectedTenant?.id;
        const toolsParams = tenantId ? `?tenantId=${tenantId}` : '';
        const toolsRes = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (toolsRes.ok) {
          const toolsData = await toolsRes.json();
          const allTools = toolsData?.data?.items || [];
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          if (providerTools.length > 0) {
            toolEnabled = providerTools.some(t => t.enabled !== false);
          }
        }
      } catch (toolsErr) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ [Preview] Could not check tool status, assuming enabled:', toolsErr.message);
        toolEnabled = true;
      }

      // 2. Check authentication status
      const authStatus = await unifiedGitSvc.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ [Preview] Error checking git tool status:', err);
      return { toolEnabled: true, authenticated: false, providerName: 'Git' };
    }
  };

  /**
   * Fetch all files from GitLab/GitHub repository using a single recursive tree call.
   * The API always returns the full recursive tree in one request (recursive=true),
   * so we avoid redundant sub-directory calls by processing the flat list directly.
   */
  const fetchAllRepositoryFiles = async (projectId, repoUrl = null, onProgress = null) => {
    const unifiedGitDataService = (await import('../services/UnifiedGitDataService')).default;
    const sourceCode = {};
    // Use a blacklist of known binary/compiled extensions instead of a whitelist,
    // so backend projects (Java, Python, Go, …) are fully included.
    const binaryExtensions = new Set([
      '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.webp', '.tiff',
      '.pdf', '.zip', '.tar', '.gz', '.rar', '.7z',
      '.exe', '.dll', '.so', '.dylib', '.class', '.jar', '.war', '.ear',
      '.ttf', '.otf', '.woff', '.woff2', '.eot',
      '.mp3', '.mp4', '.avi', '.mov', '.mkv', '.wav', '.ogg'
    ]);
    const skipDirs = new Set(['node_modules', '.git', 'build', 'dist', '.next', 'target', 'vendor']);

    // Get project info to find default branch
    let ref = 'main';
    try {
      const info = await unifiedGitDataService.getProject(projectId, repoUrl);
      ref = info.default_branch || 'main';
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not get default branch, using main:', err);
    }

    // Single API call – the service always uses recursive=true so we get the full tree at once
    let allItems = [];
    try {
      allItems = await unifiedGitDataService.getRepositoryTree(projectId, repoUrl, '', ref);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching repository tree:', err);
      throw err;
    }

    // Filter out binary files and files inside ignored directories.
    // Files without an extension (e.g. .gitignore, .mia-template) are treated as text.
    const fileItems = allItems.filter(item => {
      if (item.type !== 'blob') return false;
      // Skip files inside ignored directories
      const parts = item.path.split('/');
      if (parts.some(part => skipDirs.has(part))) return false;
      // Skip known binary extensions; include everything else (text/code)
      const dotIdx = item.name.lastIndexOf('.');
      const ext = dotIdx !== -1 ? item.name.slice(dotIdx).toLowerCase() : '';
      return !binaryExtensions.has(ext);
    });

    const totalFiles = fileItems.length;
    let fetchedFiles = 0;

    // Fetch file contents sequentially with progress tracking
    for (const item of fileItems) {
      try {
        const fileData = await unifiedGitDataService.getFileContent(projectId, repoUrl, item.path, ref);
        sourceCode[item.path] = fileData.decodedContent || '';
        fetchedFiles++;

        if (onProgress) {
          const progress = Math.round((fetchedFiles / totalFiles) * 100);
          onProgress({
            current: fetchedFiles,
            total: totalFiles,
            percentage: progress,
            currentFile: item.path
          });
        }
      } catch (fileErr) {
        // eslint-disable-next-line no-console
        console.warn(`Could not fetch file ${item.path}:`, fileErr);
        fetchedFiles++; // Still count as processed
      }
    }

    return sourceCode;
  };
  
  const selectedMicroservice = selectedResource?.type === 'service' ? selectedResource.data : (selectedResource?.type === 'configuration' ? selectedResource.data : null);
  
  // Notify parent of service/branch/tab changes, keeping the file and line in the URL (hooks/useCodeLinks.js). Held back
  // while a deep-linked service is still being resolved (see urlInitPendingRef) and while a Files link is being turned
  // into its file's Code-tab URL.
  useSelectionUrlSync({selectedResource, selectedRevision, activeTab, onSelectionChange, urlInitPendingRef, hold: Boolean(filesLinkToService)});
  
  // Get repository URL - normalize different field names (repoUrl, repositoryUrl, sshUrl)
  const repositoryUrl = useMemo(() => {
    if (!selectedMicroservice) return null;
    const url = selectedMicroservice.repositoryUrl || selectedMicroservice.repoUrl || selectedMicroservice.sshUrl || null;
    // eslint-disable-next-line no-console
    devLog('🔍 [Canvas] Getting repository URL:', {
      serviceName: selectedMicroservice.name,
      type: selectedResource?.type,
      repositoryUrl: selectedMicroservice.repositoryUrl,
      repoUrl: selectedMicroservice.repoUrl,
      sshUrl: selectedMicroservice.sshUrl,
      resolved: url
    });
    return url;
  }, [selectedMicroservice, selectedResource]);
  // Home (the 'configuration' resource) of a vfs project has no Code tab of its own: its code is the Files view. At
  // Home with the Code tab still set (remembered from a service, or the header's Logs while on Code) the canvas showed
  // the empty "Source Code · Select a … microservice" view; show Home instead.
  useEffect(() => {
    // Not while the URL names a service: the Code tab it asks for is the service's (the configuration showing is a
    // remembered Home about to give way to that service), and the URL wins.
    if (homeLeavesCodeTab({
      resourceType: selectedResource?.type || null,
      activeTab,
      serviceInUrl: Boolean(params.microservice),
      hasCodeOfItsOwn: Boolean(repositoryUrl || selectedResource?.data?.vfsBacked),
    })) {
      setActiveTab('preview');
    }
  }, [selectedResource, activeTab, repositoryUrl, params.microservice]);
  
  // Repository view state - controls whether to show repository tree or service details
  const [showRepositoryView, setShowRepositoryView] = useState(false);
  
  // Tree panel visibility state
  const [showTreePanel, setShowTreePanel] = useState(true);
  
  // Repository actions dropdown state
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const repoDropdownRef = useRef(null);

  // Auto-select first file when generatedFiles change
  useEffect(() => {
    if (generatedFiles && generatedFiles.length > 0 && !selectedFile) {
      setSelectedFile(generatedFiles[0]);
    }
  }, [generatedFiles, selectedFile]);

  // Persist Mia Platform selections to localStorage
  useEffect(() => {
    if (selectedTenant) {
      localStorage.setItem('mia_selectedTenant', JSON.stringify(selectedTenant));
    } else {
      localStorage.removeItem('mia_selectedTenant');
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('mia_selectedProject', JSON.stringify(selectedProject));
    } else {
      localStorage.removeItem('mia_selectedProject');
    }
  }, [selectedProject]);
  
  // Persist selected resource for URL reconstruction
  useEffect(() => {
    if (selectedResource) {
      // Include projectId so we can validate it matches when reconstructing URL
      const resourceWithProject = {
        ...selectedResource,
        projectId: selectedProject?.id || selectedProject?.projectId || selectedProject?._id
      };
      localStorage.setItem('mia_selectedResource', JSON.stringify(resourceWithProject));
    } else {
      localStorage.removeItem('mia_selectedResource');
    }
  }, [selectedResource, selectedProject]);
  
  // Persist active tab for URL reconstruction
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('mia_activeTab', activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedRevision) {
      localStorage.setItem('mia_selectedRevision', JSON.stringify(selectedRevision));
    } else {
      localStorage.removeItem('mia_selectedRevision');
    }
  }, [selectedRevision]);

  // Load files from IndexedDB when a service is selected
  // This ensures files are available for AI file requests immediately
  useEffect(() => {
    const loadFilesForSelectedService = async () => {
      if (!selectedResource || selectedResource.type !== 'service' || !selectedProject?.id) {
        return;
      }

      const serviceName = selectedResource.data?.name;
      if (!serviceName) {
        return;
      }

      // Prevent loading files multiple times for the same service
      const serviceKey = `${selectedProject.id}|${serviceName}`;
      if (lastLoadedFilesServiceRef.current === serviceKey) {
        return;
      }

      try {
        // eslint-disable-next-line no-console
        devLog(`📂 Auto-loading files for selected service: ${serviceName}`);

        const filesFromDb = await indexedDbFileStorage.getAllFiles(
          selectedProject.id,
          serviceName
        );

        if (filesFromDb && filesFromDb.length > 0) {
          const allFilesWithContent = filesFromDb.map(file => ({
            path: file.filePath,
            content: file.content
          }));

          // Mark this service as loaded BEFORE calling onProjectContextChange
          lastLoadedFilesServiceRef.current = serviceKey;

          // Update project context with loaded files
          if (onProjectContextChange) {
            onProjectContextChange({
              tenant: selectedTenant,
              project: selectedProject,
              revision: selectedRevision,
              selectedResource: selectedResource,
              selectedMicroservice: {
                name: serviceName,
                type: selectedResource.data?.type,
                repositoryUrl: selectedResource.data?.repoUrl || selectedResource.data?.sshUrl || selectedResource.data?.repositoryUrl
              },
              allFiles: allFilesWithContent,
              selectedFile: selectedFile,
              configuration: configuration,
              collections: collections,
              projectId: selectedProject.id
            });
          }

          // eslint-disable-next-line no-console
          devLog(`✅ Loaded ${filesFromDb.length} files from IndexedDB for AI context`);
        } else {
          // Task #157 — real, live-confirmed bug: this whole block only
          // called `onProjectContextChange` in the branch above, gated on
          // IndexedDB already having cached files for this service. A
          // vfs-backed service (the only kind vfs/Home-created
          // projects ever have) never populates IndexedDB at all — it's a
          // legacy git-workflow cache — so for a BRAND-NEW service (or any
          // vfs-backed one) this update was silently skipped forever.
          // App.jsx's `canvasProjectContext.selectedMicroservice` (which
          // VfsChatPanel's session key AND its x-mia-vfs-service header
          // both derive from) then never advanced past whatever service was
          // selected before — confirmed live: switching from a working
          // "app" service to a brand-new empty "chess" service and asking
          // the agent to build chess instead silently ran vfs_write_file
          // against "app", overwriting the working service instead of
          // populating the new one, even though the on-screen dropdown
          // already showed "chess" selected. Fire the same update here too,
          // with whatever files are actually available (none, for a fresh
          // vfs service) — the agent doesn't need `allFiles` pre-populated
          // to work correctly, but it does need `selectedMicroservice` to
          // be correct.
          // eslint-disable-next-line no-console
          devLog(`⚠️ No files found in IndexedDB for service ${serviceName} — updating project context anyway (vfs-backed services never populate IndexedDB)`);
          if (onProjectContextChange) {
            onProjectContextChange({
              tenant: selectedTenant,
              project: selectedProject,
              revision: selectedRevision,
              selectedResource: selectedResource,
              selectedMicroservice: {
                name: serviceName,
                type: selectedResource.data?.type,
                repositoryUrl: selectedResource.data?.repoUrl || selectedResource.data?.sshUrl || selectedResource.data?.repositoryUrl
              },
              allFiles: [],
              selectedFile: selectedFile,
              configuration: configuration,
              collections: collections,
              projectId: selectedProject.id
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load files from IndexedDB:', error);
      }
    };

    loadFilesForSelectedService();
  }, [selectedResource, selectedProject]);

  // Close repo dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (repoDropdownRef.current && !repoDropdownRef.current.contains(event.target)) {
        setShowRepoDropdown(false);
      }
    };

    if (showRepoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRepoDropdown]);

  // Close repo dropdown when resource changes
  useEffect(() => {
    if (selectedResource) {
      setShowRepoDropdown(false);
    }
  }, [selectedResource]);

  // Load tenants when canvas is opened
  useEffect(() => {
    if (isVisible && tenants.length === 0 && !tenantsLoading) {
      loadTenants();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Load project context from parent (when project is selected from Projects page)
  useEffect(() => {
    if (projectContext && isVisible) {
      devLog('📋 Received project context from parent:', {
        projectId: projectContext.projectId,
        projectName: projectContext.projectName,
        tenantId: projectContext.tenant?.tenantId
      });
      
      // Mark that we're receiving context from parent
      receivingContextFromParentRef.current = true;
      
      // Set tenant if available and valid (only if actually different)
      if (projectContext.tenant && projectContext.tenant.tenantId) {
        const newTenantId = projectContext.tenant.tenantId;
        const currentTenantId = selectedTenant?.id || selectedTenant?.tenantId;
        
        if (newTenantId !== currentTenantId) {
          setSelectedTenant({
            id: projectContext.tenant.tenantId,
            tenantId: projectContext.tenant.tenantId,
            name: projectContext.tenant.name
          });
        }
      }
      
      // Set project if available (only if actually different)
      if (projectContext.project) {
        const newProjectId = projectContext.project._id || projectContext.project.projectId || projectContext.project.id;
        const currentProjectId = selectedProject?.id;
        
        if (newProjectId && newProjectId !== currentProjectId) {
          // Project is changing - clear selected resource from previous project
          setSelectedResource(null);
          
          setSelectedProject({
            id: newProjectId,
            name: projectContext.project.name,
            description: projectContext.project.description,
            tenantId: projectContext.project.tenantId || projectContext.tenant?.tenantId,
            raw: projectContext.project
          });
        }
      }
      
      // Set revision if available (only if actually different)
      if (projectContext.revision) {
        const newRevisionId = projectContext.revision.revisionId;
        const currentRevisionId = selectedRevision?.id;
        
        if (newRevisionId && newRevisionId !== currentRevisionId) {
          setSelectedRevision({
            id: projectContext.revision.revisionId,
            name: projectContext.revision.name || projectContext.revision.revisionId
          });
        }
      }
      
      // Clear the flag after a brief delay to allow state updates to trigger dependent useEffects
      setTimeout(() => {
        receivingContextFromParentRef.current = false;
        // eslint-disable-next-line no-console
        devLog('✅ Cleared receivingContextFromParentRef flag - Canvas can now respond to updates');
      }, 200); // Short delay for responsiveness
    }
  }, [projectContext, isVisible]);

  // Load projects when tenant is selected
  useEffect(() => {
    if (selectedTenant && !projectsLoading) {
      // eslint-disable-next-line no-console
      devLog('🏢 Loading projects for tenant:', selectedTenant);
      
      // Validate tenant has required ID before proceeding
      if (!selectedTenant.id) {
        console.error('❌ Tenant selected but ID is missing!', selectedTenant);
        // Clear invalid tenant to prevent loops
        setSelectedTenant(null);
        return;
      }
      
      loadProjects(selectedTenant.id, '', 1, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);
  
  // Load revisions when project is selected
  useEffect(() => {
    // eslint-disable-next-line no-console
    devLog('🔄 useEffect for revisions triggered. selectedProject:', selectedProject, 'revisionsLoading:', revisionsLoading);
    
    // Only a detail of the open project changed (its thumbnail, a rename): it is loaded, keep what is selected.
    if (!projectNeedsReload({ projectId: selectedProject?.id, lastProjectId: lastProjectIdRef.current, revisionId: selectedRevision?.id, configuration })) { return; }
    if (selectedProject && selectedProject.id && !revisionsLoading) {
      // Detect project switch and cleanup UI state
      const isProjectSwitch = lastProjectIdRef.current && lastProjectIdRef.current !== selectedProject.id;
      
      if (isProjectSwitch) {
        // eslint-disable-next-line no-console
        devLog('🔄 Project switch detected, cleaning up state...', {
          from: lastProjectIdRef.current,
          to: selectedProject.id
        });
        
        // Clear preview tabs immediately (UI state only)
        setPreviewTabs([]);
        
        // Clear configuration from previous project so dropdown shows empty until new config loads
        setConfiguration(null);
        
        // Clear selected revision IMMEDIATELY to prevent config loading with stale revision
        setSelectedRevision(null);
        
        // Note: We keep render sessions alive so they can be reused
        // when the user returns to this project
        
        // Clear loading and error tracking
        loadingPreviewRef.current.clear();
        errorCountRef.current = {};
        autoTriggeredPullRef.current.clear();
        lastLoadedServiceRef.current = null;
        lastLoadedFilesServiceRef.current = null;
        externalPreviewShownRef.current.clear();
        
        // Clear configuration to prevent auto-selection from old project's services. `placeholder`: not the new
        // project's configuration yet, so how the project is entered (useProjectConfiguration) is decided on the real one.
        setConfiguration({
          endpoints: {},
          collections: {},
          groups: [],
          secrets: [],
          cmsCategories: {},
          services: {},
          placeholder: true
        });
      }
      
      // Update the last project ID tracker
      lastProjectIdRef.current = selectedProject.id;
      
      // eslint-disable-next-line no-console
      devLog('📝 Loading revisions for project:', selectedProject);
      
      // Clear selected resource when switching projects to force auto-selection of new project's service
      setSelectedResource(null);
      
      loadRevisions(selectedProject.id);
      // Force git fetch when a new project is selected
      setForceGitFetch(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedProject?.id]);
  
  // Fetch project thumbnail when project is selected
  useEffect(() => {
    // Only fetch if we don't already have a thumbnail
    if (selectedProject?.id && selectedProject?.tenantId && !selectedProject?.thumbnail) {
      getProjectThumbnail(selectedProject.id, selectedProject.tenantId).then(thumbnailUrl => {
        if (thumbnailUrl) {
          setSelectedProject(prev => ({
            ...prev,
            thumbnail: thumbnailUrl
          }));
        }
      }).catch(error => {
        console.debug('Failed to fetch thumbnail:', error);
      });
    }
  }, [selectedProject?.id, selectedProject?.tenantId, selectedProject?.thumbnail]);
  
  // Load configuration when revision is selected
  // The sandbox woke while the project was opening, or it is still opening a while later (ProjectLoading): what failed
  // meanwhile is loaded again; a project that never got as far as being selected (Flow was restarting) reloads the page.
  // The project's services changed (added, deleted, renamed, a new folder under services/: hooks/useServiceFolders):
  // the configuration the header's service picker, Start all and Preview read follows, without a page reload.
  useEffect(() => {
    const changed = (event) => {
      if (!selectedProject?.id || event?.detail?.projectId !== selectedProject.id || !selectedRevision?.id) { return; }
      loadConfiguration(selectedProject.id, selectedRevision.id);
    };
    window.addEventListener(SERVICES_CHANGED_EVENT, changed);
    return () => window.removeEventListener(SERVICES_CHANGED_EVENT, changed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedRevision?.id]);
  const reloadProjectAfterWake = useCallback(() => {
    if (!selectedProject?.id) { window.location.reload(); return; }
    if (selectedRevision?.id) { loadConfiguration(selectedProject.id, selectedRevision.id); } else { loadRevisions(selectedProject.id); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedRevision?.id]);

  useEffect(() => {
    if (selectedProject && selectedProject.id && selectedRevision && selectedRevision.id && !configLoading) {
      // eslint-disable-next-line no-console
      devLog('🔧 Loading configuration for project:', selectedProject.id, 'revision:', selectedRevision.id);
      loadConfiguration(selectedProject.id, selectedRevision.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRevision?.id, selectedProject?.id]);
  
  // Notify parent when selected file or resource changes
  useEffect(() => {
    const updateProjectContext = async () => {
      // Skip if we're currently receiving context from parent to prevent feedback loop
      if (receivingContextFromParentRef.current) {
        devLog('⏭️ Skipping context update (receiving from parent)');
        return;
      }
      
      // Skip if we don't have a project yet (but allow updates without revision for file requests)
      if (!selectedProject) {
        // eslint-disable-next-line no-console
        devLog('⏭️ Skipping context update (no project selected)');
        return;
      }
      
      if (onProjectContextChange) {
        // Get all files from IndexedDB for the selected microservice
        let allFiles = null;
        if (selectedProject && selectedProject.id && selectedMicroservice && selectedMicroservice.name) {
          const filesArray = await indexedDbFileStorage.getAllFiles(selectedProject.id, selectedMicroservice.name);
          allFiles = await Promise.all(filesArray.map(async (fileObj) => {
            const filePath = fileObj.filePath;
            const gitState = fileObj.gitState || fileObj.state; // Support both new and legacy format
            return {
              path: filePath,
              name: fileObj.fileName,
              content: fileObj.content || '',
              isNew: gitState === GIT_STATE.NEW || gitState === 'new',
              modified: gitState !== GIT_STATE.ORIGINAL && gitState !== 'original'
            };
          }));
          
          // eslint-disable-next-line no-console
          devLog(`📦 Canvas: Loaded ${allFiles.length} files from IndexedDB for ${selectedMicroservice.name}`);
          
          // Auto-trigger Pull if no files are loaded in IndexedDB — but never
          // for a vfs-backed service: there's no git repo to pull from, and
          // this used to fall through into RepositoryTree's git-tool-status
          // check with an undefined repositoryUrl, which always resolves to
          // "disabled" and renders a blocking "Git Integration Disabled"
          // screen for a project that never had git involved at all.
          const isVfsBackedService = Boolean(selectedMicroservice.vfsBacked) || vfsClient.isVfsBackedProject(selectedProject?.id);
          if (!isVfsBackedService && (!allFiles || allFiles.length === 0)) {
            const serviceKey = `${selectedProject.id}|${selectedMicroservice.name}`;

            // Only auto-trigger once per service to prevent infinite loop
            if (!autoTriggeredPullRef.current.has(serviceKey)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ No files in IndexedDB for project ${selectedProject.id}, service ${selectedMicroservice.name}. Auto-triggering Pull from GitLab...`);
              
              // Mark that we've triggered pull for this service
              autoTriggeredPullRef.current.add(serviceKey);
              
              // Dispatch event to trigger Pull operation
              window.dispatchEvent(new CustomEvent('autoTriggerGitPull', {
                detail: {
                  projectId: selectedProject.id,
                  serviceName: selectedMicroservice.name,
                  reason: 'No files in IndexedDB'
                }
              }));
            } else {
              // eslint-disable-next-line no-console
              devLog(`⏭️ Skipping auto-trigger for ${selectedMicroservice.name} (already triggered)`);
            }
          } else {
            // Check for files that need to be pushed using getChangedFilesForPreview
            const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
              selectedProject.id,
              selectedMicroservice.name
            );
            
            // eslint-disable-next-line no-console
            devLog(`📂 Project context updated with ${allFiles.length} files from ${selectedMicroservice.name} (${changedFiles.length} files need to be pushed)`);
            
            // Update hasModifications state for Push button
            setHasModifications(changedFiles.length > 0);
          }
        }
      
      // Get file content for selected file
      const selectedFileContent = selectedFile && selectedMicroservice ? (await indexedDbFileStorage.getFile(
        selectedProject.id,
        selectedMicroservice.name,
        selectedFile.path
      ))?.content || selectedFile.content : null;
      
      // Build the new context object
      const newContext = {
        tenant: selectedTenant,
        project: selectedProject,
        revision: selectedRevision,
        selectedResource: selectedResource,
        selectedMicroservice: selectedMicroservice ? {
          name: selectedMicroservice.name,
          type: selectedMicroservice.type,
          repositoryUrl: repositoryUrl
        } : null,
        selectedFile: selectedFile ? {
          name: selectedFile.name,
          path: selectedFile.path,
          content: selectedFileContent,
          language: selectedFile.language
        } : null,
        allFiles: allFiles,
        configuration: configuration,
        collections: collections,
        projectId: selectedProject?.id
      };
      
      // Only send update if context actually changed (compare essential fields)
      const contextKey = JSON.stringify({
        tenantId: selectedTenant?.id,
        projectId: selectedProject?.id,
        revisionId: selectedRevision?.id,
        resourceName: selectedResource?.data?.name,
        resourceType: selectedResource?.type,
        microserviceName: selectedMicroservice?.name,
        filePath: selectedFile?.path,
        fileCount: allFiles?.length,
        configHash: configuration ? Object.keys(configuration).sort().join(',') : null,
        collectionsHash: collections ? Object.keys(collections).sort().join(',') : null
      });
      
      // Only send if context actually changed
      if (lastProjectContextRef.current !== contextKey) {
        lastProjectContextRef.current = contextKey;
        
        if (allFiles && allFiles.length > 0) {
          // eslint-disable-next-line no-console
          devLog('📤 Sending updated project context to parent:', {
            tenant: selectedTenant?.name,
            project: selectedProject?.name,
            revision: selectedRevision?.name,
            resource: selectedResource?.data?.name,
            fileCount: allFiles?.length
          });
        } else {
          // eslint-disable-next-line no-console
          devLog('📤 Sending updated project context to parent (no files):', {
            tenant: selectedTenant?.name,
            project: selectedProject?.name,
            revision: selectedRevision?.name,
            resource: selectedResource?.data?.name
          });
        }
        onProjectContextChange(newContext);
      } else {
        // eslint-disable-next-line no-console
        devLog('⏭️ Skipping context update (no changes)');
      }
      }
    };
    
    updateProjectContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // OPTIMIZATION: Removed selectedFile from dependencies to prevent full project reload on file selection
  // File changes are still captured in the context update, just not triggering a full reload
  }, [selectedMicroservice, repositoryUrl, selectedTenant, selectedProject, selectedRevision, selectedResource, configuration, collections]);
  
  // Extract context sending logic for reuse (direct calls)
  const sendContextToParent = useCallback(async () => {
    // Skip if we're receiving context from parent
    if (receivingContextFromParentRef.current) {
      devLog('⏭️ Skipping direct context send (receiving from parent)');
      return false;
    }
    
    // Skip if we don't have required data
    if (!selectedProject || !onProjectContextChange) {
      devLog('⏭️ Skipping direct context send (no project or callback)');
      return false;
    }
    
    // Get all files from IndexedDB for the selected microservice
    let allFiles = null;
    if (selectedProject && selectedProject.id && selectedMicroservice && selectedMicroservice.name) {
      const filesArray = await indexedDbFileStorage.getAllFiles(selectedProject.id, selectedMicroservice.name);
      allFiles = await Promise.all(filesArray.map(async (fileObj) => {
        const filePath = fileObj.filePath;
        const gitState = fileObj.gitState || fileObj.state; // Support both new and legacy format
        return {
          path: filePath,
          name: fileObj.fileName,
          content: fileObj.content || '',
          isNew: gitState === GIT_STATE.NEW || gitState === 'new',
          modified: gitState !== GIT_STATE.ORIGINAL && gitState !== 'original'
        };
      }));
    }
    
    // Get file content for selected file
    const selectedFileContent = selectedFile ? (await indexedDbFileStorage.getFile(
      selectedProject.id,
      selectedMicroservice.name,
      selectedFile.path
    ))?.content || selectedFile.content : null;
    
    // Build the context object
    const newContext = {
      tenant: selectedTenant,
      project: selectedProject,
      revision: selectedRevision,
      selectedResource: selectedResource,
      selectedMicroservice: selectedMicroservice ? {
        name: selectedMicroservice.name,
        type: selectedMicroservice.type,
        repositoryUrl: repositoryUrl
      } : null,
      selectedFile: selectedFile ? {
        name: selectedFile.name,
        path: selectedFile.path,
        content: selectedFileContent,
        language: selectedFile.language
      } : null,
      allFiles: allFiles,
      configuration: configuration,
      collections: collections,
      projectId: selectedProject?.id
    };
    
    // Send context to parent
    devLog('📤 [Direct Send] Sending context to parent:', {
      project: selectedProject?.name,
      service: selectedMicroservice?.name,
      fileCount: allFiles?.length
    });
    
    onProjectContextChange(newContext);
    return true;
  }, [selectedProject, selectedTenant, selectedRevision, selectedResource, selectedMicroservice, selectedFile, configuration, collections, repositoryUrl, onProjectContextChange]);

  // Load source code for preview from IndexedDB
  const loadSourceCode = useCallback(async () => {
    if (selectedProject && selectedResource?.data?.name) {
      try {
        // Validate we have valid parameters
        if (!selectedProject.id || !selectedResource.data.name) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Cannot load source code: invalid project ID or service name', {
            projectId: selectedProject.id,
            serviceName: selectedResource.data.name
          });
          return;
        }

        const files = await indexedDbFileStorage.getFilesForAIContext(
          selectedProject.id,
          selectedResource.data.name
        );
        // getFilesForAIContext returns an object, not an array
        const fileCount = files ? Object.keys(files).length : 0;
        // eslint-disable-next-line no-console
        devLog(`📂 Loaded ${fileCount} files from IndexedDB for tree view`);
        
        // Force tree refresh by incrementing the key
        setTreeRefreshKey(prev => prev + 1);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load source code from IndexedDB:', error);
      }
    }
  }, [selectedProject?.id, selectedResource?.data?.name]);

  useEffect(() => {
    loadSourceCode();
  }, [loadSourceCode]);

  // Close repo dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRepoDropdown && !event.target.closest('.repo-dropdown-wrapper')) {
        setShowRepoDropdown(false);
      }
    };

    if (showRepoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRepoDropdown]);

  // Function to load tenants
  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      
      const tenantsData = await miaPlatformDataService.getTenants();
      // eslint-disable-next-line no-console
      devLog('🏢 Tenants received:', tenantsData);
      const formattedTenants = tenantsData.map(tenant => 
        miaPlatformDataService.formatTenant(tenant)
      );
      // eslint-disable-next-line no-console
      devLog('🏢 Formatted tenants:', formattedTenants);
      setTenants(formattedTenants);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load tenants:', error);
      setTenantsError(error.message || 'Failed to load tenants');
    } finally {
      setTenantsLoading(false);
    }
  };

  // Function to load projects with optional search and pagination
  const loadProjects = async (tenantId, searchQuery = '', page = 1, append = false) => {
    try {
      // eslint-disable-next-line no-console
      devLog('📁 Loading projects with tenantId:', tenantId, 'search:', searchQuery, 'page:', page);
      setProjectsLoading(true);
      setProjectsError(null);
      
      const result = await miaPlatformDataService.getProjects(tenantId, searchQuery, page);
      // eslint-disable-next-line no-console
      devLog('📁 Projects received:', result);
      
      const formattedProjects = result.data.map(project => 
        miaPlatformDataService.formatProject(project)
      );
      
      if (append) {
        // Append to existing projects for infinite scroll
        setProjects(prev => [...prev, ...formattedProjects]);
      } else {
        // Replace projects for new search or initial load
        setProjects(formattedProjects);
      }
      
      // Store pagination info
      setProjectsPagination(result.pagination);
      
      // eslint-disable-next-line no-console
      devLog('📊 Pagination info:', result.pagination);
      // eslint-disable-next-line no-console
      devLog('📊 Has more projects:', result.pagination?.has_more);
      // eslint-disable-next-line no-console
      devLog('📊 Current page:', result.pagination?.page, 'of', result.pagination?.total_pages);
      
      // Auto-select first project if only one is available
      if (!append && formattedProjects.length === 1) {
        setSelectedProject(formattedProjects[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load projects:', error);
      setProjectsError(error.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  // Handle project search
  const handleProjectSearch = useCallback((searchQuery) => {
    if (selectedTenant) {
      // Reset to page 1 when searching
      setProjectsCurrentPage(1);
      // Only clear selection if user is actively searching (not when clearing search)
      // Don't clear when searchQuery is empty (programmatic clear after selection)
      if (searchQuery && searchQuery !== projectsSearchQuery) {
        setSelectedProject(null);
        setSelectedRevision(null);
        setRevisions([]);
      }
      setProjectsSearchQuery(searchQuery);
      loadProjects(selectedTenant.id, searchQuery, 1, false);
    }
  }, [selectedTenant, projectsSearchQuery]);

  // Handle load more for infinite scroll
  const handleLoadMoreProjects = useCallback(() => {
    if (selectedTenant && projectsPagination?.has_more && !projectsLoading) {
      const nextPage = projectsCurrentPage + 1;
      setProjectsCurrentPage(nextPage);
      loadProjects(selectedTenant.id, projectsSearchQuery, nextPage, true);
    }
  }, [selectedTenant, projectsPagination, projectsLoading, projectsCurrentPage, projectsSearchQuery]);

  // Handle tenant selection
  const handleTenantSelect = (tenant) => {
    // Ensure tenant object has required fields
    const formattedTenant = {
      id: tenant.id || tenant.tenantId,
      tenantId: tenant.id || tenant.tenantId, // Backward compatibility
      name: tenant.name || 'Unnamed Tenant'
    };
    setSelectedTenant(formattedTenant);
    setSelectedProject(null); // Reset project when tenant changes
    setProjects([]); // Clear previous projects
    // Reset pagination state
    setProjectsPagination(null);
    setProjectsCurrentPage(1);
    setProjectsSearchQuery('');
    // Clear center panel
    setSelectedRevision(null);
    setRevisions([]);
    setConfiguration(null);
    setSelectedResource(null);
    setSelectedFile(null);
    
    // Notify parent about context change (tenant selected, no project yet)
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: tenant,
        project: null,
        revision: null
      });
    }
  };

  // Handle project selection
  const handleProjectSelect = (project) => {
    // eslint-disable-next-line no-console
    devLog('🎯 Project selected:', project);
    
    /**
     * RACE CONDITION FIX:
     * Don't send project context update here with revision:null.
     * Wait for revisions to load first, then the updateProjectContext useEffect
     * will send the complete context with the selected revision.
     * This fixes the issue where creating a project from home would fail to
     * get file and project context until chat was manually stopped and continued.
     */
    
    // Ensure project object has required fields
    const formattedProject = {
      id: project.id || project._id || project.projectId,
      name: project.name,
      description: project.description,
      tenantId: project.tenantId,
      raw: project.raw || project
    };
    
    setSelectedProject(formattedProject);
    
    // CRITICAL FIX: Sync tenant with project's tenantId
    // If project belongs to a different tenant, update selectedTenant
    if (project.tenantId && (!selectedTenant || selectedTenant.id !== project.tenantId)) {
      const projectTenant = tenants.find(t => t.tenantId === project.tenantId || t.id === project.tenantId);
      if (projectTenant) {
        // eslint-disable-next-line no-console
        devLog('🔄 Syncing tenant with project - updating from', selectedTenant?.name, 'to', projectTenant.name);
        const formattedTenant = {
          id: projectTenant.id || projectTenant.tenantId,
          tenantId: projectTenant.id || projectTenant.tenantId,
          name: projectTenant.name
        };
        setSelectedTenant(formattedTenant);
      } else {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Could not find tenant for project tenantId:', project.tenantId);
      }
    }
    
    // Reset revision and configuration when project changes
    setSelectedRevision(null);
    setRevisions([]);
    setConfiguration(null);
    // Clear center panel
    setSelectedResource(null);
    setSelectedFile(null);
    // Clear preview session and URL
    setPreviewSessionId(null);
    setPreviewUrl(null);
    
    
    // Don't send context update here - wait for revisions to load
    // This prevents race condition where context is sent with revision:null
    // and then the revisions load later but context is not updated
  };
  
  // Handle create project button click
  //
  // Lazy tenant resolution (same fallback chain as App.jsx's
  // handleStartChatFromHome / mia-flow-cli's `/agent`): don't hard-block on a
  // pre-populated selectedTenant — fall back to the first already-loaded
  // tenant, and only surface a specific error if no tenant can be resolved at
  // all, instead of a blanket precondition alert.
  const handleCreateProjectClick = async () => {
    let tenant = selectedTenant;

    if (!tenant && tenants.length > 0) {
      tenant = tenants[0];
      setSelectedTenant(tenant);
    }

    if (!tenant && !tenantsLoading) {
      try {
        const tenantsData = await miaPlatformDataService.getTenants();
        const formatted = (tenantsData || []).map(t => miaPlatformDataService.formatTenant(t));
        setTenants(formatted);
        if (formatted.length > 0) {
          tenant = formatted[0];
          setSelectedTenant(tenant);
        }
      } catch (error) {
        console.error('Failed to auto-resolve an organization for project creation:', error);
      }
    }

    if (!tenant) {
      // NOT `show-toast` — that global CustomEvent has no listener anywhere
      // in this codebase (confirmed dead this session while building
      // HistoryPanel.jsx); dispatching it here would silently show nothing.
      // Canvas.jsx has no local toast mechanism of its own either, so a
      // blocking alert is the only guaranteed-visible option for this rare
      // true-failure case — worse UX than a toast, but real, unlike the
      // alternative. This only fires when tenant resolution itself fails,
      // not as a precondition gate (see this function's lazy-resolution
      // comment above).
      alert('Could not find an organization to create the project in. Please try again or select one manually.');
      return;
    }

    setIsCreateProjectModalOpen(true);
  };
  
  // Handle create project submission
  const handleCreateProject = async (projectData) => {
    try {
      // Call the service to create project via BFF
      const result = await miaPlatformDataService.createProject(projectData);
      
      // Refresh projects list
      await loadProjects(selectedTenant.id, projectsSearchQuery, 1, false);
      
      // Auto-select the newly created project
      const newProject = result.project;
      if (newProject) {
        const formattedProject = {
          id: newProject._id || newProject.id,
          name: newProject.name,
          tenantId: newProject.tenantId,
          ...newProject
        };
        handleProjectSelect(formattedProject);
      }
      
      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to create project:', error);
      // Enhance error message for permissions issues
      if (error.message.includes('FORBIDDEN')) {
        throw new Error('Permission denied: The service account does not have permission to create projects in this organization. Please contact your Mia Platform administrator to grant project creation permissions.');
      }
      throw error;
    }
  };
  
  // Function to load revisions
  const loadRevisions = async (projectId) => {
    try {
      setRevisionsLoading(true);
      setRevisionsError(null);
      
      const revisionsData = await miaPlatformDataService.getRevisions(projectId);
      // eslint-disable-next-line no-console
      devLog('📝 Revisions received:', revisionsData);
      const formattedRevisions = revisionsData.map(revision => 
        miaPlatformDataService.formatRevision(revision)
      );
      setRevisions(formattedRevisions);
      
      // Auto-select default revision if available
      const defaultRevision = formattedRevisions.find(rev => rev.isDefault);
      if (defaultRevision) {
        setSelectedRevision(defaultRevision);
      } else if (formattedRevisions.length > 0) {
        setSelectedRevision(formattedRevisions[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load revisions:', error);
      setRevisionsError(error.message || 'Failed to load revisions');
      // Vfs/vfs-only projects (created via createQuickVfsProject
      // or directly by an agent's vfs_create_project) have no Console
      // revision at all — getRevisions() 403/404s for them by design, every
      // single time. Real bug found live: since `loadConfiguration` only
      // ever runs from an effect gated on `selectedRevision && selectedRevision.id`
      // (see that effect a bit below), leaving selectedRevision permanently
      // null here meant loadConfiguration NEVER ran for these projects — no
      // config.services, no real selectedResource, so the initial Home
      // chat prompt's own wait-for-context gate (Canvas.jsx's
      // waitForContextReady) looped forever and `run_sse` was never called
      // at all. Confirmed via adk-be-app access logs: a session was created
      // but zero POST /run_sse followed it, for 60+ seconds, for a
      // brand-new Home-created project. A synthetic default revision here
      // unblocks loadConfiguration, which itself builds config.services
      // from vfs-service directly for these projects anyway (see its own
      // vfs merge logic) — Console's revision id is never actually used by
      // that path, so "main" is just a stable placeholder identity.
      setSelectedRevision({id: 'main', name: 'main'});
    } finally {
      setRevisionsLoading(false);
    }
  };
  
  // Function to load configuration (microservices and other resources)
  const loadConfiguration = async (projectId, revisionId) => {
    try {
      setConfigLoading(true);
      setConfigError(null);

      // Console's microservices endpoint 403/404s for an vfs/vfs-only
      // project (it was never registered in Console) — that's expected, not
      // fatal: this function's real job for those projects is the
      // vfs-service merge a few lines below, which never depended on this
      // call succeeding. Previously this threw straight into the outer
      // catch, aborting before that merge ever ran — the same root cause
      // documented on loadRevisions' catch above (config.services stayed
      // empty forever, so no real selectedResource, so the initial Home
      // chat prompt never sent). Caught locally so a real Console project's
      // own microservices are still used when the call succeeds, but a
      // failure just means "no Console-registered services", not "give up".
      let config;
      try {
        config = await miaPlatformDataService.getMicroservices(
          projectId,
          revisionId
        );
      } catch (microservicesError) {
        // eslint-disable-next-line no-console
        console.warn('No Console-registered microservices for this project (expected for vfs/vfs-only projects):', microservicesError.message);
        config = {services: {}};
      }
      // eslint-disable-next-line no-console
      devLog('🔧 Configuration received:', config);

      // Merge in any vfs-only services (created via CreateServiceModal's
      // clone/archive-import path, see handleCreateService below) — Console
      // never learns about these, so they'd otherwise be invisible to every
      // UI that enumerates config.services (the service dropdowns, etc.).
      const vfsServiceNames = vfsClient.getVfsServiceNames(projectId);
      if (vfsServiceNames.length > 0) {
        config.services = { ...(config.services || {}) };
        for (const name of vfsServiceNames) {
          if (!config.services[name]) {
            config.services[name] = { name, type: 'custom', vfsBacked: true };
          }
          // displayName (relabeled from the chat's goal once known — see
          // App.jsx's handleSendMessage) is separate from `name` (the real
          // vfs folder identity, used for selection/file paths); UI should
          // show displayName when present, but keep using `name` as the key.
          const displayName = vfsClient.getVfsServiceDisplayName(projectId, name);
          if (displayName) {
            config.services[name] = { ...config.services[name], displayName };
          }
        }
      }

      // For an vfs vfs-only project, ALSO ask vfs-service itself what's
      // actually there — a service the agent created by writing files
      // directly (vfs_write_file) is never routed through CreateServiceModal,
      // so it's invisible to the localStorage-based registry above even
      // though the Code tab's own file tree (a separate, direct vfs-service
      // query) already shows it as a top-level folder. Every top-level
      // directory under the project root is a service, by the same
      // convention VfsChatPanel's `/service`/`/files` commands and
      // fetchServiceFiles already use.
      // Not gated on vfsClient.isVfsBackedProject(projectId): that flag is a
      // purely local, browser-side localStorage registry, set only when THIS
      // browser itself created the project through the frontend's own flow.
      // A project an agent created directly (vfs_create_project, no browser
      // involved at all) is real in vfs-service but was never recorded here —
      // gating on it meant this whole vfs-tree lookup silently never ran for
      // any agent-created project, the exact same "local registry doesn't
      // know about agent-created projects" gap already found and fixed once
      // in the BFF (see mia-flow-bff's vfsProjectExists). Best-effort instead:
      // just try vfs-service directly; a project it doesn't know about either
      // 404s and is caught below same as before.
      try {
        const { isDirectory, entries } = await vfsClient.listDirectoryEntries(projectId, '.');
        if (isDirectory) {
          // Every top-level folder besides `services` (its own section) and
          // internal/dotfile ones — feeds the Dashboard's generic
          // "one section per project folder" feature (docs/, memory/,
          // scripts/ from adk-be-app's scaffold, commit 759b7ce, plus
          // anything else a user or agent creates at the project root).
          config.otherFolders = entries
            .filter((entry) => entry.type === 'directory' && entry.name !== 'services' && !entry.name.startsWith('.'))
            .map((entry) => entry.name);
          // Same gap, a third time: indexedDbFileStorage's write-through push
          // to vfs-service (Monaco edits -> IndexedDB -> vfs-service) is ALSO
          // gated on this same localStorage-only flag, so an agent-created
          // project's edits silently never left IndexedDB even after this
          // effect proved vfs-service really does have the project. Mark it
          // here, as a side effect of the live check succeeding, rather than
          // only when the frontend's own creation flow happens to call it.
          vfsClient.markProjectVfsBacked(projectId);
          config.services = { ...(config.services || {}) };
          // Current (post service-layout-refactor) convention: every real
          // service lives under its own subfolder of `services/`, sibling to
          // `.mia/project.json` (project metadata) and any project-level
          // thumbnail — never mixed into the same folder as a service's own
          // source. List `services/` directly and register each subfolder as
          // its own vfsServiceName ("services/<name>", the full path — the
          // exact same string vfs_tools.py's `_scoped_path` on the agent side
          // and buildVfsSourcePayload on this side already treat as an
          // opaque subfolder path, so no other plumbing needs to know this
          // convention exists).
          const hasServicesDir = entries.some((entry) => (
            entry.type === 'directory' && entry.name === 'services'
          ));
          // Legacy convention, kept for backward compatibility with any
          // project that still has its single app's package.json/index.html
          // directly at the workspace root (the old, now-wrong layout) —
          // '.' is vfs-service's own way of saying "the project root itself".
          const hasRootManifest = entries.some((entry) => (
            entry.type === 'file' && ['package.json', 'index.html'].includes(entry.name)
          ));
          if (hasServicesDir) {
            try {
              const servicesListing = await vfsClient.listDirectoryEntries(projectId, 'services');
              if (servicesListing.isDirectory) {
                const realServiceNames = new Set();
                for (const entry of servicesListing.entries) {
                  if (entry.type !== 'directory') {
                    continue;
                  }
                  const vfsServiceName = `services/${entry.name}`;
                  realServiceNames.add(vfsServiceName);
                  if (!config.services[vfsServiceName]) {
                    config.services[vfsServiceName] = { name: vfsServiceName, type: 'custom', vfsBacked: true };
                  }
                }
                // BUG FIX: createQuickVfsProject (Home's "build an app" flow)
                // unconditionally pre-registers 'services/app' as a LOCAL
                // (localStorage-only) pointer at project-creation time,
                // before the agent has written a single file — see its own
                // comment ("no vfs-service write happens yet, the folder
                // becomes real the moment the agent writes the first file
                // under it"). When the agent instead names its own service(s)
                // something else entirely (e.g. "frontend"/"backend", never
                // touching "app" at all), that pointer is never cleaned up —
                // the merge above (vfsServiceNames) keeps adding it forever,
                // showing a phantom service with no real backing folder in
                // every dropdown that enumerates config.services. Now that
                // THIS listing is the real, authoritative ground truth for
                // what's actually under services/, prune any vfs-backed
                // entry this listing didn't confirm — never touches a real
                // Console-registered service (vfsBacked is only ever set by
                // this vfs-only code path).
                for (const name of Object.keys(config.services)) {
                  if (config.services[name].vfsBacked && name.startsWith('services/') && !realServiceNames.has(name)) {
                    delete config.services[name];
                  }
                }
              }
            } catch (servicesListError) {
              console.warn('Failed to list vfs-service services/ folder:', servicesListError);
            }
          } else if (hasRootManifest) {
            // The name IS "." deliberately, not a display label — it's used
            // verbatim as vfsServiceName when building the vfsSource payload
            // (buildVfsSourcePayload) react-container-live's fetchSourceFromVfs
            // expects, and "." is vfs-service's own convention for "the
            // project root itself" (same value used everywhere else this
            // session's manual vfsSource tests targeted a root-level app).
            // The dropdown/tab UI special-cases this exact value to show a
            // friendlier label instead of a literal ".".
            if (!config.services['.']) {
              config.services['.'] = { name: '.', type: 'custom', vfsBacked: true };
            }
          } else {
            // Oldest convention (pre-`services/`, pre-root-manifest): every
            // top-level directory was treated as its own service. Kept only
            // as a last-resort fallback for a stray/legacy project shape;
            // the project's metadata (dot-folders) and its brain/ (the
            // knowledge base every project gets, utils/projectLayout.js) are
            // never a service: a project with only its brain has no apps.
            for (const entry of entries) {
              if (entry.type === 'directory' && isServiceFolderName(entry.name) && !config.services[entry.name]) {
                config.services[entry.name] = { name: entry.name, type: 'custom', vfsBacked: true };
              }
            }
          }
        }
      } catch (vfsTreeError) {
        console.warn('Failed to list vfs-service project root for services:', vfsTreeError);
      }

      // The brain is never an app, also when a configuration saved before said so (utils/projectLayout.js).
      config.services = withoutNonServices(config.services);
      // Which project this configuration is (useProjectConfiguration decides how a project is entered on its own one).
      config.loadedFor = projectId;
      setConfiguration(config);
      
      // Merge collections from config with IndexedDB
      const configCollections = config?.collections || {};
      
      // Migrate from localStorage if needed (first time)
      await indexedDbCollectionStorage.migrateFromLocalStorage(projectId);
      
      const mergedCollections = await indexedDbCollectionStorage.mergeWithConfiguration(
        projectId,
        configCollections
      );
      setCollections(mergedCollections);
      
      // eslint-disable-next-line no-console
      devLog('📊 Collections merged:', {
        fromConfig: Object.keys(configCollections).length,
        total: Object.keys(mergedCollections).length
      });

      // Fetch full project configuration from BFF
      await loadFullProjectConfiguration(projectId);
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load configuration:', error);
      setConfigError(error.message || 'Failed to load configuration');
    } finally {
      setConfigLoading(false);
      
      // Clear the project switching flag now that Canvas configuration is fully loaded
      // This allows URL syncing to resume after initial project load is complete
      const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
      if (switchingToProject === projectId) {
        sessionStorage.removeItem('mia_switching_to_project');
        // eslint-disable-next-line no-console
        devLog('✅ Canvas: Cleared project switching flag after config load:', projectId);
      }
    }
  };
  
  // Function to load full project configuration from Console API via BFF
  const loadFullProjectConfiguration = async (projectId) => {
    try {
      const fullConfig = await miaPlatformDataService.getFullProjectConfiguration(projectId);
      
      // Store in state and localStorage
      setFullProjectConfig(fullConfig);
      localStorage.setItem('mia_fullProjectConfig', JSON.stringify(fullConfig));
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to load full project configuration:', error);
      // Don't fail the whole configuration load if this fails
    }
  };
  
  // Handle revision selection
  const handleRevisionSelect = (revision) => {
    setSelectedRevision(revision);
    setConfiguration(null); // Clear configuration when revision changes
    
    // Notify parent about revision context change
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: selectedTenant,
        project: selectedProject,
        projectId: selectedProject?.id,
        revision: revision,
        selectedFile: null, // Clear file when revision changes
        selectedResource: null // Clear resource when revision changes
      });
    }
  };
  
  // Automatically detect all previewable services and create tabs when configuration loads
  useEffect(() => {
    if (!configuration || !configuration.services || !selectedProject) {
      return;
    }

    const servicesArray = Object.values(configuration.services);
    const previewableServices = servicesArray.filter(service => {
      // A vfs-backed service (no repoUrl/sshUrl/repositoryUrl at all — the
      // common case for a project created from Home, per #76/#77) is just as
      // previewable as a git-backed one; same fix already applied to
      // useProjectConfiguration's auto-select effect, but this separate tab-
      // creation effect independently reimplemented the git-only check
      // without the vfs-backed exception, so a vfs-only project's
      // previewableServices was always empty here — no tab was ever
      // created for `selectedResource` (which WAS correctly set to the
      // vfs-backed service by the other effect), so no /api/render call
      // ever fired and the UI stayed on "Loading frontend preview..."
      // forever. Confirmed live (task #98): npm install completed fully on
      // disk, but react-container-live never saw a session created.
      if (service.vfsBacked) return true;
      const detection = detectServiceType(service);
      const hasRepo = service.repoUrl || service.sshUrl || service.repositoryUrl;
      return (detection.isFrontend || detection.isBackend) && hasRepo;
    });

    if (previewableServices.length === 0) {
      // task #81 — don't blow away a tab that showExternalPreview injected
      // for a vfs-only project with no console-registered microservices at
      // all (the common "brand-new project" case): this effect re-runs
      // whenever `selectedResource` changes, which showExternalPreview's own
      // setSelectedResource call unavoidably triggers, racing straight into
      // this "no previewable console services" branch and previously wiping
      // the just-added tab on the very next render. `external: true` marks
      // exactly those externally-injected tabs so they survive this reset,
      // while any stale real (console-service-backed) tab still gets
      // cleared as before.
      setPreviewTabs((prev) => prev.filter((tab) => tab.external));
      return;
    }

    // eslint-disable-next-line no-console
    devLog(`🎯 Found ${previewableServices.length} previewable service(s):`, previewableServices.map(s => s.name));

    // Don't automatically create tabs or select services
    // Let the user explicitly select a service from the dropdown
    // This prevents loading all services at once and causing loops
    
    // Only initialize preview tabs if we're on the preview tab and have a selected service
    if (activeTab === 'preview' && selectedResource?.type === 'service') {
      const service = selectedResource.data;
      // A pinned runnable has its own tab (openRunnablePreview); an owner whose pinned stack runs in its place
      // (runnablePreview.carrierFor) shows that stack.
      if (service?.pinnedRunnable) { return; }
      const carrier = runnablePreview.carrierFor(service?.name);
      if (carrier) { openRunnablePreview(carrier, '/', { instead: true }); return; }
      const repoUrl = service.repoUrl || service.sshUrl || service.repositoryUrl;

      // Run git pre-flight asynchronously before creating the tab, so we
      // never show "Rendering … preview…" spinner for a blocked provider.
      (async () => {
        // 1. Check if we already have a tab (avoid duplicate creation)
        // We'll do the real setPreviewTabs below after the async check.

        // 2. Check IndexedDB for cached files — if present, git isn't needed now
        let hasLocalFiles = false;
        try {
          const { default: idbStorage } = await import('../utils/indexedDbFileStorage');
          const localFiles = await idbStorage.getAllFiles(selectedProject.id, service.name);
          hasLocalFiles = localFiles.length > 0;
        } catch { /* ignore */ }

        // 3. If no local files, run pre-flight against the git provider
        let blockedError = null;
        if (!hasLocalFiles && repoUrl) {
          const gitStatus = await checkGitToolStatus(repoUrl);
          if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
            blockedError = !gitStatus.toolEnabled
              ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
              : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
            // eslint-disable-next-line no-console
            console.warn(`🚫 [TabCreate] Blocked (${blockedError}) for`, service.name);
          }
        }

        // 3b. Same reasoning as the git pre-flight above, for a different cause
        // of the same symptom: a vfs-backed service with no files yet would
        // otherwise create the tab with loading:true, show the "Rendering…"
        // spinner, and then actually POST /api/render for a project with
        // nothing to build — preview-runner has no clear success/failure
        // signal for that (nothing to detect a framework from), so it just
        // sits at "Starting" forever instead of failing fast. Checking
        // emptiness here, before the tab (and its spinner) is even created,
        // means an empty service goes straight to the existing "Empty
        // project" tab state (isEmptyService, loading:false — same state
        // loadServicePreview's own pre-flight already produces for the
        // already-open-tab case, see ~line 4267) instead of spinning.
        let emptyService = false;
        if (!blockedError && (service.vfsBacked || vfsClient.isVfsBackedProject(selectedProject?.id))) {
          try {
            emptyService = await vfsClient.isServiceEmpty(selectedProject?.id, service.name);
          } catch { /* best-effort — fall through to a normal render attempt */ }
        }

        // 3c. The shared service state (waiting for its first answer on a cold load): a service someone stopped gets a
        // tab in the stopped state, never one in "Rendering…" that the loader would then render (and so start).
        const { state: runStateAtOpen, entry: runEntryAtOpen } = blockedError || emptyService
          ? { state: null, entry: null }
          : await previewRunState(service.name);
        const openStopped = runStateAtOpen === 'stopped';
        const openReady = runStateAtOpen === 'ready' ? runEntryAtOpen : null;
        // 3d. A command-line tool (utils/cliServices.js, known from the services answer just read) has no page: its tab is
        // Try it, never "Rendering…" nor a render (it would start, exit at once and be started again).
        const cliAtOpen = blockedError || emptyService ? null : cliServiceOf(selectedProject?.id, service.name);
        if (cliAtOpen) {
          const cliPatch = { cli: cliAtOpen, loading: false, stopped: false, starting: false, error: null, isEmptyService: false };
          setPreviewTabs((currentTabs) => {
            const index = currentTabs.findIndex((tab) => tab.serviceName === service.name);
            if (index === -1) {
              setActivePreviewTab(currentTabs.length);
              return [...currentTabs, { serviceName: service.name, service, url: null, sessionId: buildSessionId(selectedProject?.id, service.name, getOrCreateUserId(selectedProject?.id, service.name)), iframeKey: Date.now(), ...cliPatch }];
            }
            setActivePreviewTab(index);
            return currentTabs.map((tab, at) => (at === index ? { ...tab, ...cliPatch } : tab));
          });
          return;
        }
        // Its own session does not serve its app (failed, crashed, none) while its pinned stack runs: the stack instead.
        if (!blockedError && !emptyService && !openStopped && !openReady) {
          const carrier = await runnablePreview.carrierNow(service.name);
          if (carrier) { openRunnablePreview(carrier, '/', { instead: true }); return; }
        }

        // 4. Create / update tab with the correct initial state
        setPreviewTabs(currentTabs => {
          const existingTabIndex = currentTabs.findIndex(tab => tab.serviceName === service.name);

          if (existingTabIndex === -1) {
            // ── New tab ────────────────────────────────────────────────────────
            const userId = getOrCreateUserId(selectedProject?.id, service.name);
            const existingSession = getSessionFromStorage(selectedProject?.id, service.name, userId);
            const hasExistingSession = existingSession && existingSession.previewUrl;

            // Use saved session values when available (correct port/language/framework from a
            // previous render). For brand-new sessions leave url/port/language/framework
            // null — the real values will be filled in once source code is fetched and
            // analysed. sessionId is the exception: it is deterministic, so PreviewLogs (keyed off
            // tab.sessionId) can stream the container's output as soon as it comes up.
            // The runner builds its id from the project and the service alone again
            // (`<project>-<service, "/" -> "-">`, previewSessionId.js), so it is known before the render.
            const predictedSessionId = buildSessionId(selectedProject?.id, service.name, userId);
            const newTab = {
              serviceName: service.name,
              service: service,
              url: hasExistingSession ? existingSession.previewUrl : null,
              sessionId: hasExistingSession ? existingSession.sessionId : predictedSessionId,
              userId: userId,
              containerPort: hasExistingSession ? existingSession.containerPort : null,
              language: hasExistingSession ? existingSession.language : null,
              framework: hasExistingSession ? existingSession.framework : null,
              // If blocked, or the service has no files yet: show that state
              // immediately — no spinner, no doomed real render attempt.
              loading: !hasExistingSession && !blockedError && !emptyService,
              iframeLoaded: hasExistingSession,
              error: hasExistingSession ? null : (blockedError || null),
              isEmptyService: !hasExistingSession && !blockedError && emptyService,
              iframeKey: Date.now(),
              // The shared state first: stopped → the stopped state (no render); already running → the app.
              ...(openStopped ? STOPPED_TAB_PATCH : {}),
              ...(openReady ? {
                url: openReady.previewUrl,
                sessionId: openReady.sessionId || predictedSessionId,
                loading: false, error: null, isEmptyService: false, iframeLoaded: false,
              } : {}),
            };

            // eslint-disable-next-line no-console
            devLog('📝 Creating new tab for:', service.name,
              hasExistingSession ? '(existing session)' : blockedError ? '(blocked)' : '(new session)');
            setActivePreviewTab(currentTabs.length);
            return [...currentTabs, newTab];
          } else {
            // ── Existing tab ───────────────────────────────────────────────────
            // eslint-disable-next-line no-console
            devLog('♻️ Tab already exists for:', service.name, 'at index:', existingTabIndex);

            const existingTab = currentTabs[existingTabIndex];

            // Stopped (the shared state): the stopped state, unless a render the user asked for is in flight.
            if (openStopped && !existingTab.stopped && !existingTab.starting && !(existingTab.loading && existingTab.renderPhase)) {
              const updatedTabs = [...currentTabs];
              updatedTabs[existingTabIndex] = { ...existingTab, ...STOPPED_TAB_PATCH };
              setActivePreviewTab(existingTabIndex);
              return updatedTabs;
            }

            // If blocked and tab has no URL/error yet, update it to show the error
            if (blockedError && !existingTab.url && !existingTab.error) {
              const updatedTabs = [...currentTabs];
              updatedTabs[existingTabIndex] = {
                ...existingTab,
                loading: false,
                error: blockedError
              };
              setActivePreviewTab(existingTabIndex);
              return updatedTabs;
            }

            if (!existingTab.url && !existingTab.error) {
              const userId = getOrCreateUserId(selectedProject?.id, service.name);
              const existingSession = getSessionFromStorage(selectedProject?.id, service.name, userId);
              if (existingSession && existingSession.previewUrl) {
                // eslint-disable-next-line no-console
                devLog('✨ Found existing session for tab, loading immediately!');
                const updatedTabs = [...currentTabs];
                updatedTabs[existingTabIndex] = {
                  ...existingTab,
                  url: existingSession.previewUrl,
                  sessionId: existingSession.sessionId,
                  userId: userId,
                  tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                  projectId: selectedProject?.id,
                  containerPort: existingSession.containerPort,
                  language: existingSession.language,
                  framework: existingSession.framework,
                  loading: false,
                  error: null
                };
                setActivePreviewTab(existingTabIndex);
                return updatedTabs;
              }
            }

            setActivePreviewTab(existingTabIndex);
            return currentTabs;
          }
        });
      })();
    }
    // Note: previewTabs is intentionally excluded from dependencies to prevent infinite loops
    // We only want this effect to run when configuration, project, activeTab, or selectedResource changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, selectedProject, activeTab, selectedResource]);
  
  // Check if selected resource is a frontend/backend service and load preview
  // Only auto-load if user is already on preview tab
  useEffect(() => {
    let isCancelled = false; // Track if effect was cleaned up
    
    const loadServicePreview = async () => {
      // Only proceed if we have a selected service resource
      // Skip if it's configuration/project config (no preview needed)
      if (!selectedResource || selectedResource.type !== 'service' || selectedResource.type === 'configuration') {
        setPreviewUrl(null);
        setPreviewIsEmptyService(false);
        return;
      }

      // NEW: Only auto-load preview if user is already on the preview tab
      // This prevents automatic tab switching when clicking services in sidebar
      if (activeTab !== 'preview') {
        // eslint-disable-next-line no-console
        devLog('⏭️ Skipping auto-preview load (not on preview tab)');
        return;
      }

      const service = selectedResource.data;
      // A pinned runnable is shown from its own session (openRunnablePreview), never rendered as code.
      if (service?.pinnedRunnable) { return; }
      
      // CRITICAL: Skip if we're receiving context from parent (prevents cascade)
      // EXCEPT for home-created projects where we need to pull files and start preview
      const isHomeCreatedProject = sessionStorage.getItem('mia_pending_initial_prompt');
      if (receivingContextFromParentRef.current && !isHomeCreatedProject) {
        // eslint-disable-next-line no-console
        devLog('⏭️ Skipping preview load (receiving from parent)');
        return;
      }
      
      if (receivingContextFromParentRef.current && isHomeCreatedProject) {
        // eslint-disable-next-line no-console
        devLog('🆕 Home-created project detected - proceeding with preview load despite receiving from parent');
      }
      
      // Check if there's already an error for this service in preview tabs
      // Don't retry if the error is about BFF connection
      if (previewTabs.length > 0) {
        const existingTab = previewTabs.find(tab => tab.serviceName === service.name);
        if (existingTab && existingTab.error && 
            (existingTab.error.includes('BFF server') || existingTab.error.includes('No source files'))) {
          // Don't retry loading if we already have a BFF connection error
          return;
        }
      }
      
      // Prevent concurrent loads of the same service
      // BUT: Check if the loading state is stale (stuck for more than 3 minutes)
      if (loadingPreviewRef.current.has(service.name)) {
        // Check if the corresponding tab is actually loading
        const correspondingTab = previewTabs.find(tab => tab.serviceName === service.name);
        
        // Clear stale loading state if:
        // 1. Tab is loading but has an error (failed build/render)
        // 2. Tab is loading but has no URL (stuck in initial state)
        // 3. No corresponding tab exists (orphaned loading state)
        if (!correspondingTab || 
            (correspondingTab.loading && correspondingTab.error) ||
            (correspondingTab.loading && !correspondingTab.url)) {
          // eslint-disable-next-line no-console
          devLog('🔄 Clearing stale loading state for:', service.name, {
            hasTab: !!correspondingTab,
            hasError: correspondingTab?.error,
            hasUrl: correspondingTab?.url
          });
          loadingPreviewRef.current.delete(service.name);
          // Continue with loading
        } else {
          // eslint-disable-next-line no-console
          devLog('⏸️  Service is already loading, skipping:', service.name);
          return;
        }
      }
      
      // Circuit breaker: Stop trying after 3 consecutive failures
      const errorCount = errorCountRef.current[service.name] || 0;
      if (errorCount >= 3) {
        console.warn(`⚠️ Circuit breaker: Too many errors for ${service.name}, stopping retries`);
        return;
      }
      
      // Prevent reloading the same service if nothing relevant changed
      // Check if this is the same service as last time AND neither selectedRevision nor forceGitFetch changed
      const serviceKey = `${selectedProject?.id}|${service.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
      if (lastLoadedServiceRef.current === serviceKey) {
        // Same service, same revision, same fetch mode - skip reload
        return;
      }
      // BUG FIX (live-reported + confirmed in real preview-runner logs: two
      // /api/render calls for the same session landing ~1s apart, the
      // second one killing/respawning the dev server the first had JUST
      // brought up — visually "the updated app shows for under a second,
      // then goes not available"). This ref used to only get marked deep
      // inside various success branches below, all well after real network
      // awaits (port-mapping fetch, existing-session check) — a real race:
      // a second run of this same effect, landing in that window (this
      // component has several other state updates that share dependencies
      // with this effect — selectedProject/selectedResource/
      // selectedRevision), saw the guard above as not-yet-claimed and
      // fired its own render too. Claiming the key synchronously, before
      // any awaits, closes that window — a second concurrent run now sees
      // this immediately and skips. The later re-assignments further down
      // are now redundant but harmless (same value) — left in place to
      // keep this a minimal, low-risk diff.
      lastLoadedServiceRef.current = serviceKey;

      // Mark as loading
      loadingPreviewRef.current.add(service.name);
      
      // eslint-disable-next-line no-console
      devLog('🔍 Checking service for preview:', {
        name: service.name,
        tags: service.tags,
        type: selectedResource.type
      });
      
      // Intelligently detect service type
      const {isFrontend, isBackend, confidence} = detectServiceType(service);
      
      // eslint-disable-next-line no-console
      devLog('🏷️ Service type detected:', {isFrontend, isBackend, confidence});
      
      // Check if user has explicitly selected a tab (from preview tabs)
      const isExplicitTabSelection = previewTabs.length > 0 && 
        previewTabs.some(tab => tab.serviceName === service.name);
      
      // If tabs exist, check if this tab already has content ready
      // This prevents unnecessary re-renders when just switching between tabs
      if (previewTabs.length > 0) {
        const tabForThisService = previewTabs.find(tab => tab.serviceName === service.name);
        if (tabForThisService) {
          // If the tab already has a URL, the container/iframe is ready - just show it
          if (tabForThisService.url) {
            devLog('✅ Tab already loaded with URL, skipping re-render:', service.name);
            loadingPreviewRef.current.delete(service.name);
            return;
          }
          // If tab is not currently loading and has no URL, skip for now
          if (!tabForThisService.loading && !tabForThisService.url) {
            return;
          }
        }
      }
      
      // The shared service state first (the one the header reads). A service someone stopped shows the stopped
      // state with Start, the same on a fresh load as when switching to Preview: opening the Preview no longer starts
      // it behind the user's back. One already running and ready is shown as it is, without a render.
      // Waits for the store's first answer: on a cold load it has none yet, and deciding then rendered.
      const { action: decidedAction, state: runState, entry: runnerEntry } = await decidePreviewOpen({
        projectId: selectedProject?.id,
        serviceName: service.name,
        force: Boolean(forceGitFetch),
        pendingInitialPrompt: Boolean(isHomeCreatedProject),
      });
      // One Preview open posts at most one render: this effect re-runs a moment after its own render (its `force`
      // flag flips back), when the runner may not list the new session yet. A render this loader posted for the same
      // service in the last RENDER_ONCE_MS is followed, not posted again.
      const renderKey = `${selectedProject?.id}|${service.name}`;
      const postedAt = renderPostedRef.current.get(renderKey) || 0;
      const openAction = decidedAction === 'render' && Date.now() - postedAt < RENDER_ONCE_MS ? 'follow' : decidedAction;
      // The owner's own session does not serve its app while its pinned stack runs: the Preview shows the stack, and
      // never renders the owner's build again.
      if (openAction === 'render') {
        const carrier = await runnablePreview.carrierNow(service.name);
        if (carrier) {
          loadingPreviewRef.current.delete(service.name);
          lastLoadedServiceRef.current = null;
          if (!isCancelled) { openRunnablePreview(carrier, '/', { instead: true }); }
          return;
        }
      }
      if (openAction === 'render') { renderPostedRef.current.set(renderKey, Date.now()); }
      devLog('🔎 [Preview] run state of', service.name, 'in', selectedProject?.id, ':', runState, '→', openAction, { force: Boolean(forceGitFetch), pendingInitialPrompt: Boolean(isHomeCreatedProject), hasEntry: Boolean(runnerEntry) });
      if (openAction === 'stopped') {
        // With no tab yet (a cold load), the tab-creation effect reads the same state and creates it stopped.
        devLog('⏹️ [Preview]', service.name, 'is stopped: showing the stopped state, not starting it');
        patchPreviewTab(service.name, STOPPED_TAB_PATCH);
        loadingPreviewRef.current.delete(service.name);
        return;
      }
      if (openAction === 'follow') {
        // A build is already under way: one Preview open posts at most one render. The watchdog shows the app once
        // the shared state says it is ready (or says the build failed).
        devLog('⏳ [Preview]', service.name, 'is already starting: following it, no second render');
        patchPreviewTab(service.name, {
          loading: true, stopped: false, starting: false, error: null, isEmptyService: false, followingBuild: true,
          sessionId: runnerEntry?.sessionId || sessionIdFor(selectedProject?.id, service.name),
          renderPhase: 'starting',
        });
        loadingPreviewRef.current.delete(service.name);
        return;
      }
      if (openAction === 'show') {
        devLog('✅ [Preview]', service.name, 'already runs: showing it');
        patchPreviewTab(service.name, {
          url: runnerEntry.previewUrl,
          sessionId: runnerEntry.sessionId || sessionIdFor(selectedProject?.id, service.name),
          projectId: selectedProject?.id,
          loading: false, stopped: false, starting: false, error: null, isEmptyService: false,
          renderPhase: undefined, renderProgress: undefined, renderLogs: undefined,
          iframeKey: Date.now(),
        });
        loadingPreviewRef.current.delete(service.name);
        return;
      }

      // Priority: Always show frontend if available in the project
      // BUT: If user explicitly clicked a tab, respect their choice
      let serviceToPreview = service;
      
      // Allow both frontend and backend services to be previewed directly
      // Backend services (Java, Node, Go, Python, etc.) will use their respective container-live servers
      //
      // A vfs-backed service never gets tags/dockerImage from Console (there's no
      // Console provisioning step for it — see App.jsx/CreateServiceModal.jsx), so
      // detectServiceType() above — called without source code, since we haven't
      // fetched any yet — can only fall back to matching the service NAME against
      // frontend/backend keyword patterns. A user-chosen project/service name
      // (e.g. "project2") won't match any of them, giving 'none' confidence and
      // blocking the preview entirely even though the service is perfectly
      // previewable. Don't bail for a vfs-backed service here: let it fall
      // through to the vfs-service file fetch below, where getContainerRoutingInfo
      // re-runs detection against the REAL file contents (high confidence).
      if (!isFrontend && !isBackend && !isExplicitTabSelection && !service.vfsBacked) {
        // Service is neither frontend nor backend, and wasn't explicitly selected - don't show preview
        setPreviewUrl(null);
        return;
      }

      // Check if we have repository URL to fetch source code
      // Try multiple fields: repoUrl, sshUrl, repositoryUrl
      // A vfs-backed project (created without a git connection — see
      // App.jsx/vfsClient.js) has none of these and doesn't need one: its
      // content lives in vfs-service instead, fetched further below.
      const repoUrl = serviceToPreview.repoUrl || serviceToPreview.sshUrl || serviceToPreview.repositoryUrl;

      // eslint-disable-next-line no-console
      devLog('📦 Repository URL:', repoUrl, serviceToPreview.vfsBacked ? '(vfs-backed, no repo needed)' : '');

      if (!repoUrl && !serviceToPreview.vfsBacked) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Service has no repository URL:', serviceToPreview.name);

        // Update preview tabs if they exist
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            const updatedTabs = [...previewTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: false,
              error: 'No repository URL available for this service'
            };
            setPreviewTabs(updatedTabs);
          }
        } else {
          setPreviewError('No repository URL available for this service');
        }
        return;
      }

      // ── PRE-FLIGHT: check tool enablement & authentication BEFORE any git call ──
      // This must run before the try{} block so no spinner/loading state is set
      // and no branch/project API calls are made against a disabled/unlinked provider.
      // Not applicable to a vfs-backed project — there is no git tool to check.
      if (!serviceToPreview.vfsBacked) {
        const gitStatus = await checkGitToolStatus(repoUrl);
        if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
          const blockedError = !gitStatus.toolEnabled
            ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
            : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
          // eslint-disable-next-line no-console
          console.warn(`🚫 [Preview] Pre-flight blocked (${blockedError}) for`, serviceToPreview.name);
          loadingPreviewRef.current.delete(serviceToPreview.name);
          if (previewTabs.length > 0) {
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceToPreview.name
                ? { ...t, loading: false, pullingCode: false, error: blockedError }
                : t
            ));
          } else {
            setPreviewLoading(false);
            setPreviewError(blockedError);
          }
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────────────

      // ── PRE-FLIGHT: skip the build entirely for a service with no real
      // content yet (e.g. just created via "+ Create new service" — a bare
      // `.gitkeep` folder, or a fresh project's default service before the
      // agent's first write). Checking this BEFORE starting a render avoids
      // both a doomed build attempt (the preview-runner would fail it with
      // a "driver could not detect a supported project" error) and the
      // noisy raw build-log panel that would otherwise stream while that
      // doomed attempt runs — neither is useful or non-alarming for a
      // service nobody has written code into yet. See vfsClient.isServiceEmpty.
      //
      // NEW CASE (added alongside the above, not replacing it): the original
      // condition only ran this check when `serviceToPreview.vfsBacked` was
      // explicitly true on the service object itself. Live-confirmed gap: a
      // service entry that's real (lives under a vfs-backed project) but
      // whose `configuration.services` record hadn't been flagged
      // `vfsBacked: true` (e.g. reopening an existing project where no
      // chat/agent activity has touched this service yet, so nothing ever
      // set that flag) skipped this whole guard and fell straight into a
      // doomed real render attempt — the exact "MultiDriver: no registered
      // driver recognizes the project at .../{project}-.-{uuid}" error,
      // shown to the user instead of a clean empty-state. Broaden the
      // trigger to ALSO catch this case: run the same pre-flight whenever
      // the overall PROJECT is vfs-backed, even if this particular service
      // record wasn't individually flagged.
      if (serviceToPreview.vfsBacked || vfsClient.isVfsBackedProject(selectedProject?.id)) {
        const empty = await vfsClient.isServiceEmpty(selectedProject?.id, serviceToPreview.name);
        if (isCancelled) return;
        if (empty) {
          loadingPreviewRef.current.delete(serviceToPreview.name);
          // Task #217: when this project has NEVER had a single chat
          // conversation (projectHasNoConversations === true, resolved by
          // the effect near this component's other state — strictly `true`,
          // not `null`/loading, so a slow check never jumps to Dashboard
          // prematurely for a project that in fact does have history), land
          // on the Dashboard tab instead of the "Empty project" preview
          // message. A project WITH conversation history (even finished)
          // keeps the exact existing behavior below, unchanged.
          if (projectHasNoConversations === true) {
            setActiveTab('dashboard');
            return;
          }
          if (previewTabs.length > 0) {
            const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
            if (tabIndex >= 0) {
              const updatedTabs = [...previewTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                loading: false,
                error: null,
                isEmptyService: true,
              };
              setPreviewTabs(updatedTabs);
            }
          } else {
            setPreviewLoading(false);
            setPreviewError(null);
            setPreviewIsEmptyService(true);
          }
          return;
        }
      }
      // Any path past this point is a real (non-empty) service load — clear
      // a stale empty-service flag from a previously selected empty service.
      setPreviewIsEmptyService(false);
      // ────────────────────────────────────────────────────────────────────────────

      try {
        // Update loading state
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            const updatedTabs = [...previewTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: true,
              error: null
            };
            setPreviewTabs(updatedTabs);
          }
        } else {
          setPreviewLoading(true);
          setPreviewError(null);
          // Only predict a sessionId for non-vfs (legacy per-stack container-live)
          // services — see the identical guard/comment above (~line 3630) for why
          // this prediction is meaningless (and actively harmful, a 404-forever
          // polling target) for vfs-backed projects on the universal runner.
          setPreviewSessionId(buildSessionId(
            selectedProject?.id,
            serviceToPreview.name,
            getOrCreateUserId(selectedProject?.id, serviceToPreview.name)
          ));
        }
        
        // Check if effect was cancelled (cleanup ran)
        if (isCancelled) return;

        // eslint-disable-next-line no-console
        devLog('🎨 Loading preview for service:', serviceToPreview.name);
        // eslint-disable-next-line no-console
        devLog('📋 Service details:', {
          type: serviceToPreview.type,
          hasRepo: !!repoUrl
        });

        // Validate we have valid IDs before querying IndexedDB
        if (!selectedProject?.id || !serviceToPreview.name) {
          console.error('❌ Cannot load preview: missing project ID or service name', {
            projectId: selectedProject?.id,
            serviceName: serviceToPreview.name,
            selectedProjectFull: selectedProject
          });
          return;
        }

        // task #112: vfs-backed projects are ALWAYS rendered via the
        // universal preview-runner pulling directly from vfs-service
        // server-side (see buildVfsSourcePayload below) — vfs-service is
        // the sole source of truth for these projects. IndexedDB (reads,
        // writes, and the local fetchServiceFiles-then-cache dance this
        // block used to do) is never touched anywhere in this path anymore.
        // That old dance was also the actual root cause of a real "stuck
        // loading" bug: it fetched every path vfs-service returned for the
        // service one file at a time — including vfs-service's own internal
        // `.git`/`.vfs-meta` bookkeeping, which was never meant to be
        // treated as project source — sometimes hundreds of extra files,
        // all wasted since the render request below never needed local file
        // content in the first place. Git-backed projects (still real, see
        // task #101 — mia-flow-be still serves them) keep the original
        // IndexedDB-based flow below unchanged.
        const isVfsRender = serviceToPreview.vfsBacked === true;

        let frontendSourceCode = {};
        let mergedFrontendFiles = {};
        // Hoisted out of the git-only branch below (real bug found tonight:
        // shared request-building code further down references this
        // regardless of branch, but it used to be `const`-declared inside
        // the git-only `else` block, so it was undefined/ReferenceError on
        // every vfs-backed render — "shouldFetchFromGit is not defined").
        // Always false for a vfs-backed render — fetching from git is
        // never applicable to a vfs-only project.
        let shouldFetchFromGit = false;

        if (isVfsRender) {
          // eslint-disable-next-line no-console
          devLog('📦 [Preview] vfs-backed project — runner pulls directly from vfs-service, no local fetch/cache needed');

          setPreviewTabs(prevTabs => prevTabs.map(t =>
            t.serviceName === serviceToPreview.name
              ? {...t, pullingCode: false, renderPhase: 'Preparing', renderProgress: 0}
              : t
          ));

          // Check if there's a pending initial prompt from new project creation.
          // This never actually depended on file content — just on "the
          // project is ready to receive its first message" — which for a
          // vfs-backed project is immediately true now that there's no local
          // fetch to wait on.
          // task #107: guarded by initialPromptConsumedRef — see its declaration.
          const pendingPrompt = initialPromptConsumedRef.current ? null : sessionStorage.getItem('mia_pending_initial_prompt');

          if (pendingPrompt) {
            initialPromptConsumedRef.current = true;
            // eslint-disable-next-line no-console
            devLog('🚀 [Preview] vfs-backed project ready, triggering initial prompt immediately:', pendingPrompt.substring(0, 100));

            sessionStorage.removeItem('mia_pending_initial_prompt');
            sessionStorage.removeItem('mia_pending_initial_mode');
            sessionStorage.removeItem('mia_pending_initial_files');

            const waitForContextReady = async () => {
              if (selectedResource && selectedResource.data && selectedResource.data.name && !receivingContextFromParentRef.current) {
                // eslint-disable-next-line no-console
                devLog('✅ [Preview] Service selected and context ready:', selectedResource.data.name);

                const wasReceiving = receivingContextFromParentRef.current;
                receivingContextFromParentRef.current = false;

                const contextSent = await sendContextToParent();

                receivingContextFromParentRef.current = wasReceiving;

                if (contextSent) {
                  // eslint-disable-next-line no-console
                  devLog('✅ [Preview] Context sent, dispatching auto-send event');

                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('populate-chat-input', {
                      detail: {
                        message: pendingPrompt,
                        autoSend: true,
                        setAskAboutComponents: true
                      }
                    }));
                    // eslint-disable-next-line no-console
                    devLog('✅ [Preview] Dispatched populate-chat-input event with autoSend');
                  }, 200);
                } else {
                  // eslint-disable-next-line no-console
                  console.warn('⚠️ [Preview] Context send failed, retrying...');
                  setTimeout(waitForContextReady, 150);
                }
              } else {
                setTimeout(waitForContextReady, 150);
              }
            };

            waitForContextReady();
          }

          setForceGitFetch(false);
        } else {
          // Check if we have IndexedDB files for this service
          const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
          const localFiles = await indexedDbFileStorage.getAllFiles(selectedProject.id, serviceToPreview.name);
          const hasLocalFiles = localFiles.length > 0;

          // Check if this is a new project that needs force git pull
          const forceGitPull = sessionStorage.getItem('mia_force_git_pull_on_load') === 'true';
          if (forceGitPull) {
            devLog('🆕 [Preview] New project detected - forcing git pull');
            sessionStorage.removeItem('mia_force_git_pull_on_load');
          }

          // eslint-disable-next-line no-console
          devLog('📊 IndexedDB check:', {
            hasLocalFiles,
            fileCount: localFiles.length,
            projectId: selectedProject?.id,
            serviceName: serviceToPreview.name,
            forceGitPull
          });

          // Fetch from git if:
          // 1. No IndexedDB files exist (first time), OR
          // 2. Force git pull flag is set (new project creation)
          shouldFetchFromGit = !hasLocalFiles || forceGitPull;

          // eslint-disable-next-line no-console
          devLog('🔍 Decision:', shouldFetchFromGit ? 'Fetch from Git (first time)' : 'Use IndexedDB (source of truth)');

          if (shouldFetchFromGit) {
            // Parse project ID from repository URL
            const projectId = extractProjectIdFromUrl(repoUrl);
            if (!projectId) {
              throw new Error('Could not extract project ID from repository URL');
            }

            // eslint-disable-next-line no-console
            devLog('📦 Fetching repository files from git for project:', projectId);
            // eslint-disable-next-line no-console
            devLog('⏳ [Preview] Phase: FETCHING - Loading source code from repository...');

            // Show toast notification that git pulling is starting
            window.dispatchEvent(new CustomEvent('show-toast', {
              detail: {
                message: `📦 Pulling code from Git repository for ${serviceToPreview.name}...`,
                type: 'info'
              }
            }));

            // Update preview tab to show "Pulling code from repository" message
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceToPreview.name
                ? {...t, pullingCode: true, renderPhase: 'Pulling code from Git', renderProgress: 0}
                : t
            ));

            // Fetch all files from the repository with progress tracking
            frontendSourceCode = await fetchAllRepositoryFiles(projectId, repoUrl, (progress) => {
              // Update preview tab with progress
              setPreviewTabs(prevTabs => prevTabs.map(t =>
                t.serviceName === serviceToPreview.name
                  ? {
                      ...t,
                      renderPhase: `Pulling code from Git `,
                      renderProgress: progress.percentage,
                      renderLogs: [
                        ...(t.renderLogs || []).slice(-10), // Keep last 10 logs
                        `📥 ${progress.current}/${progress.total}: ${progress.currentFile}`
                      ]
                    }
                  : t
              ));
            });

            if (!frontendSourceCode || Object.keys(frontendSourceCode).length === 0) {
              throw new Error('No source files found in repository');
            }

            // eslint-disable-next-line no-console
            devLog(`📦 Collected ${Object.keys(frontendSourceCode).length} files from git`);

            // eslint-disable-next-line no-console
            devLog('✅ [Preview] Phase: ANALYZING - Source code loaded, detecting framework...');

            // Update preview tab to show analysis phase
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceToPreview.name
                ? {...t, pullingCode: false, renderPhase: 'Analyzing and preparing'}
                : t
            ));

            // Store files in IndexedDB for future use (so user can see code
            // in Code tab) — git-backed projects only, see comment above.
            // eslint-disable-next-line no-console
            devLog('💾 [Preview] Saving files to IndexedDB for Code tab access...');

            let savedCount = 0;
            for (const [filePath, content] of Object.entries(frontendSourceCode)) {
              try {
                await indexedDbFileStorage.saveFile(
                  selectedProject.id,
                  serviceToPreview.name,
                  filePath,
                  content,
                  {
                    originalContent: content,
                    branch: DEFAULT_MICROSERVICE_BRANCH,
                    fileName: filePath.split('/').pop(),
                    fromGit: true,
                    fromVfs: false
                  }
                );
                savedCount++;
                // Log progress every 10 files
                if (savedCount % 10 === 0) {
                  // eslint-disable-next-line no-console
                  devLog(`💾 [Preview] Saved ${savedCount}/${Object.keys(frontendSourceCode).length} files...`);
                }
              } catch (saveErr) {
                // eslint-disable-next-line no-console
                console.warn(`⚠️ [Preview] Failed to save file ${filePath}:`, saveErr);
              }
            }
            // eslint-disable-next-line no-console
            devLog(`✅ [Preview] Successfully saved ${savedCount} files to IndexedDB`);

            // Mark that files are completely saved to IndexedDB
            sessionStorage.setItem('mia_files_saved_to_indexeddb', 'true');

            // Trigger repository tree refresh to visualize the files in Code tab
            // Dispatch event to update the file tree
            window.dispatchEvent(new CustomEvent('repositoryFilesUpdated', {
              detail: {
                projectId: selectedProject?.id,
                serviceName: serviceToPreview.name,
                fileCount: savedCount
              }
            }));
            // eslint-disable-next-line no-console
            devLog('🌲 [Preview] Dispatched event to refresh repository tree view');

            // Check if there's a pending initial prompt from new project creation
            // Dispatch it NOW after files are pulled (before preview loads)
            // task #107: guarded by initialPromptConsumedRef — see its declaration.
            const pendingPrompt = initialPromptConsumedRef.current ? null : sessionStorage.getItem('mia_pending_initial_prompt');

            if (pendingPrompt) {
              initialPromptConsumedRef.current = true;
              // eslint-disable-next-line no-console
              devLog('🚀 [Preview] Files pulled, triggering initial prompt immediately:', pendingPrompt.substring(0, 100));

              // Clear the pending flags
              sessionStorage.removeItem('mia_pending_initial_prompt');
              sessionStorage.removeItem('mia_pending_initial_mode');
              sessionStorage.removeItem('mia_pending_initial_files');

              // Wait for context to be ready, then dispatch
              const waitForContextReady = async () => {
                // Check if we have selectedResource and we're not in the middle of receiving from parent
                if (selectedResource && selectedResource.data && selectedResource.data.name && !receivingContextFromParentRef.current) {
                  // eslint-disable-next-line no-console
                  devLog('✅ [Preview] Service selected and context ready:', selectedResource.data.name);

                  // FORCE context send - critical for home-created projects
                  const wasReceiving = receivingContextFromParentRef.current;
                  receivingContextFromParentRef.current = false;

                  // Directly send context to parent now
                  const contextSent = await sendContextToParent();

                  // Restore flag
                  receivingContextFromParentRef.current = wasReceiving;

                  if (contextSent) {
                    // eslint-disable-next-line no-console
                    devLog('✅ [Preview] Context sent, dispatching auto-send event');

                    // Wait briefly for React state updates
                    setTimeout(() => {
                      // Dispatch custom event to populate chat input and auto-send
                      window.dispatchEvent(new CustomEvent('populate-chat-input', {
                        detail: {
                          message: pendingPrompt,
                          autoSend: true, // Auto-send for home-created projects
                          setAskAboutComponents: true
                        }
                      }));
                      // eslint-disable-next-line no-console
                      devLog('✅ [Preview] Dispatched populate-chat-input event with autoSend after file pull');

                      // Clear the flag
                      sessionStorage.removeItem('mia_files_saved_to_indexeddb');
                    }, 200);
                  } else {
                    // eslint-disable-next-line no-console
                    console.warn('⚠️ [Preview] Context send failed, retrying...');
                    setTimeout(waitForContextReady, 150);
                  }
                } else {
                  // Not ready yet, check again
                  setTimeout(waitForContextReady, 150);
                }
              };

              // Start checking
              waitForContextReady();
            }

            // Reset the force fetch flag
            setForceGitFetch(false);
          } else {
            // eslint-disable-next-line no-console
            devLog('📂 Using files from localStorage (skip git fetch)');
          }

          // Merge with localStorage modifications (or use only localStorage if no git fetch)
          mergedFrontendFiles = await indexedDbFileStorage.getFilesForAIContext(
            selectedProject?.id,
            serviceToPreview.name,
            frontendSourceCode
          );

          if (Object.keys(mergedFrontendFiles).length === 0) {
            throw new Error('No files available for preview. Please refresh to fetch from git.');
          }
        }

        const gitFileCount = Object.keys(frontendSourceCode).length;
        const totalFileCount = Object.keys(mergedFrontendFiles).length;

        if (isVfsRender) {
          // eslint-disable-next-line no-console
          devLog('📂 [Preview] vfs-backed project — runner pulls files directly from vfs-service, no local file count to report');
        } else if (gitFileCount > 0) {
          const modifiedCount = totalFileCount - gitFileCount;
          if (modifiedCount > 0) {
            // eslint-disable-next-line no-console
            devLog(`📝 Merged ${modifiedCount} modified frontend files from localStorage`);
          }
        } else {
          // eslint-disable-next-line no-console
          devLog(`📂 Using ${totalFileCount} frontend files from localStorage`);
        }

        // Check if the service being previewed is a frontend or backend service
        // CRITICAL: Pass source code files for accurate detection
        const serviceDetection = detectServiceType(serviceToPreview);
        
        // Determine which container to use based on service type
        let isBackendPreview = false;
        
        // Get routing info using centralized utility
        const routingInfo = getContainerRoutingInfo(serviceToPreview, mergedFrontendFiles, {});
        
        if (!routingInfo) {
          // eslint-disable-next-line no-console
          devLog('⚠️ Service is not detected as frontend or backend, skipping preview');
          setPreviewUrl(null);
          return;
        }
        
        const { isBackend, isFrontend, containerPort, containerName } = routingInfo;
        isBackendPreview = isBackend;
        
        // eslint-disable-next-line no-console
        devLog(`🎯 Routing ${isBackend ? 'backend' : 'frontend'} preview via ${containerName} on port ${containerPort}`);
        
        // Update tab with correct framework immediately after detection
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                containerPort: containerPort
              };
              return updatedTabs;
            });
          }
        }
        
        // Store container port for auto-sync
        setPreviewContainerPort(containerPort);

        // Get or create userId from localStorage (persists across page reloads)
        // This ensures consistent session IDs and eliminates session mismatch warnings
        const userId = getOrCreateUserId(selectedProject?.id, serviceToPreview.name);
        // eslint-disable-next-line no-console
        devLog(`🔑 Using User ID: ${userId}`);
        
        // Check if we have an existing session in localStorage
        const existingSession = getSessionFromStorage(selectedProject?.id, serviceToPreview.name, userId);
        
        if (existingSession && existingSession.previewUrl) {
          // eslint-disable-next-line no-console
          devLog('✨ Found existing session in localStorage, reusing it!');
          // eslint-disable-next-line no-console
          devLog('📋 Session data:', existingSession);
          
          // Validate the stored session against the shared service state (the runner has no /api/port-mappings:
          // that check always 404ed and re-rendered).
          try {
            const sessionExists = Boolean(runnerEntry) && isPreviewReady(runnerEntry);
            devLog('🔍 Session check:', { lookingFor: existingSession.sessionId, found: sessionExists });

            if (sessionExists) {
              // eslint-disable-next-line no-console
              devLog('✅ Session is still active, reusing it');
              
              // Update preview tabs with existing session data
              if (previewTabs.length > 0) {
                const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
                if (tabIndex >= 0) {
                  setPreviewTabs(currentTabs => {
                    const updatedTabs = [...currentTabs];
                    updatedTabs[tabIndex] = {
                      ...updatedTabs[tabIndex],
                      url: existingSession.previewUrl,
                      sessionId: existingSession.sessionId,
                      userId: userId,
                      tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                      projectId: selectedProject?.id,
                      containerPort: existingSession.containerPort || containerPort,
                      language: existingSession.language,
                      loading: false,
                      iframeLoaded: true, // Session already exists, no need to show loading
                      error: null,
                      iframeKey: Date.now() // Force iframe refresh when reusing session
                    };
                    return updatedTabs;
                  });
                  // eslint-disable-next-line no-console
                  devLog('✅ Updated preview tab with existing session (iframe will refresh)');
                  
                  // Update mia_selectedSession to track this as the active session
                  setSelectedSession({
                    sessionId: existingSession.sessionId,
                    previewUrl: existingSession.previewUrl,
                    containerPort: existingSession.containerPort || containerPort,
                    language: existingSession.language,
                    tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                    projectId: selectedProject?.id,
                    serviceName: serviceToPreview.name,
                    userId: userId,
                    lastAccessed: new Date().toISOString()
                  });
                }
              } else {
                // Fallback to old single preview mode
                setPreviewUrl(existingSession.previewUrl);
                setPreviewSessionId(existingSession.sessionId);
                
                // Update mia_selectedSession
                setSelectedSession({
                  sessionId: existingSession.sessionId,
                  previewUrl: existingSession.previewUrl,
                  containerPort: existingSession.containerPort || containerPort,
                  language: existingSession.language,
                  tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                  projectId: selectedProject?.id,
                  serviceName: serviceToPreview.name,
                  userId: userId,
                  lastAccessed: new Date().toISOString()
                });
              }
              
              // Don't auto-switch tabs - let user choose when to view preview
              // Only switch if we're loading a new preview (not reusing a session)
              // This allows users to click services and see their code/config first
              // if (activeTab !== 'preview') {
              //   // eslint-disable-next-line no-console
              //   devLog('✅ Switching to preview tab to display reused session');
              //   setActiveTab('preview');
              // }
              
              setPreviewLoading(false);
              
              // Mark as successfully loaded
              const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|normal`;
              lastLoadedServiceRef.current = serviceKey;
              
              // Clear from loading ref since we're reusing the session
              loadingPreviewRef.current.delete(serviceToPreview.name);
              
              // Exit early - session is valid
              return;
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ The stored session is not running - will re-render');
              
              // Clear invalid session from storage
              const storageKey = `preview-session-${selectedProject?.id}-${serviceToPreview.name}-${userId}`;
              localStorage.removeItem(storageKey);
              
              // Continue to render a new session below
            }
          } catch (fetchError) {
            // eslint-disable-next-line no-console
            console.warn('⚠️ Session validation failed:', fetchError.message, '- will re-render');
            
            // Clear invalid session from storage
            const storageKey = `preview-session-${selectedProject?.id}-${serviceToPreview.name}-${userId}`;
            localStorage.removeItem(storageKey);
          }
        }
        
        // eslint-disable-next-line no-console
        devLog('🆕 No existing session found, creating new one...');

        // Find backend service in the configuration (only relevant for frontend previews)
        let backendService = null;
        if (!isBackendPreview && configuration && configuration.services) {
          // Convert services object to array of service objects
          const servicesArray = Object.values(configuration.services);
          backendService = servicesArray.find(svc => {
            const detection = detectServiceType(svc);
            return detection.isBackend;
          });
          
          if (backendService) {
            // eslint-disable-next-line no-console
            devLog('🔍 Found backend service:', backendService.name);
            // Store backend service for incremental updates
            setActiveBackendService(backendService);
          } else {
            setActiveBackendService(null);
          }
        } else if (isBackendPreview) {
          // If previewing backend, store it as active
          setActiveBackendService(serviceToPreview);
        }

        // Prepare parallel requests
        const requests = [];
        
        // Check cancellation before expensive operations
        if (isCancelled) {
          // eslint-disable-next-line no-console
          devLog('⏹️ Preview cancelled by user');
          return;
        }
        
        // Send service to appropriate container
        // eslint-disable-next-line no-console
        devLog('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // eslint-disable-next-line no-console
        devLog('🚀 STARTING CONTAINER API CALL');
        // eslint-disable-next-line no-console
        devLog(isVfsRender
          ? `📦 Telling ${containerName} to pull from vfs-service directly (no local files sent)`
          : `📦 Sending ${totalFileCount} files to ${containerName}`);
        // eslint-disable-next-line no-console
        devLog(`🔑 Service name (sessionId): ${serviceToPreview.name}`);
        // eslint-disable-next-line no-console
        devLog(`📂 Project: ${selectedProject?.name} (${selectedProject?.id})`);
        // eslint-disable-next-line no-console
        devLog(`🎯 Container port: ${containerPort}`);
        // eslint-disable-next-line no-console
                // eslint-disable-next-line no-console
        devLog(`👤 User ID: ${userId}`);
        
        const containerApiUrl = getContainerApiUrl(containerPort, '/api/render');
        // eslint-disable-next-line no-console
        devLog(`🌐 Calling container API:`, containerApiUrl);
        // eslint-disable-next-line no-console
        devLog('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        // Start the async render job
        let renderResponse;
        try {
          // eslint-disable-next-line no-console
          devLog('📡 Initiating fetch request to container...');
          let sourceCodePayload;
          if (serviceToPreview.vfsBacked) {
            // vfs-backed service: let the container pull its own files directly
            // from vfs-service instead of shipping them gathered from IndexedDB.
            sourceCodePayload = vfsClient.buildVfsSourcePayload(selectedProject?.id, serviceToPreview.name);
          } else {
            // Ensure all file contents are strings (convert objects to JSON strings)
            // This prevents "Received an instance of Object" errors when writing files
            const sanitizedFiles = {};
            for (const [filePath, content] of Object.entries(mergedFrontendFiles)) {
              if (typeof content === 'object' && content !== null) {
                // Convert objects to JSON strings (e.g., parsed package.json)
                sanitizedFiles[filePath] = JSON.stringify(content, null, 2);
              } else {
                sanitizedFiles[filePath] = content;
              }
            }
            sourceCodePayload = { sourceCode: sanitizedFiles };
          }

          renderResponse = await getAuthorizedFetch()(containerApiUrl, {
            method: 'POST',
            headers: getBffHeaders({
              'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
              tenantId: selectedTenant?.tenantId || selectedTenant?.id,
              serviceName: serviceToPreview.name,
              ...sourceCodePayload,
              projectId: selectedProject?.id,
              revisionId: selectedRevision?.id,
              userId: userId, // Stable userId from localStorage ensures consistent sessions
            }),
          });
          // eslint-disable-next-line no-console
          devLog('✅ Fetch request completed, status:', renderResponse.status);
        } catch (fetchError) {
          // eslint-disable-next-line no-console
          console.error('❌ FETCH REQUEST FAILED:', fetchError);
          // eslint-disable-next-line no-console
          console.error('   Error name:', fetchError.name);
          // eslint-disable-next-line no-console
          console.error('   Error message:', fetchError.message);
          // eslint-disable-next-line no-console
          console.error('   Error stack:', fetchError.stack);
          throw fetchError;
        }
        
        if (!renderResponse.ok) {
          const errorText = await renderResponse.text();
          // eslint-disable-next-line no-console
          console.error('❌ Container API returned error:', renderResponse.status, errorText);
          throw new Error(`Failed to start render: ${renderResponse.statusText}`);
        }
        
        const renderData = await renderResponse.json();
        // eslint-disable-next-line no-console
        devLog('📦 Render data received:', renderData);
        if (!renderData.success || !renderData.sessionId) {
          throw new Error(renderData.message || 'Failed to start render job');
        }
        
        // Store userId for this preview session (will be used in tab object)
        // eslint-disable-next-line no-console
        devLog(`🔑 Session User ID: ${userId} for job ${renderData.sessionId}`);
        
        // eslint-disable-next-line no-console
        devLog(`🎬 Render job started with ID: ${renderData.sessionId}`);
        // eslint-disable-next-line no-console
        devLog('⏳ [Preview] Phase: RENDERING - Container is building your application...');
        
        // Get base URL for polling
        const containerBaseUrl = containerApiUrl.replace('/api/render', '');
        
        // Poll for job completion with progress updates
        const frontendJobPromise = pollRenderStatusShared(
          serviceToPreview.name,
          containerBaseUrl,
          renderData.sessionId,
          (phase, progress, logs) => {
            // Clear build error modal when a new build starts (after a previous error)
            // This happens when user fixes code and saves - build restarts successfully
            if (buildErrorModal.isOpen && 
                buildErrorModal.serviceName === serviceToPreview.name &&
                (phase === 'initializing' || phase === 'copying' || phase === 'installing' || phase === 'starting')) {
              // eslint-disable-next-line no-console
              devLog(`✅ [Error Recovery] Clearing error modal for ${serviceToPreview.name} - new build phase: ${phase}`);
              setBuildErrorModal({ isOpen: false, error: null, serviceName: null });
            }
            
            // Update tab with progress and logs
            // Use functional setState to get current tabs and avoid stale closures
            setPreviewTabs(currentTabs => {
              if (currentTabs.length === 0) {
                return currentTabs;
              }
              
              const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              // eslint-disable-next-line no-console
              devLog(`📊 Progress update: ${progress}% - Phase: ${phase} - Tab found at index: ${tabIndex}`);
              // eslint-disable-next-line no-console
              devLog(`📋 Logs received:`, logs?.length || 0, 'entries');
              
              if (tabIndex >= 0) {
                const updatedTabs = [...currentTabs];
                // Replace logs entirely (API returns complete log history, not incremental)
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  renderProgress: progress,
                  renderPhase: phase,
                  renderLogs: logs || [],
                };
                return updatedTabs;
              }
              
              return currentTabs;
            });
          }
        );
        
        requests.push(frontendJobPromise);

        // 2. If this is a frontend preview and backend service exists, fetch and send to appropriate backend container
        if (!isBackendPreview && backendService) {
          // eslint-disable-next-line no-console
          devLog('🔧 Found backend service:', backendService.name, '- loading in parallel');
          
          const backendRepoUrl = backendService.repoUrl || backendService.sshUrl || backendService.repositoryUrl;
          
          if (backendRepoUrl && shouldFetchFromGit) {
            const backendProjectId = extractProjectIdFromUrl(backendRepoUrl);
            
            if (backendProjectId) {
              // eslint-disable-next-line no-console
              devLog('📦 Fetching backend repository files from git');
              
              const backendSourceCode = await fetchAllRepositoryFiles(backendProjectId, backendRepoUrl);
              const mergedBackendFiles = await indexedDbFileStorage.getFilesForAIContext(
                selectedProject?.id,
                backendService.name,
                backendSourceCode
              );
              
              // Get routing info for backend service
              const backendRoutingInfo = getContainerRoutingInfo(backendService, mergedBackendFiles, {});
              const backendContainerPort = backendRoutingInfo?.containerPort;
              
              // eslint-disable-next-line no-console
              devLog(`📦 Sending ${Object.keys(mergedBackendFiles).length} backend files to ${backendRoutingInfo?.containerName || 'universal'} (port ${backendContainerPort})`);
              
              const backendContainerApiUrl = getContainerApiUrl(backendContainerPort, '/api/render');
              
              // Use the same userId from this preview session for backend service isolation
              // This ensures frontend and backend services are grouped in the same session
              
              requests.push(
                getAuthorizedFetch()(backendContainerApiUrl, {
                  method: 'POST',
                  headers: getBffHeaders({
                    'Content-Type': 'application/json',
                  }),
                  body: JSON.stringify({
                    tenantId: selectedTenant?.tenantId || selectedTenant?.id,
                    serviceName: backendService.name,
                    sourceCode: mergedBackendFiles,
                    projectId: selectedProject?.id,
                    revisionId: selectedRevision?.id,
                    userId: userId,
                  }),
                })
              );
            }
          } else if (backendRepoUrl) {
            // Use IndexedDB files for backend
            const mergedBackendFiles = await indexedDbFileStorage.getAllFilesForPreview(
              selectedProject?.id,
              backendService.name,
              {}
            );
            
            if (Object.keys(mergedBackendFiles).length > 0) {
              // Get routing info for backend service
              const backendRoutingInfo = getContainerRoutingInfo(backendService, mergedBackendFiles, {});
              const backendContainerPort = backendRoutingInfo?.containerPort;
              
              const backendContainerApiUrl = getContainerApiUrl(backendContainerPort, '/api/render');
              
              // Use the same userId from this preview session for backend service isolation
              // This ensures frontend and backend services are grouped in the same session
              
              requests.push(
                getAuthorizedFetch()(backendContainerApiUrl, {
                  method: 'POST',
                  headers: getBffHeaders({
                    'Content-Type': 'application/json',
                  }),
                  body: JSON.stringify({
                    tenantId: selectedTenant?.tenantId || selectedTenant?.id,
                    serviceName: backendService.name,
                    sourceCode: mergedBackendFiles,
                    projectId: selectedProject?.id,
                    revisionId: selectedRevision?.id,
                    userId: userId,
                  }),
                })
              );
            }
          }
        }

        // Execute all requests in parallel with error handling
        const results = await Promise.all(requests.map(req => 
          req.catch(err => ({
            error: err,
            status: 'failed',
            isNetworkError: err.name === 'TypeError' && err.message.includes('fetch')
          }))
        ));
        
        // Check if cancelled after async operation
        if (isCancelled) return;
        
        // Check frontend result (first request - now a job result)
        const frontendResult = results[0];
        
        if (frontendResult.error || frontendResult.status === 'failed') {
          // Handle network/connection errors gracefully
          if (frontendResult.isNetworkError) {
            // The single universal preview-runner (container-live/preview-runner,
            // a Rust binary — `cargo run --bin preview-runner-universal`, not an
            // npm-based per-stack service) is the only container these messages
            // should ever point at now.
            const errorMessage = `Cannot connect to the preview runner on port ${containerPort}. Please start it:\n\ncd container-live/preview-runner\ncargo run --bin preview-runner-universal`;

            // eslint-disable-next-line no-console
            console.error('❌ Container not running:', errorMessage);

            // Update preview tabs if they exist
            if (previewTabs.length > 0) {
              const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              if (tabIndex >= 0) {
                setPreviewTabs(currentTabs => {
                  const updatedTabs = [...currentTabs];
                  updatedTabs[tabIndex] = {
                    ...updatedTabs[tabIndex],
                    loading: false,
                    error: 'Preview runner not running. Start it with: cd container-live/preview-runner && cargo run --bin preview-runner-universal'
                  };
                  return updatedTabs;
                });
              }
            } else {
              setPreviewError('Preview runner not running. Start it with: cd container-live/preview-runner && cargo run --bin preview-runner-universal');
            }
            setPreviewLoading(false);
            return; // Exit gracefully without throwing
          }
          
          // Other errors
          const originalError = frontendResult.error;
          const errorMsg = originalError?.message || originalError || 'Failed to load preview';
          const rethrown = new Error(errorMsg);
          // Preserve the "nothing to build yet" tag across this rewrap —
          // Promise.all's per-request .catch() above only kept the error
          // OBJECT (frontendResult.error), not this new Error we're about
          // to throw; without re-attaching it here, every caller downstream
          // would see a plain Error and fall back to the generic (alarming)
          // build-failure UI even for a genuinely empty, not-yet-coded
          // service. Fall back to re-matching the message directly in case
          // `originalError` isn't the tagged Error from pollRenderStatus
          // (e.g. it came from a different failure path).
          rethrown.isEmptyService = Boolean(originalError?.isEmptyService) || isEmptyServiceError(errorMsg);
          // Same for "could not reach the preview" (pollRenderStatus's PreviewStatusError).
          if (originalError?.name === 'PreviewStatusError') {
            rethrown.name = 'PreviewStatusError';
            rethrown.code = originalError.code;
          }
          throw rethrown;
        }
        
        // Frontend job completed successfully
        if (frontendResult.previewUrl) {
          // The runner's session id: `<project>-<service, "/" -> "-">` (previewSessionId.js).
          const expectedSessionId = buildSessionId(selectedProject?.id, serviceToPreview.name, userId);
          const returnedSessionId = frontendResult.sessionId || expectedSessionId;
          
          // CRITICAL: Validate that returned sessionId matches expected format
          if (returnedSessionId !== expectedSessionId) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️  SESSION ID FORMAT DIFFERENCE`);
            // eslint-disable-next-line no-console
            console.warn(`   Expected: ${expectedSessionId}`);
            // eslint-disable-next-line no-console
            console.warn(`   Got: ${returnedSessionId}`);
            // eslint-disable-next-line no-console
            console.warn(`   Using returned sessionId (HMR will still work)`);
          }
          
          // Use the sessionId returned by the backend
          const validSessionId = returnedSessionId;
          
          // Use the preview URL returned by the backend (already points to Swagger UI for backend services)
          const finalPreviewUrl = frontendResult.previewUrl;
          // The runner is the source of truth for what this project IS (it
          // reports `stackLabel` from its own driver detection) — show that.
          // For a vfs-backed service never fall back to the FE's own name-based
          // guess: it has no files to look at and used to say "Node.js" for Java.
          const displayedFramework = frontendResult.stackLabel || null;
          const displayedLanguage = frontendResult.stack || null;
          
          // eslint-disable-next-line no-console
          devLog('✅ Preview URL received:', finalPreviewUrl);
          // eslint-disable-next-line no-console
          devLog('🎉 [Preview] Phase: READY - Application is now available!');
          // eslint-disable-next-line no-console
          devLog('🔄 Updating tab state to loading=false for service:', serviceToPreview.name);
          if (isBackendPreview) {
            // eslint-disable-next-line no-console
            devLog('🎯 Backend service - Swagger UI at:', finalPreviewUrl);
          }
          
          // Update preview tabs if they exist
          // Use functional update to avoid stale closure issues
          setPreviewTabs(currentTabs => {
            if (currentTabs.length > 0) {
              const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              // eslint-disable-next-line no-console
              devLog('🔍 Looking for tab with serviceName:', serviceToPreview.name, 'Found at index:', tabIndex);
              // eslint-disable-next-line no-console
              devLog('📋 Current tabs:', currentTabs.map(t => ({name: t.serviceName, loading: t.loading, url: t.url})));
              // eslint-disable-next-line no-console
              devLog('📋 Total tabs:', currentTabs.length, 'activePreviewTab:', activePreviewTab);
              if (tabIndex >= 0) {
                const updatedTabs = [...currentTabs];
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  url: finalPreviewUrl,
                  sessionId: validSessionId,
                  userId: userId, // Store userId for this session
                  tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                  projectId: selectedProject?.id,
                  containerPort: containerPort,
                  language: displayedLanguage,
                  framework: displayedFramework, // Update framework for status bar display
                  loading: false,
                  error: null,
                  isEmptyService: false,
                  // Clear render progress but keep logs visible until iframe loads successfully
                  renderProgress: undefined,
                  renderPhase: undefined
                  // Note: renderLogs intentionally NOT cleared here - will be cleared by handleIframeLoad on successful render
                };
                // eslint-disable-next-line no-console
                devLog('✅ Updated preview tab:', serviceToPreview.name, 'URL:', finalPreviewUrl, 'loading: false');
                
                return updatedTabs;
              } else {
                // eslint-disable-next-line no-console
                console.error('❌ Could not find tab for service:', serviceToPreview.name);
                return currentTabs;
              }
            } else {
              // eslint-disable-next-line no-console
              devLog('⚠️ No preview tabs exist yet, falling back to single preview mode');
              return currentTabs;
            }
          });
          
          // Save session to localStorage for instant reload on next visit
          saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
            sessionId: validSessionId,
            previewUrl: finalPreviewUrl,
            containerPort: containerPort,
            language: displayedLanguage,
            framework: displayedFramework,
            tenantId: selectedTenant?.id || selectedTenant?.tenantId,
            projectId: selectedProject?.id
          });
          
          // If there are NO tabs (old single-preview fallback), also set the fallback state
          if (previewTabs.length === 0) {
            // Fallback to old single preview mode
            setPreviewUrl(finalPreviewUrl);
            setPreviewSessionId(validSessionId);
            
            // Save session to localStorage
            saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
              sessionId: validSessionId,
              previewUrl: finalPreviewUrl,
              containerPort: containerPort,
              language: displayedLanguage,
              framework: displayedFramework,
              tenantId: selectedTenant?.id || selectedTenant?.tenantId,
              projectId: selectedProject?.id
            });
          }
          
          // eslint-disable-next-line no-console
          devLog('✅ Preview loaded:', finalPreviewUrl, 'sessionId:', validSessionId);
          
          // Mark this service as successfully loaded
          const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
          lastLoadedServiceRef.current = serviceKey;
          
          // Reset error count on success
          errorCountRef.current[serviceToPreview.name] = 0;
          previewResult(selectedProject?.id, true, { stack: displayedLanguage || displayedFramework });
          
          // Mark frontend files as sent
          if (shouldFetchFromGit) {
            await indexedDbFileStorage.markAllChangedAsSent(selectedProject?.id, serviceToPreview.name);
          }
          
          // Check backend result if it was sent
          if (results.length > 1 && backendService) {
            const backendResult = results[1];
            if (!backendResult.error && backendResult.status !== 'failed') {
              // eslint-disable-next-line no-console
              devLog('✅ Backend loaded:', backendResult.previewUrl || 'running');
              
              // Update backend tab if it exists
              if (backendResult.previewUrl && previewTabs.length > 0) {
                const backendTabIndex = previewTabs.findIndex(tab => tab.serviceName === backendService.name);
                if (backendTabIndex >= 0) {
                  // eslint-disable-next-line no-console
                  devLog('🔍 Updating backend tab:', backendService.name, 'with URL:', backendResult.previewUrl);
                  setPreviewTabs(currentTabs => {
                    const updatedTabs = [...currentTabs];
                    updatedTabs[backendTabIndex] = {
                      ...updatedTabs[backendTabIndex],
                      url: backendResult.previewUrl,
                      sessionId: backendResult.sessionId || backendService.name,
                      containerPort: 3014,
                      loading: false,
                      error: null
                    };
                    return updatedTabs;
                  });
                  // eslint-disable-next-line no-console
                  devLog('✅ Backend tab updated:', backendService.name, 'URL:', backendResult.previewUrl);
                }
              }
              
              if (shouldFetchFromGit) {
                await indexedDbFileStorage.markAllChangedAsSent(selectedProject?.id, backendService.name);
              }
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Backend loading failed, but frontend will continue');
            }
          }
          
          // eslint-disable-next-line no-console
          devLog('✅ Preview loaded:', frontendResult.previewUrl, 'sessionId:', frontendResult.sessionId);
          
          // Note: Auto-send for home-created projects now happens after files are pulled (before preview loads)
          // This makes the flow faster by not waiting for preview to build
        } else if (containerPort === 3022) {
          // Helm chart container — no iframe preview URL, HelmPreviewPanel uses WebSocket directly
          const helmSessionId = frontendResult.sessionId;
          // eslint-disable-next-line no-console
          devLog(`⎈ Helm validation complete — sessionId: ${helmSessionId}, status: ${frontendResult.status}`);

          setPreviewTabs(currentTabs => {
            const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
            if (tabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                // No url needed — HelmPreviewPanel renders directly
                url: `helm://${serviceToPreview.name}`,
                sessionId: helmSessionId,
                userId: userId,
                containerPort: 3022,
                language: 'helm',
                framework: 'Helm Chart',
                loading: false,
                error: null,
                isEmptyService: false,
                renderProgress: undefined,
                renderPhase: undefined,
                renderLogs: undefined,
              };
              return updatedTabs;
            }
            return currentTabs;
          });

          saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
            sessionId: helmSessionId,
            previewUrl: `helm://${serviceToPreview.name}`,
            containerPort: 3022,
            language: 'helm',
            framework: 'Helm Chart',
            tenantId: selectedTenant?.id || selectedTenant?.tenantId,
            projectId: selectedProject?.id,
          });

          const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
          lastLoadedServiceRef.current = serviceKey;
          errorCountRef.current[serviceToPreview.name] = 0;
        } else {
          // No preview URL received - clear loading state and show error
          // eslint-disable-next-line no-console
          console.error('❌ No previewUrl in render result:', frontendResult);
          
          // Ensure loading state is cleared
          setPreviewTabs(currentTabs => {
            const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
            if (tabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                loading: false,
                error: frontendResult.message || 'Failed to generate preview - no URL received'
              };
              return updatedTabs;
            }
            return currentTabs;
          });
          
          throw new Error(frontendResult.message || 'Failed to generate preview');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error loading preview:', error);

        // Increment error count for circuit breaker
        errorCountRef.current[serviceToPreview.name] = (errorCountRef.current[serviceToPreview.name] || 0) + 1;

        // Build a human-readable error message with extra context for known error types
        let errorMessage = error.message;
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          errorMessage = 'Cannot connect to preview services. Please ensure the preview service for the current technology is up and running!';
          // eslint-disable-next-line no-console
          console.warn('⚠️ Network error detected – preview service may not be running.');
        } else if (error.message.includes('BFF server')) {
          errorMessage = error.message + '\n\nStart BFF with: cd mia-flow-bff && npm start';
        }

        // A service with no files yet (freshly created/selected, nothing
        // written into it — e.g. `services/<name>` before the agent's first
        // `vfs_write_file`) is NOT a build failure: vfs-service correctly
        // has nothing to pull, and the preview-runner correctly reports
        // that as "no files". Route it to a friendly empty state instead of
        // the alarming Build Error modal/card — see isEmptyServiceError.
        const isEmptyService = Boolean(error.isEmptyService) || isEmptyServiceError(errorMessage);
        // The status poll could not reach the runner (redirected, refused, a page instead of JSON): not the code's
        // fault, so no build-error card and no "Fix with AI": "could not reach the preview" with Retry.
        const unreachable = error?.name === 'PreviewStatusError';
        if (unreachable) { errorMessage = `${PREVIEW_UNREACHABLE_PREFIX}${error.message}`; }
        if (!isEmptyService) { previewResult(selectedProject?.id, false); }

        if (!isEmptyService && !unreachable) {
          // Highlight the Ask AI button instead of popping the blocking modal —
          // the user still knows something went wrong, without the interruption.
          setPendingFixError({ error: errorMessage, serviceName: serviceToPreview.name });
        }

        // Also update the tab so it doesn't remain in a loading / empty state. From the CURRENT tabs: this effect's
        // `previewTabs` is the list from when it started, often before the tab existed (the tab is created by another,
        // async effect), so the old `previewTabs.length > 0` check skipped the update and left the tab on
        // "Rendering…" for good.
        if (previewTabsRef.current.some(tab => tab.serviceName === serviceToPreview.name)) {
          setPreviewTabs(currentTabs => currentTabs.map(tab => (tab.serviceName === serviceToPreview.name ? {
            ...tab,
            loading: false,
            error: isEmptyService ? null : errorMessage,
            isEmptyService,
          } : tab)));
        } else if (!isEmptyService) {
          // Fallback to old single preview mode
          setPreviewError(errorMessage);
        }
      } finally {
        setPreviewLoading(false);
        // Always remove from loading set when done (success or error)
        if (serviceToPreview?.name) {
          loadingPreviewRef.current.delete(serviceToPreview.name);
        }
      }
    };

    // Debounce the preview loading to prevent rapid consecutive calls
    if (previewLoadTimeoutRef.current) {
      clearTimeout(previewLoadTimeoutRef.current);
    }
    
    previewLoadTimeoutRef.current = setTimeout(() => {
      loadServicePreview();
    }, 1000); // 1000ms debounce
    
    // Cleanup function to prevent stale requests and clear debounce
    return () => {
      isCancelled = true;
      if (previewLoadTimeoutRef.current) {
        clearTimeout(previewLoadTimeoutRef.current);
      }
    };
    // Only depend on the core identifiers, not the full objects
    // This prevents cascading re-renders when multiple state updates occur.
    // `activeTab` IS a dependency: loadServicePreview bails out while the
    // Canvas isn't on the preview tab, and clicking a service from the Home
    // dashboard sets `selectedResource` BEFORE the tab switches to 'preview'.
    // Without it the effect never re-ran after the switch — the preview tab
    // was created with loading:true but /api/render was never called, leaving
    // "Rendering…" forever. Re-running is safe: the early return above happens
    // before lastLoadedServiceRef is claimed, and an already-loaded service is
    // skipped by that same ref / the tab-has-URL checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResource?.data?.name, selectedProject?.id, selectedRevision?.id, forceGitFetch, activeTab]);

  // The CRUD-collection preview service (crud-service-universal) has been
  // decommissioned. A 'collection' resource can still be selected/viewed
  // (see handleResourceSelect below) — it just has no live preview to load
  // anymore, so this degrades to a no-op instead of the removed sync-and-
  // open-Swagger-tab flow, rather than crashing on old data still shaped
  // like `{type: 'collection'}`.

  // Listen for file changes and send incremental updates to preview
  const fileChangeListenerRef = useRef(null);
  const previewUpdateDebounceTimer = useRef(null);
  const pendingPreviewUpdates = useRef(new Set());
  
  useEffect(() => {
    const handleFileChange = async (event) => {
      const {projectId: changedProjectId, serviceName: rawChangedServiceName, filePath} = event.detail || {};

      // BUG FIX (live-reported: chat-driven edits stopped reaching the
      // running preview at all — "⏭️ Skipping preview update - conditions
      // not met" on every single change): a project-ROOT-scoped vfs resync
      // (forceResyncServiceFromVfs pulling the whole project tree in one
      // pass — every service's files, .mia/, brain/, all under one call)
      // dispatches this event with `serviceName: '.'` for every file it
      // touches — that's the SYNC OPERATION's own scope, not which
      // specific service a given file actually belongs to. Comparing that
      // raw '.' against a real service tab's name (e.g. "services/app")
      // never matched, so the incremental-update check below always
      // treated this as "no active preview for this change" and silently
      // skipped it — even though the file plainly lives under
      // services/app/. Derive the real owning service from the path
      // itself whenever the reported scope is the project root.
      const changedServiceName = (rawChangedServiceName === '.' && filePath?.startsWith('services/'))
        ? filePath.split('/').slice(0, 2).join('/')
        : rawChangedServiceName;

      // eslint-disable-next-line no-console
      devLog('📝 File changed in Monaco:', {changedProjectId, changedServiceName, rawChangedServiceName, filePath});

      // 1. Update tree view - reload all files to reflect changes (debounced)
      if (changedProjectId === selectedProject?.id && changedServiceName === selectedResource?.data?.name) {
        // eslint-disable-next-line no-console
        devLog('🌲 Refreshing tree view with updated files');
        await loadSourceCode();
        
        // Update hasModifications after reloading files
        // Use getChangedFilesForPreview to get files that need to be pushed
        const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
          selectedProject.id,
          selectedResource.data.name
        );
        const hasChanges = changedFiles.length > 0;
        // eslint-disable-next-line no-console
        devLog('🔍 Checking modifications:', {
          changedFilesCount: changedFiles.length,
          changedFiles: changedFiles.map(f => ({path: f.filePath, state: f.state})),
          hasChanges
        });
        setHasModifications(hasChanges);
        // eslint-disable-next-line no-console
        devLog('✅ [DEBUG] Set hasModifications to:', hasChanges);
        
        // Note: Do NOT reload the file if it's currently open in editor - 
        // the user is actively editing it and reloading would be disruptive
      }
      
      // 2. Batch preview updates - add to pending queue and debounce
      // This prevents sending duplicate updates when AI applies multiple diffs rapidly
      pendingPreviewUpdates.current.add(JSON.stringify({projectId: changedProjectId, serviceName: changedServiceName, filePath}));
      
      // Clear existing timer and start new one
      if (previewUpdateDebounceTimer.current) {
        clearTimeout(previewUpdateDebounceTimer.current);
      }
      
      previewUpdateDebounceTimer.current = setTimeout(async () => {
        const updates = Array.from(pendingPreviewUpdates.current).map(s => JSON.parse(s));
        pendingPreviewUpdates.current.clear();
        
        if (updates.length === 0) return;
        
        // Process the first update (all updates should be for the same service)
        const firstUpdate = updates[0];
        const {projectId: updateProjectId, serviceName: updateServiceName} = firstUpdate;
        
        // eslint-disable-next-line no-console
        devLog(`📦 Processing batched preview update for ${updates.length} file change(s)`);
        
        // 3. Update preview with incremental changes
        // Check if preview is active (either single preview or tabs)
        const activePreviewTab = previewTabs.find(tab => tab.serviceName === updateServiceName);
        const hasActivePreview = !!previewSessionId || !!activePreviewTab;
      
        // eslint-disable-next-line no-console
        devLog('🔍 Checking preview update conditions:', {
          hasPreviewSessionId: !!previewSessionId,
          hasActivePreviewTab: !!activePreviewTab,
          hasActivePreview,
          hasSelectedResource: !!selectedResource,
          resourceType: selectedResource?.type,
          previewSessionId,
          activePreviewTab: activePreviewTab?.serviceName
        });
        
        if (!hasActivePreview || !selectedResource || selectedResource.type !== 'service') {
          // eslint-disable-next-line no-console
          devLog('⏭️ Skipping preview update - conditions not met');
          return;
        }
        
        // Check if file change is for currently previewed service or its backend
        const isServiceChange = updateProjectId === selectedProject?.id && 
                                (selectedResource.data?.name === updateServiceName || 
                                 activeBackendService?.name === updateServiceName);
        
        if (!isServiceChange) {
          // eslint-disable-next-line no-console
          devLog('⏭️ Skipping preview update - changed file not for active service');
          return;
        }
        
        try {
          const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
          const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(selectedProject?.id, updateServiceName);
        
          if (changedFiles.length === 0) {
            return;
          }
          
          // Get routing info using centralized utility
          const allFiles = await indexedDbFileStorage.getAllFilesForPreview(selectedProject?.id, updateServiceName, {});
          const currentService = updateServiceName === selectedResource.data?.name ? selectedResource.data : activeBackendService;
          
          const routingInfo = getContainerRoutingInfo(currentService, allFiles, { activePreviewTab, previewSessionId });
          
          if (!routingInfo) {
            console.error('❌ Failed to get routing info for service:', updateServiceName);
            return;
          }
          
          const { isBackend, isFrontend, containerPort, containerName, sessionId } = routingInfo;
          
          // Skip update if no session exists yet (preview container not started)
          if (!sessionId) {
            // eslint-disable-next-line no-console
            devLog('⏭️ Skipping incremental update - preview session not yet created (will use full render)');
            return;
          }
          
          // eslint-disable-next-line no-console
          devLog(`🎯 Update routing ${isBackend ? 'backend' : 'frontend'} (${containerName}, port ${containerPort})`);
          
          // eslint-disable-next-line no-console
          devLog('🔍 Service detection for update:', {
            serviceName: updateServiceName,
            isBackendChange: isBackend,
            isFrontendChange: isFrontend,
            serviceType: isBackend ? 'backend' : 'frontend',
            containerPort,
            containerName,
            sessionId
          });
          
          // eslint-disable-next-line no-console
          devLog(`🔄 ${isBackend ? 'Backend' : 'Frontend'} file changed, sending batched incremental update (${updates.length} files)`);
          
          // eslint-disable-next-line no-console
          devLog(`📤 Sending ${changedFiles.length} changed file(s) to ${containerName} (session: ${sessionId})`);
          
          // Send incremental update
          const updateApiUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/update`);
          const updatePayload = currentService?.vfsBacked
            // vfs-backed service: just say WHAT changed, let the container pull fresh content from vfs-service.
            ? { vfsPaths: changedFiles.map(file => file.filePath) }
            : { files: changedFiles.reduce((acc, file) => { acc[file.filePath] = file.content; return acc; }, {}) };
          const updateResponse = await getAuthorizedFetch()(updateApiUrl, {
            method: 'POST',
            headers: getBffHeaders({
              'Content-Type': 'application/json',
            }),
            body: JSON.stringify(updatePayload),
          });

          if (updateResponse.ok) {
            const updateResult = await updateResponse.json();
            if (updateResult.success) {
              // Mark files as sent to preview (separate from GitLab push)
              await indexedDbFileStorage.markAllChangedAsPreviewSent(selectedProject?.id, updateServiceName);
              // eslint-disable-next-line no-console
              devLog(`✅ ${isBackend ? 'Backend' : 'Frontend'} preview updated with ${changedFiles.length} file(s)`);
              
              // Hard-reload the iframe (full unmount/remount — see
              // refreshPreview's own setIframeKey) only for BACKEND
              // changes, which genuinely need it: a backend service
              // restarts, may rebind to a different port, and has no
              // client-side HMR of its own to pick the change up live.
              //
              // REMOVED for frontend changes (live UX request — "the
              // preview loads twice"): for a Vite/React frontend, the
              // incremental update above already lets Vite's OWN dev
              // server hot-reload the change in place over its existing
              // HMR websocket (confirmed live: "[vite] hot updated: /src/
              // App.css", applied instantly, no navigation). This
              // `setTimeout(refreshPreview, 2000)` fired UNCONDITIONALLY
              // afterward regardless — so every frontend edit visibly
              // reloaded twice: once seamlessly via Vite's live HMR, then
              // again ~2s later via a full iframe teardown/remount that
              // redid work Vite had already finished. Vite's own HMR
              // client already falls back to a full in-iframe reload on
              // its own (via its WebSocket) for the rarer edits it can't
              // hot-swap, so there's no frontend case this was covering
              // that Vite doesn't already handle itself.
              if (isBackend) {
                // Backend needs longer delay for Maven rebuild + service restart
                setTimeout(() => {
                  refreshPreview();
                  // eslint-disable-next-line no-console
                  devLog('🔄 Preview iframe refreshed');
                }, 3000);
              }
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to send incremental update:', error);
        }
      }, 500); // Debounce for 500ms to batch rapid file changes
    };

    // Only register listener once to prevent duplicates
    if (!fileChangeListenerRef.current) {
      fileChangeListenerRef.current = handleFileChange;
      window.addEventListener('monacoFileChanged', handleFileChange);
    }
    
    return () => {
      if (fileChangeListenerRef.current) {
        window.removeEventListener('monacoFileChanged', fileChangeListenerRef.current);
        fileChangeListenerRef.current = null;
      }
      // Clear debounce timer on cleanup
      if (previewUpdateDebounceTimer.current) {
        clearTimeout(previewUpdateDebounceTimer.current);
      }
    };
  }, [previewSessionId, previewTabs, selectedResource, selectedProject, activeBackendService, refreshPreview, loadSourceCode]);

  // REMOVED (live-reported preview flicker root cause): a `handleDiffApplied`
  // listener on a `diffApplied` event used to live here, firing immediately
  // (no debounce) on every chat-applied diff and independently POSTing to
  // the same `/api/preview/:sessionId/update` endpoint as `handleFileChange`
  // above. But `useAutoDiffApplier.jsx`'s `indexedDbFileStorage.saveFile`/
  // `deleteFile` calls already unconditionally dispatch `monacoFileChanged`
  // for the identical edit (see indexedDbFileStorage.js) — the event
  // `handleFileChange` above already listens for, debounced and batched.
  // So one chat-applied edit fired TWO independent update+refresh cycles,
  // staggered ~500ms apart (this debounce vs. the removed listener's
  // immediate fire), each triggering its own preview-runner rebuild —
  // confirmed via server logs as the cause of "shows the new preview,
  // then flickers to an error, then recovers" moments later. `diffApplied`
  // had exactly one listener in the whole codebase (this one, grepped
  // across src/ and the vendored chatbot-widget) — safe to remove outright
  // rather than just unsubscribing, so the dispatch sites in
  // useAutoDiffApplier.jsx were removed too.

  // Listen for MCP service creation events and auto-trigger Pull
  useEffect(() => {
    const handleServiceCreated = async (event) => {
      const {projectId, serviceName, toolName} = event.detail || {};
      
      // Only trigger if this is for the currently selected project
      if (selectedProject && projectId === selectedProject.id) {
        // eslint-disable-next-line no-console
        devLog(`🆕 Service created via ${toolName}: ${serviceName}. Auto-triggering Pull and selecting service...`);
        
        // Small delay to allow backend to fully create the service
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Refresh configuration to get the newly created service
        try {
          const updatedConfig = await miaPlatformDataService.getMicroservices(
            selectedProject.id,
            selectedRevision.id
          );
          
          if (updatedConfig?.services?.[serviceName]) {
            // Real live bug: this used to also call setSelectedResource(new
            // service) + onProjectContextChange here — auto-switching the
            // active resource the instant the agent's OWN tool call creates
            // a service. VfsChatPanel's remount key (App.jsx) includes the
            // selected service name (task #157, correct for a MANUAL
            // service switch), so this silently remounted the chat widget
            // with a brand-new session id mid-conversation — right as the
            // agent that just created the service was still actively
            // working. Confirmed live: asking the agent to add a service
            // visibly reset the chat to an empty "Code Generation Playbook"
            // state seconds later. Just refresh the configuration (new
            // service becomes visible/selectable) — never move the user's
            // view out from under an in-progress conversation. They can
            // switch to the new service themselves once ready.
            setConfiguration(updatedConfig);
            // eslint-disable-next-line no-console
            devLog(`✅ Configuration refreshed after service creation: ${serviceName} (not auto-selecting — see comment above)`);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to refresh configuration after service creation:', error);
        }
        
        // Dispatch event to trigger Pull operation
        window.dispatchEvent(new CustomEvent('autoTriggerGitPull', {
          detail: {
            projectId,
            serviceName,
            reason: 'Service created via MCP'
          }
        }));
      }
    };
    
    window.addEventListener('mcpServiceCreated', handleServiceCreated);
    
    return () => {
      window.removeEventListener('mcpServiceCreated', handleServiceCreated);
    };
  }, [selectedProject, selectedRevision]);

  // Listen for auto-sync updates and refresh preview iframe
  useEffect(() => {
    const handleSyncStarted = (event) => {
      const {projectId, serviceName} = event.detail || {};
      
      if (previewSessionId && selectedResource && 
          projectId === selectedProject?.id && 
          serviceName === selectedResource.name) {
        // eslint-disable-next-line no-console
        devLog('📤 Sync started for preview');
        setCompilationStatus('syncing');
      }
    };

    const handlePreviewUpdated = (event) => {
      const {projectId, serviceName, compilationReady} = event.detail || {};

      // No service selected yet (the "agent is still on its first coding
      // turn, no service existed when loadConfiguration last ran" case —
      // e.g. right after #118's Home-flow fix lands the user in Code view
      // before the agent has written anything). Re-running loadConfiguration
      // here is what actually recovers: it re-checks vfs-service's real file
      // tree (Canvas.jsx's own loadConfiguration already does this), finds
      // the now-real service, updates `configuration`, and
      // useProjectConfiguration's auto-select effect (keyed on
      // `configuration`) picks a real selectedResource for the first time —
      // silently, no user-facing alert, matching the "verify silently when
      // vfs is ready" ask. Without this, `configuration` was fetched once
      // (empty) and never re-fetched, so the dropdown/preview stayed on
      // "No services yet" forever even after the agent finished coding.
      //
      // BUG FIX (task #144): originally gated on `!selectedResource` alone —
      // but useProjectConfiguration's own fallback sets selectedResource to
      // the synthetic "Project Configuration" pseudo-resource
      // (type: 'configuration') whenever loadConfiguration ran before any
      // real service existed, which is a TRUTHY value. That poisoned this
      // exact recovery check: once the pseudo-resource fallback fired once,
      // `selectedResource` was never falsy again, so this reload never
      // re-ran even after the agent's files landed for real — leaving the
      // user permanently stuck on the fake "Project Configuration" resource
      // (whose literal name then got used as if it were a real vfs path by
      // the agent's own tool calls — "Path does not exist: Project
      // Configuration/..."). Also treat the pseudo-resource as "not really
      // selected yet" for this recovery purpose.
      const hasOnlyPseudoResource = selectedResource?.type === 'configuration';
      if ((!selectedResource || hasOnlyPseudoResource) && projectId === selectedProject?.id && !configLoading) {
        // eslint-disable-next-line no-console
        devLog('🔄 First real files landed with no service selected yet — reloading configuration:', serviceName);
        if (selectedRevision?.id) {
          loadConfiguration(selectedProject.id, selectedRevision.id);
        }
        return;
      }

      // A service ALREADY selected/open, but the event names one that
      // isn't in `configuration.services` yet — the multi-service case
      // (vfs_switch_service, task): the agent wrote a brand-new service
      // (e.g. "backend") into a project that already had one open ("app"),
      // so the guard above never fires (a real resource IS already
      // selected) and the service dropdown/tab picker was built once, way
      // before this new folder existed — live-confirmed stuck showing only
      // the original service forever, even after the new one had real
      // files. Re-running loadConfiguration merges the new service into the
      // dropdown without touching the current selection (it only calls
      // setConfiguration, never setSelectedResource).
      if (
        serviceName &&
        projectId === selectedProject?.id &&
        !configLoading &&
        !configuration?.services?.[serviceName]
      ) {
        // eslint-disable-next-line no-console
        devLog('🔄 New service appeared mid-session — refreshing service list:', serviceName);
        if (selectedRevision?.id) {
          loadConfiguration(selectedProject.id, selectedRevision.id);
        }
      }

      // Check if the updated service matches ANY preview tab (for multi-tab support)
      const matchingTab = previewTabs.find(tab =>
        tab.serviceName === serviceName && projectId === selectedProject?.id
      );

      // Also check if it matches the single preview mode (legacy support)
      const matchesSinglePreview = previewSessionId && selectedResource &&
          projectId === selectedProject?.id &&
          serviceName === selectedResource.name;

      // Refresh if it matches any tab or the single preview
      if (matchingTab || matchesSinglePreview) {
        // eslint-disable-next-line no-console
        devLog('🔄 Auto-sync completed for', serviceName, ', compilation ready:', compilationReady);
        
        if (compilationReady) {
          setCompilationStatus('ready');
          // Clear a stale Build Failed modal for this service directly, keyed
          // on the event's own serviceName rather than relying on
          // refreshPreview's activeTab-index lookup (previewTabs[activePreviewTab])
          // to happen to match — on a brand-new project's first render
          // attempt (the #98 cold-start race), activePreviewTab may not yet
          // point at the tab the earlier failure was recorded against, so
          // that check can silently miss and leave the modal open even after
          // this retry succeeds and real content renders underneath it.
          setBuildErrorModal((prev) => (
            prev.isOpen && prev.serviceName === serviceName
              ? { isOpen: false, error: null, serviceName: null }
              : prev
          ));
          setPendingFixError((prev) => (
            prev && prev.serviceName === serviceName ? null : prev
          ));
          // Small delay to ensure visual feedback before refresh
          setTimeout(() => {
            // A tab already exists (matchingTab), but if it has never had a
            // real session/url yet, this is still the FIRST successful
            // write for it — refreshPreview() only recovers a tab that
            // already has an ERROR on it (it otherwise just reloads the
            // iframe, which does nothing when there was never a url to
            // load). Live-confirmed: a placeholder tab created before any
            // code existed matched here, refreshPreview() saw no error on
            // it and silently no-op'd, and no /api/render call was ever
            // made — the exact same "stuck on Starting forever" symptom
            // this whole event was meant to fix.
            if (matchingTab && !matchingTab.error && !matchingTab.url && !matchingTab.sessionId) {
              autoRenderOnPreviewNotFound(matchingTab).catch((err) => {
                // eslint-disable-next-line no-console
                console.error('❌ Initial auto-render failed for', serviceName, err);
              });
            } else if (matchingTab?.error) {
              // Recovery case only — an existing, HEALTHY tab (has a url/
              // sessionId, no error) must NOT be refreshed here. Live-
              // reported flicker root cause: this same vfs write (agent
              // tool call OR Monaco save routed through vfs live-sync) also
              // reaches Canvas's own `handleFileChange` (monacoFileChanged
              // listener a few hundred lines up), which already POSTs the
              // incremental update to preview-runner and schedules its own
              // refreshPreview() 2-3s later. Both fire for the identical
              // edit — this one ~1s after dispatch, that one ~2-3s after
              // its own POST resolves — landing a SECOND iframe reload
              // while the dev server was still settling from the first,
              // which is exactly the "shows the new preview, then flickers
              // to a 502, then recovers" pattern reported after the
              // previous diffApplied-listener fix (that one was a true
              // duplicate; this is a second, independent duplicate between
              // Canvas's own two update paths). A healthy tab's refresh is
              // already owned by handleFileChange — this listener's only
              // remaining job for an existing tab is recovering one stuck
              // in an error state.
              refreshPreview();
            }
            setCompilationStatus(null);
          }, 1000);
        } else {
          setCompilationStatus('compiling');
        }
      } else if (
        compilationReady &&
        projectId === selectedProject?.id &&
        serviceName &&
        serviceName !== 'Project Configuration' &&
        serviceName !== 'project-configuration'
      ) {
        // Deliberately NOT gated on matching selectedResource at all (an
        // earlier version of this branch required
        // `serviceName === selectedResource?.data?.name`/`.name` — still
        // live-confirmed to silently never match on a brand-new project:
        // `selectedResource` can still be the synthetic "Project
        // Configuration" pseudo-resource, or a stale/differently-shaped
        // value, depending on exactly when `configuration` finishes loading
        // relative to this event, and that timing is not something this
        // handler can assume). autoRenderOnPreviewNotFound itself already
        // resolves everything it needs (the real service config, vfs vs.
        // git files) from `serviceName` and `selectedProject` alone, and is
        // safe to call speculatively — it no-ops safely if the service
        // isn't in `configuration` yet.
        // No preview tab/session exists yet for this service — this is the
        // FIRST successful write for a brand-new service, so there is
        // nothing for refreshPreview() to refresh (it only recovers an
        // EXISTING tab's error state or reloads an existing iframe; it
        // never performs the initial /api/render). Without this branch the
        // event above was a silent no-op and the preview never appeared
        // until the user clicked the manual refresh button, which calls
        // this same auto-render path directly.
        // eslint-disable-next-line no-console
        devLog('🔄 First files landed with no preview session yet — triggering initial render for', serviceName);
        autoRenderOnPreviewNotFound({ serviceName }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('❌ Initial auto-render failed for', serviceName, err);
        });
      }
    };

    window.addEventListener('previewSyncStarted', handleSyncStarted);
    window.addEventListener('previewFilesUpdated', handlePreviewUpdated);

    return () => {
      window.removeEventListener('previewSyncStarted', handleSyncStarted);
      window.removeEventListener('previewFilesUpdated', handlePreviewUpdated);
    };
  }, [previewSessionId, selectedResource, selectedProject, previewTabs, refreshPreview, configuration, configLoading, selectedRevision]);

  // Listen for git ref (branch/tag) changes and refresh preview
  useEffect(() => {
    const handleGitRefChanged = (event) => {
      const {projectId, serviceName, ref, refType} = event.detail || {};
      
      // Update selectedResourceGitBranch if this change is for the currently selected resource
      if (selectedResource && selectedResource.type === 'service' &&
          projectId === selectedProject?.id &&
          serviceName === (selectedResource.data?.name || selectedResource.name)) {
        setSelectedResourceGitBranch(ref);
        // eslint-disable-next-line no-console
        devLog(`🌿 Updated git branch for selected resource ${serviceName}:`, ref);
      }

      // Check if the change affects the current preview
      const matchingTab = previewTabs.find(tab => 
        tab.serviceName === serviceName && projectId === selectedProject?.id
      );
      
      const matchesSinglePreview = previewSessionId && selectedResource && 
          projectId === selectedProject?.id && 
          serviceName === selectedResource.name;
      
      if (matchingTab || matchesSinglePreview) {
        // eslint-disable-next-line no-console
        devLog(`🔄 Git ${refType} changed to ${ref} for ${serviceName}, refreshing preview...`);
        
        // Wait a bit for files to be pulled and saved to IndexedDB
        setTimeout(() => {
          refreshPreview();
        }, 1000);
      }
    };

    window.addEventListener('gitRefChanged', handleGitRefChanged);
    
    return () => {
      window.removeEventListener('gitRefChanged', handleGitRefChanged);
    };
  }, [previewSessionId, selectedResource, selectedProject, previewTabs, refreshPreview]);

  // Listen for runtime errors posted by the injected iframeErrorCapture script.
  // The script inside the iframe sends errors via:
  //   postMessage({ source: 'mia-flow-preview-client', type, message, stack })
  // Without this listener the only symptom visible to the user is a white screen.
  useEffect(() => {
    const handleIframeMessage = (event) => {
      // Only handle messages from our injected error-capture script
      if (!event.data || event.data.source !== 'mia-flow-preview-client') {
        return;
      }

      const { type, message, stack } = event.data;

      // Show the Build Error Modal only for real errors, not for console.warn / console.error noise.
      // 'vite-overlay' = Vite's own compile-time error overlay (e.g. unterminated JSX) —
      // it fires before any app JS runs, so window.onerror never catches it; the injected
      // script in iframeErrorCapture.js watches the DOM for it separately.
      if (type === 'error' || type === 'unhandledrejection' || type === 'vite-overlay') {
        // Derive service name from the currently active preview tab
        const activeTabData = previewTabs[activePreviewTab];
        const serviceName = activeTabData?.serviceName || 'Preview';

        // Build a readable error string that includes the stack trace when available
        const fullError = stack && stack !== message
          ? `${message}\n\nStack trace:\n${stack}`
          : message;

        // eslint-disable-next-line no-console
        console.warn(`🚨 [iframeErrorCapture] Runtime error in preview (${serviceName}):`, fullError);

        setPendingFixError({ error: fullError, serviceName });
      }
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [previewTabs, activePreviewTab]);

  // Handle resource selection (service, endpoint, collection, etc.)
  const handleResourceClick = (resource) => {
    // Check if we're clicking on the same resource
    const isSameResource = selectedResource?.type === resource?.type && 
      selectedResource?.name === resource?.name;
    
    // If clicking on the same resource, just keep the current view state
    if (isSameResource) {
      return;
    }
    
    // Check if we're switching between sources (services with repository URLs)
    const isCurrentSourceService = selectedResource?.type === 'service' && 
      (selectedResource?.data?.repositoryUrl || selectedResource?.data?.repoUrl || selectedResource?.data?.sshUrl);
    const isNewSourceService = resource?.type === 'service' && 
      (resource?.data?.repositoryUrl || resource?.data?.repoUrl || resource?.data?.sshUrl);
    
    // Check if selecting a collection
    const isCollection = resource?.type === 'collection';
    const isCurrentCollection = selectedResource?.type === 'collection';
    
    // Keep repository view if:
    // 1. Switching between sources and currently in source code view
    // 2. Switching between collections and currently in source code view
    const shouldKeepRepositoryView = showRepositoryView && (
      (isCurrentSourceService && isNewSourceService) ||
      (isCurrentCollection && isCollection)
    );
    
    setSelectedResource(resource);
    setSelectedFile(null); // Clear file selection when resource is selected
    
    // Set selected collection if it's a collection resource
    if (isCollection) {
      setSelectedCollection({
        name: resource.name,
        data: resource.data
      });
      // Keep current view when switching between collections, otherwise default to details
      if (!isCurrentCollection) {
        setShowRepositoryView(false); // Show details view by default for new collection selection
      }
    } else {
      setSelectedCollection(null);
      if (!shouldKeepRepositoryView) {
        setShowRepositoryView(false); // Reset to details view when selecting a new resource (unless switching between sources)
      }
    }
    
    // eslint-disable-next-line no-console
    devLog('🔧 Selected resource:', resource);
    
    // Notify parent about resource selection
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: selectedTenant,
        project: selectedProject,
        projectId: selectedProject?.id,
        revision: selectedRevision,
        selectedResource: resource,
        selectedFile: null // Clear file when resource changes
      });
    }
  };

  // Handle service creation (reload configuration after creating a service)
  const handleCreateService = async (serviceData, filesByPath) => {
    if (!selectedProject || !selectedRevision) return;

    // vfs-backed path (CreateServiceModal cloning a template/git repo into a
    // new vfs-service subfolder — see gitTemplateClone.js — instead of
    // provisioning a new Console/GitLab-backed service): there is no Console
    // config to reload, since nothing was registered there. Select the new
    // service directly and seed IndexedDB with whatever was just cloned so
    // the Code tab shows it immediately (the App.jsx vfs-seed effect would
    // eventually do this too, but only on a fresh mount with empty local
    // storage — this covers the "just created it in this session" case).
    if (serviceData?.vfsBacked) {
      try {
        if (filesByPath) {
          for (const [filePath, content] of Object.entries(filesByPath)) {
            await indexedDbFileStorage.saveFile(selectedProject.id, serviceData.name, filePath, content, {
              fromVfs: true,
              originalContent: content,
            });
          }
        }
        // Registered so it survives a reload and shows up in the service
        // dropdowns (UnifiedTopBar), which otherwise only enumerate
        // Console-registered services — see loadConfiguration's merge above.
        vfsClient.registerVfsService(selectedProject.id, serviceData.name);
        setConfiguration((prev) => ({
          ...prev,
          services: { ...(prev?.services || {}), [serviceData.name]: serviceData },
        }));
        setSelectedResource({type: 'service', data: serviceData, name: serviceData.name});
        window.dispatchEvent(new CustomEvent('repositoryFilesUpdated', {
          detail: {projectId: selectedProject.id, serviceName: serviceData.name, fileCount: filesByPath ? Object.keys(filesByPath).length : 0}
        }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to seed newly cloned service into IndexedDB:', error);
      } finally {
        setIsCreateServiceModalOpen(false);
      }
      return;
    }

    try {
      // eslint-disable-next-line no-console
      devLog('🔄 Reloading configuration after service creation...');
      await loadConfiguration(selectedProject.id, selectedRevision.id);
      // eslint-disable-next-line no-console
      devLog('✅ Configuration reloaded successfully');
      // Close the modal after successful creation
      setIsCreateServiceModalOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to reload configuration:', error);
    }
  };

  // "+ Create new service" of the service dropdown: the Home's Apps "+" ways (an empty service, a .zip, a folder, a git
  // repository), one flow (home/AddService.jsx). Whatever was added is listed and opened.
  const addServiceFlow = useAddService(selectedProject?.id || selectedProject?._id, {
    taken: Object.keys(configuration?.services || {}).map((key) => shortServiceName(servicePath(key))),
    onCreateEmpty: (serviceData, filesByPath) => handleCreateService(serviceData, filesByPath),
    onDone: async({ services: added = [] }) => {
      const projectKey = selectedProject?.id || selectedProject?._id;
      if (projectKey && selectedRevision?.id) { await loadConfiguration(projectKey, selectedRevision.id).catch(() => {}); }
      const first = added[0];
      if (first) {
        setSelectedResource({ type: 'service', data: { name: first, displayName: shortServiceName(first), vfsBacked: true }, name: first });
      }
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: added.length > 1 ? `Added ${added.length} services.` : 'Service added.', type: 'success' } }));
    },
  });
  const addServicePhase = addServiceFlow.state?.phase;
  useEffect(() => {
    if (addServicePhase === 'failed') {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: addServiceFlow.state.text, type: 'error' } }));
    }
  }, [addServicePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle collection save from editor
  const handleCollectionSave = (updatedCollectionData) => {
    if (!selectedCollection || !selectedProject) return;
    
    const updatedCollections = {
      ...collections,
      [selectedCollection.name]: updatedCollectionData
    };
    
    setCollections(updatedCollections);
    setSelectedCollection({
      ...selectedCollection,
      data: updatedCollectionData
    });
    
    // Update configuration
    if (configuration) {
      setConfiguration({
        ...configuration,
        collections: updatedCollections
      });
    }
    
    // eslint-disable-next-line no-console
    devLog('💾 Collection updated in Canvas:', selectedCollection.name);
  };

  // Handle refresh - reload configuration while keeping filters
  const handleRefresh = async () => {
    // Store current selections to restore after refresh
    const currentResourceName = selectedResource?.name;
    const currentResourceType = selectedResource?.type;
    
    // Refresh tenants list (keeping selection)
    if (tenants.length > 0) {
      await loadTenants();
    }
    
    // Refresh projects list if tenant is selected (keeping selection)
    if (selectedTenant?.id) {
      await loadProjects(selectedTenant.id, projectsSearchQuery, 1, false);
    }
    
    // Refresh revisions list if project is selected (keeping selection)
    if (selectedProject?.id) {
      await loadRevisions(selectedProject.id);
    }
    
    // Refresh configuration if both project and revision are selected
    if (selectedProject && selectedRevision) {
      // eslint-disable-next-line no-console
      devLog('🔄 Refreshing configuration for project:', selectedProject.id, 'revision:', selectedRevision.id);
      
      // Store the resource to re-select after configuration loads
      if (currentResourceName && currentResourceType) {
        setPendingResourceReselect({
          name: currentResourceName,
          type: currentResourceType
        });
      }
      
      await loadConfiguration(selectedProject.id, selectedRevision.id);
    }
  };
  
  // Re-select resource after configuration refresh
  useEffect(() => {
    if (pendingResourceReselect && configuration && collections) {
      const {name, type} = pendingResourceReselect;
      
      // eslint-disable-next-line no-console
      devLog('🔄 Re-selecting resource after refresh:', name, type);
      
      // Build refreshed resource with updated data from new configuration
      const refreshedResource = {
        name,
        type
      };
      
      // Find the updated resource data from the new configuration
      if (type === 'service' && configuration.services?.[name]) {
        refreshedResource.data = configuration.services[name];
      } else if (type === 'endpoint' && configuration.endpoints?.[name]) {
        refreshedResource.data = configuration.endpoints[name];
      } else if (type === 'collection') {
        const refreshedCollection = collections[name] || configuration.collections?.[name];
        if (refreshedCollection) {
          refreshedResource.data = refreshedCollection;
        }
      }
      
      // Re-select the resource to refresh the displayed content
      if (refreshedResource.data) {
        setSelectedResource(refreshedResource);
        
        // Also update collection if it was selected
        if (type === 'collection') {
          setSelectedCollection({
            name,
            data: refreshedResource.data
          });
        }
        
        // eslint-disable-next-line no-console
        devLog('✅ Resource re-selected with fresh data:', name);
      }
      
      // Clear the pending re-select
      setPendingResourceReselect(null);
    }
  }, [configuration, collections, pendingResourceReselect]);
  
  // Expose handleRefresh to parent via ref
  useImperativeHandle(ref, () => ({
    handleRefresh,
    // task #81 — lets a sibling (VfsChatPanel, via App.jsx's canvasRef) jump
    // straight to a real, ready live-preview URL the agent just built and
    // returned from `vfs_get_preview_url`, without requiring a console-
    // registered microservice to exist first. The normal Preview tab flow
    // (previewTabs population, the "Select service to preview" dropdown)
    // is entirely keyed off `configuration.services`, which is empty for
    // a brand-new vfs-only project — exactly the common case right after a
    // first build, when there's nothing yet to select. Setting a synthetic
    // `selectedResource` of type 'service' bypasses the `!selectedResource`
    // "No services yet" branch above (see its own comment), and appending
    // an already-ready ({loading:false, error:null, url}) entry to
    // previewTabs makes `isShowingLivePreview` true and renders the iframe
    // immediately — the same rendering path a real console microservice's
    // preview already uses, just fed a URL from outside instead of from
    // this component's own render pipeline.
    showExternalPreview: ({ serviceName, url }) => {
      if (!serviceName || !url) return;
      const existingIndex = previewTabs.findIndex((t) => t.serviceName === serviceName);
      // BUG FIX (live-reported: after this tab was created, no chat-driven
      // edit ever reached the running preview — the only thing that worked
      // was the manual Refresh button): this used to be a made-up
      // placeholder (`external-${serviceName}`), not preview-runner's real
      // session id. Canvas's own incremental-update path later sends
      // `POST /api/preview/${tab.sessionId}/update` for every file the
      // agent (or Monaco) edits on an already-open tab — with the
      // placeholder, that always 404s silently (confirmed live:
      // `external-services%2Fapp/update 404`), so the running dev server
      // never actually got told about the change; only a full manual
      // Refresh (which re-derives the real session id a different way,
      // via generateSessionId) ever worked. `url` already IS
      // `.../api/preview/<realSessionId>/proxy` — preview-runner's own
      // URL shape — so the real id is right there; no need to invent one.
      const realSessionId = sessionIdFromPreviewUrl(url);
      const tab = {
        serviceName,
        url,
        loading: false,
        iframeLoaded: true,
        error: null,
        sessionId: realSessionId || `external-${serviceName}`,
        containerPort: null,
        framework: 'vfs-live-preview',
        iframeKey: Date.now(),
        // Marks this tab as not console-service-backed, so the
        // "auto-detect previewable services from configuration" effect
        // (above) doesn't clear it out from under us — see that effect's
        // own comment on this exact race.
        external: true,
      };
      setPreviewTabs((prev) => {
        const idx = prev.findIndex((t) => t.serviceName === serviceName);
        if (idx === -1) return [...prev, tab];
        const next = [...prev];
        next[idx] = { ...next[idx], ...tab };
        return next;
      });
      // Only the FIRST time this service's preview becomes available do we
      // grab the user's attention (select it + jump to Preview) — every
      // later call (the agent checking on a still-building session again)
      // just refreshes the tab's own data above, in the background, without
      // moving the user off whatever they're deliberately looking at. See
      // externalPreviewShownRef's own doc.
      const isFirstTimeShown = !externalPreviewShownRef.current.has(serviceName);
      if (isFirstTimeShown) {
        externalPreviewShownRef.current.add(serviceName);
        setSelectedResource({ type: 'service', data: { name: serviceName, vfsBacked: true } });
        setActiveTab('preview');
      }
      // Live-reported: switching a multi-service project's dropdown to
      // "backend" correctly updated the URL bar and status bar (both read
      // previewTabs[activePreviewTab], same as the iframe) to show
      // "backend" — but the iframe itself kept rendering the FRONTEND's
      // already-loaded content. Root cause: `setActivePreviewTab` above is
      // the ONLY thing that moves the active tab, but it's gated to
      // first-ever-call-per-service — once the agent had already fetched
      // "backend"'s preview URL earlier in the conversation, a LATER
      // re-fetch (e.g. after fixing a startup bug and re-checking) updated
      // this tab's stored `.url` above but never touched `activePreviewTab`
      // again, leaving it pointed at whatever OTHER tab was active. Re-sync
      // it whenever this update is for the service the user/agent has
      // ALREADY explicitly selected (selectedResource already names it) —
      // that's not "yanking" anyone anywhere, it's fixing the index to
      // match what the rest of the UI is already showing.
      if (isFirstTimeShown || selectedResource?.data?.name === serviceName) {
        setActivePreviewTab(existingIndex === -1 ? previewTabs.length : existingIndex);
      }
    },
  }));

  // Detect content type based on content structure
  const contentType = useMemo(() => {
    if (!content?.content) return 'document';
    
    // Check if content type is explicitly specified
    if (content.type === 'image') return 'image';
    if (content.type === 'video') return 'video';
    
    const text = content.content;
    
    // Check if content is a data URL (base64 image or video)
    if (typeof text === 'string' && text.startsWith('data:image/')) {
      return 'image';
    }
    if (typeof text === 'string' && text.startsWith('data:video/')) {
      return 'video';
    }
    
    // Check for code patterns
    const codePatterns = [
      /```[\s\S]+```/g, // Code blocks
      /function\s+\w+\s*\(/g, // Function declarations
      /import\s+.*from/g, // ES6 imports
      /export\s+(default\s+)?/g, // ES6 exports
      /<\/?\w+[^>]*>/g, // HTML tags
      /\{\s*[\w\s:,]+\}/g, // JSON-like objects
    ];
    
    const hasCode = codePatterns.some(pattern => pattern.test(text));
    
    // Check for document patterns
    const docPatterns = [
      /^#\s+/m, // Markdown headers
      /^\*\s+/m, // Bullet points
      /^\d+\.\s+/m, // Numbered lists
    ];
    
    const hasDocStructure = docPatterns.some(pattern => pattern.test(text));
    
    if (hasCode) return 'code';
    if (hasDocStructure) return 'document';
    
    // Default to document for general content
    return 'document';
  }, [content?.type, content?.content]);

  // Whether the Preview tab is showing (or about to show) the live
  // container-live app UI — the frontend/backend iframe, its loading
  // spinner + PreviewLogs, or the multi-tab preview list — as opposed to
  // the generated-text/image/video content viewer. Single source of truth
  // shared by the branch that picks which of those two UIs to render and
  // by the canvas-footer visibility guard, so they can never disagree
  // (previously they used two subtly different formulas, which let the
  // word-count/copy/download footer leak in behind a loading preview for
  // vfs-backed services whose name-based detection reports isFrontend/
  // isBackend as false).
  const isShowingLivePreview = useMemo(() => {
    if (previewTabs.length > 0) return true;
    if (selectedResource?.type === 'service') {
      // Same gap as loadServicePreview's own pre-check: a vfs-backed
      // service has no tags/dockerImage, so name-only detection degrades
      // to 'none' confidence and would otherwise report false here even
      // though previewUrl is already set and correct.
      if (selectedResource.data?.vfsBacked) return true;
      const detection = detectServiceType(selectedResource.data);
      return detection.isFrontend || detection.isBackend;
    }
    return Boolean(previewUrl);
  }, [previewTabs, selectedResource, previewUrl]);

  // Extract code from markdown if present
  const extractedCode = useMemo(() => {
    if (!content?.content || contentType !== 'code') return '';
    
    const text = content.content;
    const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
    const matches = [...text.matchAll(codeBlockRegex)];
    
    if (matches.length > 0) {
      return matches.map(match => match[1]).join('\n\n');
    }
    
    return text;
  }, [content?.content, contentType]);

  // Detect programming language for syntax highlighting
  const detectedLanguage = useMemo(() => {
    if (!content?.content) return 'text';
    
    const text = content.content.toLowerCase();
    
    // Check for HTML first (most specific)
    if (text.includes('<!doctype html>') || 
        text.includes('<html') || 
        (text.includes('<head>') && text.includes('<body>'))) {
      return 'html';
    }
    
    if (text.includes('function') && text.includes('const')) return 'javascript';
    if (text.includes('def ') && text.includes('import')) return 'python';
    if (text.includes('{') && text.includes('"')) return 'json';
    if (text.includes('select') || text.includes('from')) return 'sql';
    if (text.includes('body {') || text.includes('.class')) return 'css';
    
    return 'text';
  }, [content?.content]);

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Follow mode (components/collab/FollowController.jsx): open the followed person's file in their service, on Code.
  const handleFollowOpenFile = ({ service, filePath, path, lineNumber, first }) => {
    // A file in no service (the brain, the project root): the project's Files view on it, as a chat file link does
    // (`?view=files&path=…&line=…`; the Home effect above opens it), not the Code tab, which Home has none of.
    if (!service) {
      const params = new URLSearchParams(window.location.search);
      params.set('view', 'files');
      params.set('path', path || filePath);
      if (lineNumber) { params.set('line', String(lineNumber)); } else { params.delete('line'); }
      params.delete('tab');
      if (first) { setWorkspaceView(null); }
      navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash }, { replace: !first });
      return;
    }
    // handleServiceSelect ignores a service the project's configuration does not list.
    if (first && service && selectedResource?.data?.name !== service) { handleServiceSelect(service, 'code'); }
    if (first && activeTab !== 'code') { setActiveTab('code'); }
    window.dispatchEvent(new CustomEvent('openFile', {
      detail: { projectId: selectedProject?.id, serviceName: service || undefined, filePath, lineNumber: lineNumber || undefined },
    }));
  };

  // Shared preview, collaboration phase 3 (hooks/useSharedPreview.js): publish where this tab's preview is, and open
  // "see what X sees" in this tab's own preview pane (the same preview URL at their route).
  const pendingSharedPreviewRef = useRef(null);
  const applySharedPreview = (name, route) => {
    const index = previewTabs.findIndex((tab) => serviceNameOf(tab.serviceName) === name && tab.url);
    if (index < 0) { return false; }
    setPreviewTabs((tabs) => tabs.map((tab, i) => {
      if (i !== index) { return tab; }
      const base = tab.baseUrl || tab.url;
      return { ...tab, baseUrl: base, url: joinRoute(base, route) || base, iframeKey: Date.now() };
    }));
    setActivePreviewTab(index);
    return true;
  };
  const handleOpenSharedPreview = ({ service, route }) => {
    // A pinned runnable (Apps' "Open preview" sends its runner name): its own entry, never its owner.
    const pinnedTarget = runnablePreview.resolve(service);
    if (pinnedTarget) { openRunnablePreview(pinnedTarget, route); return; }
    const name = serviceNameOf(service) || serviceNameOf(previewTabs[activePreviewTab]?.serviceName) || serviceNameOf(selectedResource?.data?.name);
    // Always Preview on that service, from any page (Apps, Home, a workspace view): the service selected by its
    // configuration key (`services/<name>`; the short name alone selected nothing and left the page on Home), the
    // workspace view left. A service the configuration does not know yet (just added) is selected once it is loaded.
    const plan = openPreviewPlan(configuration?.services, name, selectedResource?.data?.name || null);
    setWorkspaceView(null);
    if (plan.key) { handleServiceSelect(plan.key, 'preview'); }
    if (activeTab !== 'preview' && splitViewMode !== 'split') { setActiveTab('preview'); }
    if (name && applySharedPreview(name, route)) { return; }
    // Not running in this tab yet: open that service's preview, then go to the route once it has a URL.
    pendingSharedPreviewRef.current = { name, route, select: plan.load ? plan.name : null };
    if (plan.load && selectedProject?.id && selectedRevision?.id) { loadConfiguration(selectedProject.id, selectedRevision.id); }
  };
  // Open preview of a service the configuration did not know yet: selected on Preview as soon as it is loaded.
  useEffect(() => {
    const pending = pendingSharedPreviewRef.current;
    const key = pending?.select ? serviceKeyIn(configuration?.services, pending.select) : null;
    if (!key) { return; }
    pendingSharedPreviewRef.current = { ...pending, select: null };
    setWorkspaceView(null);
    handleServiceSelect(key, 'preview');
    setActiveTab('preview');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration?.services]);
  useEffect(() => {
    const pending = pendingSharedPreviewRef.current;
    if (pending && applySharedPreview(pending.name, pending.route)) { pendingSharedPreviewRef.current = null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTabs]);
  const sharedPreviewProjectId = selectedProject?.id && (selectedProject?.storageTier || 'vfs') !== 'console' ? selectedProject.id : null;
  // The workspace tabs and views (collaboration phase 4) are for vfs-backed projects; Changes N counts what others
  // changed since you last opened Changes.
  const workspaceEnabled = Boolean(sharedPreviewProjectId && vfsClient.isVfsBackedProject(sharedPreviewProjectId));
  // A vfs project with no service yet has nothing selected: its Home ("Start by…") shows instead of an empty page.
  const brandNewWorkspace = Boolean(workspaceEnabled && !selectedResource && configuration && Object.keys(configuration.services || {}).length === 0);
  const changesCount = useChangesCount(workspaceEnabled ? sharedPreviewProjectId : null, { seen: workspaceView?.tab === 'changes' });
  // "In this Project", docked on the right on desktop (components/workspace/InThisProjectPanel.jsx): open or closed,
  // remembered in this browser; not on phones and tablets, nor where the workspace is too narrow for it (the header's
  // avatars keep their popover there).
  const workspaceRowRef = useRef(null);
  const compactWindow = useCompact();
  const workspaceRowNarrow = useNarrowElement(workspaceRowRef, 860);
  const inProjectAvailable = workspaceEnabled && !compactWindow && !workspaceRowNarrow;
  // Which tabs the project shows (utils/projectTabsModel.js): what the page knows for the smart ones (the services, the
  // datastores among the technologies). History is always one tab, its three views on top.
  // The release gate (Quality): its failing rules put Quality on the row in Auto, and are its count.
  const { gate: qualityGate } = useQualityGate(workspaceEnabled ? sharedPreviewProjectId : null);
  const qualityBlocked = blockedCount(qualityGate);
  // Configuration's Auto reason: secrets a service declares that nobody set yet (hooks/useAppConfigSignal.js).
  const secretsMissing = useAppConfigSignal(workspaceEnabled ? sharedPreviewProjectId : null);
  // Data's and Traces' Auto reason: the stores the sandbox runs for the project (a scaffolded PostgreSQL is in no
  // service nor technology), read again when the service list changes.
  const dataStoreCount = useDataStoresSignal(workspaceEnabled ? sharedPreviewProjectId : null, Object.keys(configuration?.services || {}).join('|'));
  // MCP's Auto reason: the MCP servers the services expose (found in their code), read again when the service list changes.
  const mcpServers = useMcpServersSignal(workspaceEnabled ? sharedPreviewProjectId : null, Object.keys(configuration?.services || {}).join('|'));
  const tabSignals = useMemo(
    () => ({
      ...tabSignalsOf(Object.entries(configuration?.services || {}), vfsProjectMetadata?.technologies || [], { stores: dataStoreCount }),
      qualityBlocked,
      secretsMissing,
      mcpServers,
    }),
    [configuration?.services, vfsProjectMetadata?.technologies, dataStoreCount, qualityBlocked, secretsMissing, mcpServers],
  );
  const [inProjectOpen, setInProjectOpenState] = useState(() => {
    try { return localStorage.getItem('flow.inProject.open') !== 'false'; } catch { return true; }
  });
  const setInProjectOpen = (open) => {
    setInProjectOpenState(open);
    try { localStorage.setItem('flow.inProject.open', open ? 'true' : 'false'); } catch { /* not remembered */ }
  };
  const inProjectPresence = useProjectPresence(inProjectAvailable ? sharedPreviewProjectId : null);
  // Where the Home puts its own tools (Start all, Open app) in the header's tab row: set while that row shows the Home.
  const [workspaceToolsSlot, setWorkspaceToolsSlot] = useState(null);
  useSharedPreview({
    projectId: sharedPreviewProjectId,
    tab: activeTab === 'preview' || splitViewMode === 'split' ? previewTabs[activePreviewTab] || null : null,
    iframeRef,
    onOpen: handleOpenSharedPreview,
  });

  // Collaboration phase 3: the Activity timeline, viewers' suggestions, handoff notes (components/collab). Comments,
  // Suggestions, Leave a note and Handoff notes are one dialog, Team ▾ › Notes (collab/NotesModal.jsx), open on a tab.
  const [notesDialog, setNotesDialog] = useState(null); // { tab, focusId } | null
  const [suggestionsPending, setSuggestionsPending] = useState(0);
  // With no tab asked (Team ▾ › Notes): Suggestions while some wait, else Comments.
  const openNotes = (tab = null, focusId = null) => setNotesDialog({ tab: tab || (suggestionsPending > 0 ? 'suggestions' : 'comments'), focusId });
  // The project Home's Reviews and comments gadget opens Notes on Comments (`flow:open-comments`).
  useEffect(() => {
    const open = () => setNotesDialog({ tab: 'comments', focusId: null });
    window.addEventListener('flow:open-comments', open);
    return () => window.removeEventListener('flow:open-comments', open);
  }, []);
  // "Shared with N people · Manage" in the In this Project panel opens Share.
  const [sharingFromPanel, setSharingFromPanel] = useState(false);
  const collabProjectName = selectedProject?.name || selectedProject?.id || '';
  const collabNameRef = useRef(collabProjectName);
  collabNameRef.current = collabProjectName;
  // Arrived in a shared project; on leaving it after changing something, LeaveNotePrompt offers a note.
  useEffect(() => {
    if (!sharedPreviewProjectId) { return undefined; }
    markArrived(sharedPreviewProjectId);
    return () => { markLeft(sharedPreviewProjectId, collabNameRef.current); };
  }, [sharedPreviewProjectId]);
  // Editors and the owner: how many viewer suggestions wait (the top bar's badge).
  useEffect(() => {
    setSuggestionsPending(0);
    if (!sharedPreviewProjectId || !vfsClient.isVfsBackedProject(sharedPreviewProjectId)) { return undefined; }
    let cancelled = false;
    const load = () => getSuggestions(sharedPreviewProjectId, { status: 'pending' })
      .then((answer) => { if (!cancelled) { setSuggestionsPending(answer?.canDecide ? pendingCount(answer) : 0); } })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [sharedPreviewProjectId, notesDialog]);

  // Selects a service in the top bar, or Project Home ('project-configuration' / ''), which shows the
  // project Dashboard/Files. Extracted from the top bar's inline `onServiceSelect` so other handlers
  // (e.g. switching to Code in a project that has no app yet) can reuse exactly the same navigation.
  // `forceTab`: the tab the caller is about to show ('code' | 'preview'), when `activeTab` here is still the old one (a
  // setActiveTab just above is not visible to this closure).
  const handleServiceSelect = (serviceName, forceTab) => {
    const tabNow = forceTab || activeTab;
    // A pinned runnable picked in the header ("app · docker-compose.yaml"): its own preview.
    if (runnableNameOf(serviceName) && !configuration?.services?.[serviceName]) {
      openRunnablePreview(runnablePreview.resolve(serviceName));
      return;
    }
            // Going to a page (Home, a service) leaves the workspace view (Changes, Activity, Sessions).
            setWorkspaceView(null);
            if (serviceName === 'canvas') {
              // Clear selection and reset to canvas view
              setSelectedResource(null);
              setPreviewUrl(null);
              setPreviewTabs([]);
              setActivePreviewTab(-1);
              
              // Notify parent to update URL
              if (onProjectContextChange) {
                onProjectContextChange({
                  tenant: selectedTenant,
                  project: selectedProject,
                  projectId: selectedProject?.id,
                  revision: selectedRevision,
                  selectedResource: null,
                  selectedMicroservice: null,
                  activeTab: 'preview'
                });
              }
            } else if (serviceName === '+create-service') {
              // The add-a-service chooser (editors only)
              if (!isViewOnly) { setIsCreateServiceModalOpen(true); }
            } else if (serviceName === 'project-configuration' || serviceName === '') {
              // Select the project configuration repository. '' is the
              // service SearchDropdown's pinned "Home" row value
              // (UnifiedTopBar.jsx) — the ProjectBreadcrumb's own Home
              // button uses 'project-configuration' instead, so without
              // this the dropdown's Home row fell through to neither
              // branch above and did nothing at all.
              const configurationRepo = {
                name: 'Project Configuration',
                repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
                repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
                type: 'configuration'
              };
              
              // Log for debugging
              devLog('🗂️ [Canvas] Selected Project Configuration:', {
                repositoryUrl: configurationRepo.repositoryUrl,
                fullProjectConfig: fullProjectConfig,
                selectedProject: selectedProject
              });
              
              setSelectedResource({type: 'configuration', data: configurationRepo});
              // Same reset the 'canvas' branch above already does when
              // leaving a service — without it, the OLD service's preview
              // state (previewUrl/previewTabs/activePreviewTab) stays set,
              // and the URL/context never gets told a service is no longer
              // selected (no onProjectContextChange call existed here at
              // all). Live-confirmed: clicking "Go to Project Home" from a
              // service's Preview/Code view showed a flash of that SAME
              // service's stale preview iframe (dropdown already reset to
              // the placeholder, body still the old iframe) before finally
              // landing on Dashboard a beat later.
              setPreviewUrl(null);
              setPreviewTabs([]);
              setActivePreviewTab(-1);
              if (onProjectContextChange) {
                onProjectContextChange({
                  tenant: selectedTenant,
                  project: selectedProject,
                  projectId: selectedProject?.id,
                  revision: selectedRevision,
                  selectedResource: null,
                  selectedMicroservice: null,
                  activeTab: 'preview'
                });
              }

              // Home (the "Project Configuration" pseudo-resource) only has
              // Dashboard/Files content wired up inside the Preview tab's
              // render branch — there is no Code-tab equivalent, since
              // "Code" is a per-service concept once you're inside one.
              // Force back to Preview so the Dashboard/Files pill (which the
              // top bar shows regardless of tab whenever this pseudo-
              // resource is selected) always has real content behind it,
              // instead of leaving the stale Code tab's empty repository-
              // browser stub on screen (confirmed live: this is what "click
              // Home while on Code tab" used to leave stuck on screen).
              setActiveTab('preview');
              // "Go to Project Home" must always land on Dashboard, not
              // whatever `dashboardOverviewMode` was last left on. The
              // task #146 effect above only resets this on a PROJECT
              // change (`selectedProject?.id`) — clicking Home again on
              // the SAME project while already on the Files pill left it
              // stuck there (live-confirmed: user explicitly wants Home to
              // deterministically mean Dashboard, every time).
              setDashboardOverviewMode('dashboard');
              // BUG FIX: `setActiveTab` above is the raw state setter, not
              // `handleTabChange` (the tab-switcher UI's own handler,
              // which also calls `onSelectionChange` to keep the URL in
              // sync — see its own code just above). Without an explicit
              // call here, the URL was left exactly as it was before this
              // click (still whatever /code/.../<service>/<branch>/<tab>
              // it last pointed at) while React state now says "no service
              // selected" — a state/URL mismatch that left the URL
              // reconstruction effects (useCanvasProjectContext.js) with
              // stale, inconsistent input to reconcile, which is what
              // live-reported as "clicking Home switches back to the app
              // URL" instead of staying on
              // /code/<tenantId>/<projectId>?conversationId=<id>. Calling
              // this directly, with no microservice/branch/tab, forces the
              // URL to that exact bare project URL immediately —
              // syncUrlWithCanvasSelections (App.jsx) already preserves
              // the conversationId query param on its own.
              if (onSelectionChange) {
                onSelectionChange(null, null, null, null, null);
              }
            } else if (serviceName && configuration.services[serviceName]) {
              const service = configuration.services[serviceName];
              setSelectedResource({type: 'service', data: service});

              // Different behavior based on active tab
              if (servicePickKeepsCode({ activeTab: tabNow, atHome: selectedResource?.type !== 'service', homeMode: dashboardOverviewMode })) {
                // On Code (the service's, or the Files view at Home): stay on Code, with the picked service's files.
                if (activeTab !== 'code') { setActiveTab('code'); }
                setShowRepositoryView(true);
                setShowTreePanel(false);
              } else if (tabNow === 'preview') {
                // Notify parent to update URL for the selected service
                if (onProjectContextChange) {
                  onProjectContextChange({
                    // `handleProjectContextChange` (App.jsx's
                    // onProjectContextChange consumer, in
                    // useCanvasProjectContext.js) does a WHOLESALE
                    // setCanvasProjectContext(context) — not a merge — so
                    // omitting `projectId` here doesn't leave the old value
                    // in place, it wipes it to undefined. App.jsx's render
                    // gate for VfsChatPanel is
                    // `hideCanvas || canvasProjectContext?.projectId`, so
                    // losing it made the chat panel unmount and fall back to
                    // the "Setting up your project…" placeholder on every
                    // service-dropdown switch made while on the Preview tab
                    // (live-confirmed regression, task #159). Every other
                    // call site of onProjectContextChange in this file sets
                    // `projectId` explicitly (see loadConfiguration's own
                    // `projectId: selectedProject?.id`) — this one just
                    // never did.
                    tenant: selectedTenant,
                    project: selectedProject,
                    projectId: selectedProject?.id,
                    revision: selectedRevision,
                    selectedResource: {type: 'service', data: service},
                    selectedMicroservice: service,
                    activeTab: 'preview'
                  });
                }
              }
            }
          };

  const handleTabChange = (tab) => {
    setWorkspaceView(null);
    // No app to show code for: the project has no services at all, or the service being viewed is empty
    // (a documents-only project, or one whose artifact was never created). The Code view would only be
    // an empty tree, so go to the project Dashboard instead, same as clicking Home.
    if (tab === 'code' && selectedResource?.type !== 'configuration') {
      const services = Object.keys(configuration?.services || {});
      const viewedService = previewTabs[activePreviewTab];
      if ((configuration && services.length === 0) || viewedService?.isEmptyService) {
        handleServiceSelect('project-configuration');
        return;
      }
    }
    // A pinned runnable is a Preview entry only: any other tab is its owner's (its folder's code, …).
    const runnableOwner = tab !== 'preview' && selectedResource?.data?.pinnedRunnable ? selectedResource.data.owner : null;
    if (runnableOwner && configuration?.services?.[runnableOwner]) { handleServiceSelect(runnableOwner, tab); }
    setActiveTab(tab);
    
    // Update URL with tab change
    if (onSelectionChange) {
      const lineNumber = location.hash ? parseInt(location.hash.replace('#L', '')) : null;
      onSelectionChange(
        runnableOwner || selectedResource?.data?.name || params.microservice,
        selectedRevision?.name || params.branch || 'main',
        tab,
        tab === 'code' ? selectedFile?.path : null,
        tab === 'code' && lineNumber ? lineNumber : null
      );
    }
  };

  // The person's plan hides Code (the sandbox admin's Plans › tabs): a service's Code view (a deep link to
  // …/<service>/<branch>/code/<file>, a file link from the chat, Traces or Quality, the Apps list, a service opened while
  // Preview is hidden too) and Home's Files view land on the project Home's Dashboard, with the same short note as any
  // hidden tab. Also when the plan's answer arrives after the view opened. The Code view's terminal dock goes with it;
  // the Terminal tab is its own tab and stays as the plan says.
  const codeHidden = isTabHidden(planHiddenTabs, 'code');
  const gate = codeGate({ hidden: planHiddenTabs, resource: selectedResource?.type || null, activeTab, homeMode: dashboardOverviewMode });
  const gateKey = gate ? `${gate.go}:${gate.note}` : null;
  useEffect(() => {
    if (!gate) { return; }
    if (gate.go === 'home') { handleServiceSelect('project-configuration'); } else { setDashboardOverviewMode('dashboard'); }
    if (gate.note) { window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: hiddenTabNote('code'), type: 'info' } })); }
  }, [gateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deployment/pods handlers now come from usePipelineDeployment() above.

  // The top bar's Refresh: a ready preview only reloads its iframe; Shift+click or a preview that is not ready takes the
  // full re-render below (the "Taking longer than usual" notice always does).
  const handleReloadPreview = (options = {}) => {
    const serviceName = selectedResource?.data?.name;
    const tab = previewTabs.find((t) => t.serviceName === serviceName);
    if (selectedResource?.type === 'service' && canReloadInPlace(tab, options)) {
      setPreviewTabs((tabs) => tabs.map((t) => (t.serviceName === serviceName ? { ...t, iframeKey: Date.now() } : t)));
      return undefined;
    }
    return handleRefreshPreview(options);
  };

  // A full re-render via /api/render (it pulls the source from vfs-service and may restart the service).
  const handleRefreshPreview = async ({ forceClean = false } = {}) => {
    if (selectedResource?.type === 'service') {
      try {
        // eslint-disable-next-line no-console
        devLog(`🔄 Refresh clicked - forcing full re-render via /api/render${forceClean ? ' (clean install)' : ''}`);
        
        // Get all files from IndexedDB for the current service
        const allFiles = await indexedDbFileStorage.getAllFilesForPreview(
          selectedProject?.id,
          selectedResource.data?.name,
          {}
        );
        
        if (Object.keys(allFiles).length === 0) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ No files found in IndexedDB to refresh – triggering full auto-render from git');
          const activeTab = previewTabs.find(tab => tab.serviceName === selectedResource.data?.name);
          if (activeTab) {
            await autoRenderOnPreviewNotFound(activeTab, { explicit: true });
          }
          return;
        }
        
        // eslint-disable-next-line no-console
        devLog(`📤 Forcing full re-render with ${Object.keys(allFiles).length} files`);
        
        // Get routing info using centralized utility
        const activeTab = previewTabs.find(tab => tab.serviceName === selectedResource.data?.name);
        const routingInfo = getContainerRoutingInfo(
          selectedResource.data,
          allFiles,
          { activePreviewTab: activeTab, previewSessionId: activeTab?.sessionId }
        );
        
        if (!routingInfo) {
          console.error('❌ Failed to get routing info for force refresh');
          return;
        }
        
        const { isBackend: isBackendService, containerPort, containerName, sessionId } = routingInfo;
        
        // eslint-disable-next-line no-console
        devLog(`🎯 Force re-render routing ${isBackendService ? 'backend' : 'frontend'} (${containerName})`);
        
        if (!sessionId) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ No active preview session found for this service, proceeding with a fresh render anyway');
        }
        
        // Extract service name and find tab index
        const serviceName = selectedResource.data?.name;
        const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceName);

        // eslint-disable-next-line no-console
        devLog(`📡 Calling /api/render for ${containerName} (port ${containerPort}) with session: ${sessionId}`);
        
        // Extract userId for render request
        const userId = getOrCreateUserId(selectedProject?.id, serviceName);
        
        // Show loading state in preview tab
        if (tabIndex >= 0) {
          setPreviewTabs(currentTabs => {
            const updatedTabs = [...currentTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: true,
              error: null,
              renderPhase: 'starting',
              renderProgress: 0,
              renderLogs: [forceClean ? '🔄 Starting full re-render (clean install)...' : '🔄 Starting full re-render...']
            };
            return updatedTabs;
          });
        }
        
        // Force full re-render via /api/render
        const renderApiUrl = getContainerApiUrl(containerPort, '/api/render');
        const renderResponse = await getAuthorizedFetch()(renderApiUrl, {
          method: 'POST',
          headers: getBffHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            tenantId: selectedTenant?.tenantId || selectedTenant?.id,
            serviceName: serviceName,
            ...(selectedResource.data?.vfsBacked
              ? vfsClient.buildVfsSourcePayload(selectedProject?.id, serviceName)
              : { sourceCode: allFiles }),
            projectId: selectedProject?.id,
            revisionId: selectedRevision?.id,
            userId: userId,
            forceClean,
          }),
        });

        if (renderResponse.ok) {
          const renderResult = await renderResponse.json();
          if (renderResult.success && renderResult.sessionId) {
            // eslint-disable-next-line no-console
            devLog(`🎬 Re-render job started with ID: ${renderResult.sessionId}`);
            
            // Poll for completion with progress updates
            const containerBaseUrl = renderApiUrl.replace('/api/render', '');
            // BUG FIX (live-reported: after a force re-render, the tab's
            // status shows "Stopped" and the preview shows "could not be
            // loaded" even though the render genuinely succeeded):
            // pollRenderStatus's return value — which already carries the
            // real previewUrl once the build is CONFIRMED ready — used to
            // be discarded entirely here. Without it, `tab.url` was never
            // set, so the old status bar's `isRunning = !!activeTab.url`
            // read false ("Stopped") despite success, and Canvas's own
            // "tab has no url, no error, not loading" fallback showed the
            // static, non-retrying "Preview could not be loaded" box. The
            // code instead relied entirely on a blind `setTimeout(1000)`
            // a few lines down to indirectly force a reload — but
            // preview-runner's own logs show a real kill→respawn→bind
            // cycle regularly takes 2.6s+, so that fixed 1s guess raced
            // (and often lost against) the respawn, independent of this
            // url bug — both fixed by using the confirmed-ready result
            // directly instead of guessing.
            const finalRenderStatus = await pollRenderStatusShared(
              serviceName,
              containerBaseUrl,
              renderResult.sessionId,
              (phase, progress, logs) => {
                devLog(`📊 Re-render progress: ${progress}% - Phase: ${phase}`);
                
                // Update tab with progress - use functional setState to avoid stale closure
                setPreviewTabs(currentTabs => {
                  const currentTabIndex = currentTabs.findIndex(t => t.serviceName === serviceName);
                  if (currentTabIndex >= 0) {
                    const updatedTabs = [...currentTabs];
                    const existingLogs = updatedTabs[currentTabIndex].renderLogs || [];
                    
                    // Preserve cleanup logs (first 4 logs if they start with cleanup emoji)
                    const cleanupLogs = existingLogs.slice(0, 4).filter(log => 
                      typeof log === 'string' && (log.startsWith('🧹') || log.startsWith('✅') || log.startsWith('⚠️') || log.startsWith('❌'))
                    );
                    const hasCleanupLogs = cleanupLogs.length > 0 && cleanupLogs[0].includes('cleanup');
                    
                    // Combine cleanup logs with render logs
                    const combinedLogs = hasCleanupLogs 
                      ? [...cleanupLogs, ...(logs || [])]
                      : (logs || []);
                    
                    updatedTabs[currentTabIndex] = {
                      ...updatedTabs[currentTabIndex],
                      loading: true,
                      renderPhase: phase,
                      renderProgress: progress,
                      renderLogs: combinedLogs
                    };
                    return updatedTabs;
                  }
                  return currentTabs;
                });
              }
            );
            
            // eslint-disable-next-line no-console
            devLog(`✅ Successfully re-rendered ${Object.keys(allFiles).length} files for ${containerName}`);
            
            // Clear loading state and render progress
            if (tabIndex >= 0) {
              setPreviewTabs(currentTabs => {
                const updatedTabs = [...currentTabs];
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  loading: false,
                  // Clear render progress state to hide the loading bar
                  renderPhase: undefined,
                  renderProgress: undefined,
                  renderLogs: undefined,
                  iframeKey: Date.now(),
                  // The confirmed-ready URL, not a guess — see the
                  // finalRenderStatus comment above.
                  url: finalRenderStatus?.previewUrl || updatedTabs[tabIndex].url,
                };
                return updatedTabs;
              });
            }
            
            // Force refresh the iframe after re-render completes
            const updatedResource = {...selectedResource};
            setSelectedResource(null);
            setTimeout(() => setSelectedResource(updatedResource), 1000);
          } else {
            // eslint-disable-next-line no-console
            console.error('❌ Failed to start re-render:', renderResult.message);
            
            // Show error in tab
            if (tabIndex >= 0) {
              setPreviewTabs(currentTabs => {
                const updatedTabs = [...currentTabs];
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  loading: false,
                  error: renderResult.message || 'Failed to start re-render'
                };
                return updatedTabs;
              });
            }
          }
        } else {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to call /api/render:', renderResponse.status);
          
          // Show error in tab
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                loading: false,
                error: `Failed to call /api/render: ${renderResponse.status}`
              };
              return updatedTabs;
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error refreshing preview:', error);
        if (error?.name === 'PreviewStatusError') {
          const refreshedName = selectedResource.data?.name;
          setPreviewTabs(currentTabs => currentTabs.map(t => (t.serviceName === refreshedName
            ? { ...t, loading: false, error: `${PREVIEW_UNREACHABLE_PREFIX}${error.message}` }
            : t)));
          return;
        }
        // Still try to refresh the iframe
        const updatedResource = {...selectedResource};
        setSelectedResource(null);
        setTimeout(() => setSelectedResource(updatedResource), 1000);
      }
    }
  };

  return (
    <div
      className={`canvas-container ${isVisible ? 'visible' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
      // In fullscreen, leave left/width unset entirely — an inline style
      // ALWAYS wins over a CSS class rule, so setting `left: 'auto', width:
      // '100%'` here unconditionally overrode Canvas.css's `.canvas-
      // container.fullscreen` / `.app-container.sidebar-collapsed .canvas-
      // container.fullscreen` rules (which correctly compute left:72px/260px
      // from the real sidebar width) with a flat 'auto'/'100%' that ignores
      // the sidebar entirely — this is why the sidebar kept visually
      // overlapping fullscreen Code/Dashboard even after those CSS rules
      // were fixed (live-confirmed via DevTools: the rendered element's
      // inline style read `left: auto; width: 100%` regardless of sidebar
      // state). Non-fullscreen resize-beside-chat-sidebar behavior (the
      // `chatSidebarWidth`-based inline style) is untouched.
      style={isVisible && !isFullscreen ? {
        left: `${chatSidebarWidth}px`,
        width: `calc(100% - ${chatSidebarWidth}px)`
      } : undefined}
    >
        {/* Unified Top Bar */}
        <UnifiedTopBar
          activeTab={activeTab}
          tenantId={selectedTenant?.tenantId || selectedTenant?.id}
          configuration={{...configuration, environments: fullProjectConfig?.environments}}
          selectedProject={topBarSelectedProject}
          isFullscreen={isFullscreen}
          onOpenHistory={(tab = null) => setWorkspaceView(viewFromHistory(typeof tab === 'string' ? tab : null))}
          onOpenNotes={(tab) => openNotes(typeof tab === 'string' ? tab : null)}
          suggestionsPending={suggestionsPending}
          showHistoryButton={vfsClient.isVfsBackedProject(selectedProject?.id)}
          splitViewMode={splitViewMode}
          onSplitViewModeChange={(mode) => {
            setSplitViewMode(mode);
            if (mode === 'preview') {
              setActiveTab('preview');
            }
          }}
          previewTabs={previewTabs}
          pinnedPreviews={runnablePreview.entries}
          onDetails={() => {
            // Navigate to service tab to show service details
            handleTabChange('service');
          }}
          onRefresh={handleReloadPreview}
          onServiceSelect={handleServiceSelect}
          onTabChange={handleTabChange}
          showDashboardTab={showDashboardTab}
          onToggleFullscreen={handleToggleFullscreen}
          onNewConversation={onNewConversation}
          onShowHistory={onShowHistory}
          selectedResource={selectedResource}
          dashboardOverviewMode={dashboardOverviewMode}
          onDashboardOverviewModeChange={changeHomeMode}
          // The Files view at Home is about the service of the open file (or of the folder it is rooted at), never plain "Home".
          filesScope={dashboardOverviewMode === 'files'
            ? filesScopeOf({
              focus: dashboardFilesFocusPath,
              openFile: filesPathFromSearch(location.search),
              serviceKeys: Object.values(configuration?.services || {}).map((service) => service?.name).filter((name) => name && name !== '.' && name !== 'brain'),
            })
            : null}
          onFilesRoot={() => {
            // From a service's Preview/API/Code the project files are a Home-level view: leave the service first.
            if (selectedResource?.type === 'service') { handleServiceSelect('project-configuration'); }
            setDashboardFilesFocusPath(null);
            const params = new URLSearchParams(location.search);
            params.delete('path');
            params.delete('line');
            params.set('view', 'files');
            navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash }, { replace: true });
            setDashboardOverviewMode('files');
          }}
          onProjectAccessChange={setProjectAccess}
          workspace={workspaceEnabled ? {
            view: workspaceView,
            onViewChange: setWorkspaceView,
            changesCount,
            signals: tabSignals,
            panelOpen: inProjectAvailable ? inProjectOpen : null,
            onTogglePanel: inProjectAvailable ? () => setInProjectOpen(!inProjectOpen) : null,
            onOpenPanel: inProjectAvailable ? () => setInProjectOpen(true) : null,
            toolsSlotRef: setWorkspaceToolsSlot,
          } : null}
        />

        {isViewOnly && <ReadOnlyNotice ownerName={projectAccess?.owner?.name || projectAccess?.owner?.email} />}

        {/* Follow a person (collaboration): their file opens here and the editor scrolls with them. */}
        {vfsClient.isVfsBackedProject(selectedProject?.id) && (
          <FollowController onOpenFile={handleFollowOpenFile} projectId={selectedProject?.id} />
        )}

        {/* Canvas Content, and in its place a workspace view (Changes, Activity, Sessions) when one is open. Mounted even
            with none open: a Terminal tab opened once stays there, hidden, so its shells live on (WorkspaceView). */}
        {/* The project's pages that fill the height (Code, Preview, Logs) carry the same "<Project> / <Step> / <Tab>"
            line as the workspace views, without their description. */}
        {workspaceEnabled && !workspaceView && ['code', 'preview', 'logs'].includes(pageTab) && (
          <WorkspaceTitle compact projectName={topBarSelectedProject?.name || null} tab={pageTab} />
        )}
        <div className="canvas-workspace-row" ref={workspaceRowRef}>
        {workspaceEnabled && (
          <WorkspaceView
            changesCount={changesCount}
            historyMerged
            onAskAI={onAskAI}
            onChange={setWorkspaceView}
            // Traces → Open logs: the project Home's Logs view on that service, around the request's time (`at`).
            onOpenLogs={config.sandboxFirst ? (serviceName, { at } = {}) => {
              setWorkspaceView(null);
              if (selectedResource?.type === 'service') { handleServiceSelect('project-configuration'); }
              const params = new URLSearchParams(window.location.search);
              params.delete('tab');
              params.delete('trace');
              params.set('view', 'logs');
              params.set('service', serviceName);
              if (at) { params.set('at', String(Math.round(at))); } else { params.delete('at'); }
              navigate(`${location.pathname}?${params.toString()}`);
              setDashboardOverviewMode('logs');
            } : null}
            onOpenHandoff={(noteId) => openNotes('handoff', noteId)}
            onOpenSuggestion={(id) => openNotes('suggestions', id)}
            projectId={selectedProject?.id || selectedProject?._id}
            projectName={topBarSelectedProject?.name || selectedProject?.id || selectedProject?._id}
            readOnly={isViewOnly}
            serviceName={selectedResource?.data?.name || selectedResource?.name}
            view={workspaceView}
          />
        )}
        <div className="canvas-content" hidden={Boolean(workspaceEnabled && workspaceView)}>
          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="dashboard-layout">
              <DynamicTaskPanel
                selectedProject={selectedProject}
                previewTabs={previewTabs}
                configuration={configuration}
                selectedEnvironment={selectedEnvironment}
                onAction={(action, ...args) => {
                  switch (action) {
                    case 'create-project':
                      setIsCreateProjectModalOpen(true);
                      break;
                    case 'view-logs': {
                      // Jump to the Preview tab and expand the live container logs
                      // panel for the failing service (see PreviewLogs.jsx).
                      const failedTab = args[0];
                      if (failedTab) {
                        const idx = previewTabs.findIndex(t => t.serviceName === failedTab.serviceName);
                        if (idx >= 0) setActivePreviewTab(idx);
                      }
                      setLogsExpanded(true);
                      handleTabChange('preview');
                      break;
                    }
                    case 'start-service':
                      handleTabChange('preview');
                      break;
                    case 'deploy':
                      setShowDeploymentModal(true);
                      break;
                    case 'add-service':
                      handleTabChange('code');
                      break;
                    case 'add-endpoint':
                      handleTabChange('code');
                      break;
                    default:
                      break;
                  }
                }}
                isVisible={true}
              />
              <UnifiedDashboard
                selectedProject={selectedProject}
                selectedEnvironment={selectedEnvironment}
                previewTabs={previewTabs}
                activePreviewTab={activePreviewTab}
                configuration={configuration}
                onAction={(action, ...args) => {
                  switch (action) {
                    case 'settings':
                      handleTabChange('summary');
                      break;
                    case 'view-preview':
                      setActivePreviewTab(args[0]);
                      handleTabChange('preview');
                      break;
                    case 'open-tab':
                      window.open(args[0], '_blank', 'noopener,noreferrer');
                      break;
                    case 'restart':
                      controlContainer(args[0], args[1], 'restart');
                      break;
                    case 'start-service':
                      handleTabChange('preview');
                      break;
                    case 'deploy':
                      setShowDeploymentModal(true);
                      break;
                    case 'add-service':
                      handleTabChange('code');
                      break;
                    case 'view-logs':
                      setLogsExpanded(true);
                      handleTabChange('preview');
                      break;
                    default:
                      break;
                  }
                }}
              />
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === 'preview' && (
            <>
              {/* Configuration Markdown Preview - Show first if configuration is selected; a brand-new vfs project (no
                  service, so nothing is selected) is on its Home too: "Start by…". */}
              {selectedResource?.type === 'configuration' || brandNewWorkspace ? (
                <div className="canvas-dashboard-overview" style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
                  {/* task #146/hierarchy pass: the Dashboard/Files switch that
                      used to float here now lives in UnifiedTopBar itself
                      (replacing the Preview/Code pill while at Home level,
                      see its `dashboardOverviewMode` prop) — this is just the
                      content that mode selects. */}
                  <div style={{flex: 1, minHeight: 0}}>
                    {dashboardOverviewMode === 'logs' && selectedProject?.id ? (
                      <ServiceLogs
                        around={Number(new URLSearchParams(location.search).get('at')) || null}
                        initialService={new URLSearchParams(location.search).get('service') || logsService}
                        onServiceChange={(name) => {
                          const params = new URLSearchParams(location.search);
                          params.set('service', name);
                          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                        }}
                        projectId={selectedProject.id}
                        serviceNames={Object.keys(configuration?.services || {}).map(servicePath)}
                      />
                    ) : dashboardOverviewMode === 'files' && selectedProject?.id ? (
                      // Reuse the SAME tree+editor Code mode's own "Code" tab
                      // uses (line ~7891 below) instead of a separately
                      // re-styled lookalike (`ProjectFilesBrowser`, retired) —
                      // explicit, repeated product ask. `serviceName` here is
                      // whatever top-level folder was clicked into (a
                      // Services row, a Brain/folder card's "Browse files"
                      // link — see DashboardView's onOpenFiles), or the
                      // literal project root ('.') for the bare Files toggle
                      // with nothing specific selected — `vfsClient`'s
                      // `fetchServiceFiles`/`isServiceEmpty` already treat any
                      // of these as a plain path prefix, no special-casing
                      // needed. `vfsBacked: true` + no `repositoryUrl` routes
                      // RepositoryTree's own Push flow through vfs-service
                      // directly (today's new vfs-direct save path) instead
                      // of the external-GitHub/GitLab-only push it otherwise
                      // assumes — real persistence, not a cosmetic swap.
                      <CodeViewWithLogger
                        projectId={selectedProject.id}
                        serviceName={dashboardFilesFocusPath || '.'}
                        autoOpenPath={pathWithinFocus(dashboardFilesFocusPath, filesPathFromSearch(location.search))}
                        autoOpenLine={filesLineFromSearch(location.search)}
                        repositoryUrl={null}
                        branch="main"
                        onFileSelect={(filePath, file) => {
                          handleFileSelect(filePath, file);
                          // The picked file goes into the URL (`&path=…`, full project path), so a reload or a shared link opens it again.
                          const picked = typeof filePath === 'string' ? filePath : filePath?.path;
                          if (!picked) { return; }
                          const params = new URLSearchParams(location.search);
                          const full = fullFilesPath(dashboardFilesFocusPath, picked);
                          if (params.get('path') === full) { return; }
                          params.set('view', 'files');
                          params.set('path', full);
                          params.delete('line');
                          navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash }, { replace: true });
                        }}
                        onFileChange={(filePath) => devLog('Dashboard/Files file changed:', filePath)}
                        selectedProject={selectedProject}
                        selectedRevision={selectedRevision}
                        selectedTenant={selectedTenant}
                        selectedResource={{type: 'service', data: {name: dashboardFilesFocusPath || '.', vfsBacked: true}}}
                        configuration={configuration}
                        collections={collections}
                        onProjectContextChange={onProjectContextChange}
                        previewUrl={previewUrl}
                        setPreviewUrl={setPreviewUrl}
                        setPreviewError={setPreviewError}
                        setForceGitFetch={setForceGitFetch}
                        autoTriggeredPullRef={autoTriggeredPullRef}
                        onSwitchToChat={handleSwitchToChat}
                        // Browsing a synthetic project-root/brain "service"
                        // here, not a real routable Code-mode service — must
                        // never overwrite the page's actual URL identity
                        // (Dashboard's Project Configuration pseudo-resource
                        // route) on file select. See RepositoryTree.jsx's
                        // updateUrlWithFile comment.
                        syncUrlOnFileSelect={false}
                        readOnly={isViewOnly}
                      />
                    ) : (
                      <DashboardView
                        selectedProject={memoizedSelectedProject}
                        configuration={configuration}
                        vfsMetadata={vfsProjectMetadata}
                        tenant={selectedTenant}
                        onResourceSelect={handleResourceClick}
                        onProjectDeleted={(deletedProjectId) => {
                          setProjects(prev => prev.filter(p => p.id !== deletedProjectId));
                          setSelectedProject(null);
                          if (onNavigateHome) onNavigateHome();
                        }}
                        onProjectUpdated={(updated) => {
                          setSelectedProject((prev) => (prev ? { ...prev, ...updated } : prev));
                          setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
                        }}
                        onVfsMetadataUpdated={setVfsProjectMetadata}
                        // Start all and Open app go to the header's tab row while it shows the Home (desktop).
                        toolsSlot={workspaceEnabled ? workspaceToolsSlot : null}
                        runToolsInHeader={Boolean(workspaceEnabled && config.sandboxFirst)}
                        // An unread handoff note, pinned in What changed: Read note opens the project's notes on it.
                        onOpenHandoff={(noteId) => openNotes('handoff', noteId)}
                        onOpenSuggestion={(id) => openNotes('suggestions', id)}
                        // A folder or zip imported into a brand-new project: its services show at once.
                        onServicesChanged={() => { if (selectedProject?.id && selectedRevision?.id) { loadConfiguration(selectedProject.id, selectedRevision.id); } }}
                        onOpenLogs={config.sandboxFirst ? (serviceName) => {
                          const params = new URLSearchParams(location.search);
                          params.set('view', 'logs');
                          params.set('service', serviceName);
                          navigate(`${location.pathname}?${params.toString()}`);
                          setDashboardOverviewMode('logs');
                        } : null}
                        onOpenFiles={(path) => {
                          setDashboardFilesFocusPath(path || null);
                          setDashboardOverviewMode('files');
                        }}
                        // Apps: a service's name opens its Code view here (its link keeps the URL for a new tab); its
                        // Preview where the plan hides Code (nothing in place, the link then lands on Home, with Preview
                        // hidden too).
                        onOpenCode={!codeHidden || !isTabHidden(planHiddenTabs, 'preview') ? (name, service) => {
                          setWorkspaceView(null);
                          setSelectedResource({type: 'service', data: service, name});
                          setActiveTab(codeHidden ? 'preview' : 'code');
                        } : null}
                        // "Continue in chat": the project's latest conversation in the chat panel.
                        onContinueInChat={(conversationId) => {
                          const params = new URLSearchParams(location.search);
                          if (!conversationId || params.get('conversationId') === conversationId) { return; }
                          params.set('conversationId', conversationId);
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : !selectedResource ? (
                // Nothing selected yet — every branch below this one assumes
                // `selectedResource` is set, so this used to fall all the way
                // through to the generic markdown-fallback branch with empty
                // `content`, rendering as a genuinely blank panel with no
                // spinner, no message, nothing. Two real sub-cases, but
                // BOTH must show *something* rather than risk a third,
                // unaccounted-for blank state: configuration/revisions still
                // in flight (loadRevisions -> selects a revision ->
                // loadConfiguration is a two-step round trip), or a real,
                // confirmed-empty services list (a vfs-only project with no
                // services yet — real and working via VfsChatPanel, which
                // needs no microservice at all).
                // The placeholder put in while another project opens is not "no services": that is still loading.
                configuration && !configuration.placeholder && Object.keys(configuration.services || {}).length === 0 ? (
                  <div className="canvas-no-services-empty-state">
                    <Icon name="folder" size={32} />
                    <h3>No services yet</h3>
                    <p>
                      Chat with the agent to build something directly in this
                      project, or add a service from the Code tab once you have
                      files to run.
                    </p>
                  </div>
                ) : (
                  // Opening a project whose sandbox sleeps: "Waking your sandbox…" with its phase from the first moment
                  // (watched at once), then the project loads again once it is up.
                  <ProjectLoading
                    onAwake={reloadProjectAfterWake}
                    sandbox={Boolean(config.sandboxFirst || (selectedProject?.id && vfsClient.isVfsBackedProject(selectedProject.id)))}
                  />
                )
              ) : isShowingLivePreview ? (
                <div
                  className={`canvas-frontend-preview ${previewFullscreen ? 'preview-fullscreen' : ''}`}
                  ref={previewContainerRef}
                >
                  {/* Multi-tab preview rendering */}
                  {previewTabs.length > 0 ? (
                    <>
                      {previewTabs.map((tab, index) => {
                        const isActive = activePreviewTab === index;
                        return (
                          <div 
                            key={tab.serviceName}
                            style={{ display: isActive ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
                          >
                            {tab.cli ? (
                              // A command-line tool: Try it (utils/cliServices.js), not a page.
                              <CliTryIt
                                canRun={canRunServices}
                                configured={tab.cli.configured !== false}
                                onOpenHowItRuns={() => window.dispatchEvent(new CustomEvent('collab:openHistory', { detail: { tab: 'apps', focus: { service: tab.serviceName } } }))}
                                onOpenTerminal={() => window.dispatchEvent(new CustomEvent('collab:openHistory', { detail: { tab: 'terminal', focus: { service: tab.serviceName } } }))}
                                projectId={selectedProject?.id}
                                serviceName={tab.serviceName}
                                tool={tab.cli.tool || tab.serviceName.replace(/^services\//, '')}
                              />
                            ) : (tab.stopped || tab.starting) && !tab.loading && !tab.error ? (
                              <PreviewStoppedState
                                canRun={canRunServices}
                                onStart={() => handleStartStoppedService(tab.serviceName)}
                                serviceName={tab.displayName || tab.serviceName}
                                startError={tab.startError || null}
                                starting={Boolean(tab.starting)}
                              />
                            ) : tab.isEmptyService && !tab.loading ? (
                              // A service with no files yet isn't a build failure — it just
                              // hasn't been coded. No warning icon, no Retry/Fix-with-AI: those
                              // imply something broke, which nothing did. See isEmptyServiceError.
                              <div className="preview-empty-service-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '12px', textAlign: 'center', padding: '24px' }}>
                                <Icon name="folder" size={32} />
                                <h3 style={{ margin: 0 }}>{tab.serviceName} is empty</h3>
                                <p style={{ margin: 0, maxWidth: '360px', opacity: 0.8 }}>
                                  This service has no files yet. Start a conversation in the chat to build it.
                                </p>
                                <button
                                  style={{ padding: '8px 20px', background: '#007acc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                                  onClick={handleSwitchToChat}
                                  type="button"
                                >
                                  {'Go to Chat'}
                                </button>
                              </div>
                            ) : tab.error ? (
                              (() => {
                                if (tab.error.startsWith(PREVIEW_UNREACHABLE_PREFIX)) {
                                  // The status check was redirected or refused: Flow's session, not the app's code.
                                  return (
                                    <div className="preview-build-error preview-unreachable" role="alert">
                                      <span className="preview-build-error-icon">
                                        <Icon className="warning" name="warning" size={28} />
                                      </span>
                                      <h3 className="preview-build-error-title">{'Could not reach the preview'}</h3>
                                      <p className="preview-build-error-hint">
                                        {tab.error.slice(PREVIEW_UNREACHABLE_PREFIX.length)}
                                        {' Retry, or reload the page to sign in again.'}
                                      </p>
                                      <div className="preview-error-actions">
                                        <button
                                          className="preview-error-btn preview-error-btn-retry"
                                          onClick={() => autoRenderOnPreviewNotFound(tab, { explicit: true })}
                                          type="button"
                                        >
                                          <Icon name="refresh" size={14} glow={false} /> Retry
                                        </button>
                                        <button
                                          className="preview-error-btn"
                                          onClick={() => window.location.reload()}
                                          type="button"
                                        >
                                          {'Reload page'}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }
                                if (tab.error.startsWith('__GIT_TOOL_DISABLED__:') || tab.error.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                                  const isDisabled = tab.error.startsWith('__GIT_TOOL_DISABLED__:');
                                  const pName = tab.error.split(':')[1];
                                  const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                                  return (
                                    <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                                      <div className="preview-git-blocked-icon">
                                        <Icon name={pIconName} size={48} glow={true} />
                                      </div>
                                      <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Account Not Connected`}</h3>
                                      <p>{isDisabled
                                        ? `The ${pName} integration is disabled. Enable it in Tools to load this preview.`
                                        : `Your ${pName} account is not connected. Connect it to load this preview.`}
                                      </p>
                                      <div className="preview-git-blocked-steps">
                                        {isDisabled ? (<>
                                          <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                          <div className="preview-blocked-step"><span>2</span><span>Enable <strong>{pName}</strong></span></div>
                                          <div className="preview-blocked-step"><span>3</span><span>Reload this page</span></div>
                                        </>) : (<>
                                          <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                          <div className="preview-blocked-step"><span>2</span><span>Connect your <strong>{pName}</strong> account</span></div>
                                          <div className="preview-blocked-step"><span>3</span><span>Come back — preview will load automatically</span></div>
                                        </>)}
                                      </div>
                                      <button
                                        className="preview-git-blocked-btn"
                                        onClick={() => { window.location.href = '/tools'; }}
                                        type="button"
                                      >
                                        {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                                      </button>
                                    </div>
                                  );
                                }
                                // BUG FIX (live-reported: the raw, multi-line build/
                                // install error — often a full stack trace with absolute
                                // file paths — was dumped as one centered <p>, reading as
                                // an unstyled wall of orange text with no visual hierarchy
                                // and no indication of what to actually DO about it beyond
                                // the two small buttons at the very bottom). Redesigned:
                                // a plain-language summary up top (with an explicit pointer
                                // to chat, not just an icon-button someone has to notice),
                                // the raw detail demoted into a scrollable, monospace,
                                // left-aligned code block instead of centered prose.
                                return (
                                  <div className="preview-build-error">
                                    <span className="preview-build-error-icon">
                                      <Icon className="warning" name="warning" size={28} />
                                    </span>
                                    <h3 className="preview-build-error-title">{isRunnableTab(tab) ? `${tab.displayName || tab.serviceName} failed to start` : 'Build failed'}</h3>
                                    <p className="preview-build-error-hint">
                                      {isRunnableTab(tab) ? 'This stack failed to start. Ask the AI in chat to fix it — click ' : 'This service failed to build. Ask the AI in chat to fix it — click '}
                                      <strong>{'Fix with AI'}</strong>
                                      {' below to send this error there directly — or try '}
                                      <strong>{'Retry'}</strong>
                                      {' if this looks like a transient failure.'}
                                    </p>
                                    <pre className="preview-build-error-detail"><code>{tab.error}</code></pre>
                                    <div className="preview-error-actions">
                                      <button
                                        className="preview-error-btn preview-error-btn-retry"
                                        onClick={() => autoRenderOnPreviewNotFound(tab, { explicit: true })}
                                        type="button"
                                      >
                                        <Icon name="refresh" size={14} glow={false} /> Retry
                                      </button>
                                      {onAskAI && (
                                        <button
                                          className="preview-error-btn preview-error-btn-ai"
                                          onClick={() => onAskAI(
                                            `The preview for "${tab.serviceName}" failed to load with this error:\n\n${tab.error}\n\nPlease investigate and fix it.`
                                          )}
                                          type="button"
                                        >
                                          {'🤖 Fix with AI'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()
                            ) : tab.url ? (
                              <>
                                {/* Loading overlay - shows on top of iframe while loading */}
                                {tab.loading && !tab.iframeLoaded && (
                                  <div className="preview-loading-overlay" style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0, 0, 0, 0.95)',
                                    zIndex: 1000,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <div className="loading-spinner" />
                                    <p style={{ fontSize: '16px', fontWeight: '500', marginTop: '16px' }}>
                                      {tab.pullingCode 
                                        ? `📦 Pulling code from Git for ${tab.serviceName}...` 
                                        : tab.renderPhase === 'installing'
                                          ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                          : `Rendering ${tab.serviceName} preview...`
                                      }
                                    </p>
                                    <PreviewRenderProgress phase={tab.renderPhase} progress={tab.renderProgress} logs={tab.renderLogs} />
                                    {tab.slowStart && (
                                      <div className="preview-slow-start" role="status">
                                        <span>{'Taking longer than usual'}</span>
                                        <span aria-hidden="true">{' · '}</span>
                                        <button className="preview-slow-start-btn" onClick={() => handleRefreshPreview()} type="button">{'Refresh'}</button>
                                        <span aria-hidden="true">{' · '}</span>
                                        <button className="preview-slow-start-btn" onClick={() => setLogsExpanded(true)} type="button">{'Logs'}</button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Navbar and iframe - always rendered when url exists */}
                                <div className="preview-navbar">
                                  <button
                                    className="nav-btn"
                                    onClick={() => {
                                      const updatedTabs = [...previewTabs];
                                      updatedTabs[index] = {...tab, iframeKey: Date.now()};
                                      setPreviewTabs(updatedTabs);
                                    }}
                                    title="Force Refresh"
                                    type="button"
                                  >
                                    <Icon name="refresh" size={12} glow={false} />
                                  </button>
                                  {editingTabUrl === tab.serviceName ? (
                                    <form
                                      className="url-form"
                                      onSubmit={(e) => {
                                        e.preventDefault();
                                        const baseUrl = getBaseUrl(tab.url);
                                        const pathInput = tabUrlInputs[tab.serviceName] || '/';
                                        const newUrl = constructFullUrl(baseUrl, pathInput);
                                        const updatedTabs = [...previewTabs];
                                        updatedTabs[index] = {...tab, url: newUrl, iframeKey: Date.now()};
                                        setPreviewTabs(updatedTabs);
                                        setEditingTabUrl(null);
                                      }}
                                    >
                                      <input
                                        className="url-input"
                                        type="text"
                                        value={tabUrlInputs[tab.serviceName] !== undefined ? tabUrlInputs[tab.serviceName] : getUrlPath(tab.url)}
                                        onChange={(e) => setTabUrlInputs({...tabUrlInputs, [tab.serviceName]: e.target.value})}
                                        onBlur={() => setEditingTabUrl(null)}
                                        placeholder="/path"
                                        autoFocus
                                      />
                                      <button type="submit" className="nav-btn" title="Go">
                                        <Icon name="arrow-right" size={12} glow={false} />
                                      </button>
                                    </form>
                                  ) : (
                                    <div
                                      className="url-display"
                                      onClick={() => {
                                        setTabUrlInputs({...tabUrlInputs, [tab.serviceName]: getUrlPath(tab.url)});
                                        setEditingTabUrl(tab.serviceName);
                                      }}
                                      title="Click to edit URL path"
                                    >
                                      <Icon name="compass" size={11} glow={false} />
                                      <span className="url-text">{getUrlPath(tab.url)}</span>
                                    </div>
                                  )}
                                  <button
                                    className="nav-btn"
                                    onClick={() => window.open(tab.url, '_blank')}
                                    title="Open in new tab"
                                    type="button"
                                  >
                                    <Icon name="link" size={12} glow={false} />
                                  </button>
                                  {/* An Expo service: a link and QR code for Expo Go on a phone (editors; preview/ExpoGo.jsx). */}
                                  {!isViewOnly && (
                                    <ExpoGoButton framework={tab.framework} language={tab.language} projectId={tab.projectId || selectedProject?.id} service={tab.serviceName} />
                                  )}
                                </div>
                                {/* Hide iframe when loading/re-rendering to avoid showing error messages */}
                                {tab.containerPort === 3022 ? (
                                  /* Helm Chart Container — show inline validation panel instead of iframe */
                                  <HelmPreviewPanel
                                    enabled={isActive}
                                    sessionId={tab.sessionId}
                                    projectId={tab.projectId || selectedProject?.id}
                                    serviceName={tab.serviceName}
                                    containerPort={3022}
                                    onRunTest={async () => {
                                      const renderApiUrl = getContainerApiUrl(3022, '/api/render');
                                      const projectId = tab.projectId || selectedProject?.id;
                                      const userId = getOrCreateUserId(projectId, tab.serviceName);
                                      // getAllFiles returns an array of {filePath, content, ...} records
                                      const rawRecords = await indexedDbFileStorage.getAllFiles(projectId, tab.serviceName);
                                      if (!rawRecords || rawRecords.length === 0) return;
                                      // Convert array → {filePath: content} map, sanitize objects to strings
                                      const files = {};
                                      for (const record of rawRecords) {
                                        const p = record.filePath || record.path;
                                        const c = record.content;
                                        if (p) files[p] = typeof c === 'string' ? c : JSON.stringify(c, null, 2);
                                      }
                                      if (Object.keys(files).length === 0) return;
                                      await getAuthorizedFetch()(renderApiUrl, {
                                        method: 'POST',
                                        headers: getBffHeaders({'Content-Type': 'application/json'}),
                                        body: JSON.stringify({
                                          serviceName: tab.serviceName,
                                          sourceCode: files,
                                          projectId,
                                          userId,
                                        }),
                                      });
                                    }}
                                  />
                                ) : (
                                  <>
                                    {!tab.loading && (
                                      <iframe
                                        className="frontend-preview-iframe"
                                        key={tab.iframeKey}
                                        ref={isActive ? iframeRef : null}
                                        sandbox="allow-scripts allow-same-origin allow-forms"
                                        src={tab.url}
                                        title={`${tab.serviceName} Preview`}
                                        onLoad={(e) => handleIframeLoad(e, tab)}
                                      />
                                    )}
                                    {/* Show loading overlay when re-rendering */}
                                    {tab.loading && (
                                      <div className="preview-loading">
                                        <div className="loading-spinner" />
                                        <p>{tab.pullingCode 
                                          ? `Pulling code from repository for ${tab.serviceName}...` 
                                          : tab.renderPhase === 'installing'
                                            ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                            : `Rendering ${tab.serviceName} preview...`
                                        }</p>
                                        <PreviewRenderProgress phase={tab.renderPhase} progress={tab.renderProgress} logs={tab.renderLogs} />
                                      </div>
                                    )}
                                    {/* Preview Logs Component — respects the user's own
                                        logsExpanded preference on every (re-)load/refresh,
                                        including a manual Refresh click. Used to force itself
                                        open while `tab.loading` was true so build stdout/stderr
                                        was visible live during a build — but that meant a plain
                                        refresh (e.g. after the preview already auto-refreshed
                                        post-edit, task #160) popped the closed logs panel back
                                        open every time, which the user explicitly doesn't want.
                                        A real build ERROR still surfaces via the separate Build
                                        Failed modal (onBuildError below), independent of this. */}
                                    <PreviewLogs
                                      enabled={isActive}
                                      position="bottom"
                                      sessionId={tab.sessionId}
                                      containerPort={tab.containerPort}
                                      isExpanded={logsExpanded}
                                      onToggleExpanded={() => setLogsExpanded(prev => !prev)}
                                      onBuildError={(errorText) => {
                                        // Use a Canvas-level key so the guard survives PreviewLogs
                                        // unmount/remount (e.g. when the user switches tabs).
                                        const shownKey = `${tab.serviceName}:${tab.sessionId || 'no-session'}`;
                                        if (buildErrorShownRef.current.has(shownKey)) return;
                                        buildErrorShownRef.current.add(shownKey);
                                        setPendingFixError({ error: errorText, serviceName: tab.serviceName });
                                      }}
                                      hasPendingError={pendingFixError?.serviceName === tab.serviceName}
                                      pendingErrorText={pendingFixError?.serviceName === tab.serviceName ? pendingFixError.error : null}
                                      onPendingErrorConsumed={() => setPendingFixError(null)}
                                      // Don't force this open during tab.loading anymore — the
                                      // LogViewer above (tab.renderLogs) already shows the same
                                      // build-progress stream, so forcing both open at once
                                      // duplicated/overlapped the same logs on screen. Hidden (still
                                      // mounted, so build errors are still caught) while loading.
                                      hideUI={!logsExpanded || tab.loading}
                                      onAskAI={(payload) => {
                                        window.dispatchEvent(new CustomEvent('askAIFromLogs', {
                                          detail: payload
                                        }));
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            ) : tab.loading ? (
                              <div className="preview-loading preview-loading-with-logs">
                                <div className="loading-spinner" />
                                <p>{tab.pullingCode
                                  ? `Pulling code from repository for ${tab.serviceName}...`
                                  : tab.renderPhase === 'installing'
                                    ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                    : `Rendering ${tab.serviceName} preview...`
                                }</p>
                                <PreviewRenderProgress phase={tab.renderPhase} progress={tab.renderProgress} logs={tab.renderLogs} />
                                {tab.slowStart && (
                                  <div className="preview-slow-start" role="status">
                                    <span>{'Taking longer than usual'}</span>
                                    <span aria-hidden="true">{' · '}</span>
                                    <button className="preview-slow-start-btn" onClick={() => handleRefreshPreview()} type="button">{'Refresh'}</button>
                                    <span aria-hidden="true">{' · '}</span>
                                    <button className="preview-slow-start-btn" onClick={() => setLogsExpanded(true)} type="button">{'Logs'}</button>
                                  </div>
                                )}
                                {/* No second PreviewLogs panel here: PreviewRenderProgress above
                                    already shows the build's log stream, and a floating Preview Logs
                                    panel under it repeated the same lines. */}
                              </div>
                            ) : (
                              /* Fallback: tab has no url, no error and is not loading.
                                 Open the modal so the user gets feedback instead of a white screen. */
                              <div className="preview-error" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '16px' }}>
                                <span className="error-icon"><Icon className="warning" name="warning" size={32} /></span>
                                <p style={{ margin: 0 }}>{'Preview could not be loaded.'}</p>
                                <button
                                  style={{ padding: '8px 20px', background: '#007acc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                                  onClick={() => setBuildErrorModal({ isOpen: true, error: 'The preview could not be started. This may be caused by a build error, a missing service, or a configuration issue. Please check your code and try again.', serviceName: tab.serviceName })}
                                  type="button"
                                >
                                  {'View Details'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : previewIsEmptyService ? (
                    // Mirrors the multi-tab isEmptyService branch above — a
                    // service with no files yet isn't a build failure, so no
                    // warning icon / Retry / Fix-with-AI here either.
                    <div className="preview-empty-service-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '12px', textAlign: 'center', padding: '24px' }}>
                      <Icon name="folder" size={32} />
                      <h3 style={{ margin: 0 }}>Empty project</h3>
                      <p style={{ margin: 0, maxWidth: '360px', opacity: 0.8 }}>
                        This project has no files yet. Start a conversation in the chat to build it.
                      </p>
                      <button
                        style={{ padding: '8px 20px', background: '#007acc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                        onClick={handleSwitchToChat}
                        type="button"
                      >
                        {'Go to Chat'}
                      </button>
                    </div>
                  ) : previewLoading ? (
                    <div className="preview-loading preview-loading-with-logs">
                      <div className="loading-spinner" />
                      <p>{'Loading frontend preview...'}</p>
                      {/* Live container logs — reuses the same SSE stream as the loaded-preview
                          view (PreviewLogs.jsx) so the user sees real container stdout/stderr
                          while the container is starting, not just a bare spinner. Forced
                          expanded here regardless of the user's logsExpanded preference: during
                          loading this is the only useful information on screen. */}
                      {previewSessionId && (
                        <PreviewLogs
                          enabled={true}
                          position="inline"
                          sessionId={previewSessionId}
                          containerPort={previewContainerPort}
                          isExpanded={true}
                          onToggleExpanded={() => {}}
                          hideUI={false}
                          hasPendingError={Boolean(pendingFixError)}
                          pendingErrorText={pendingFixError?.error}
                          onPendingErrorConsumed={() => setPendingFixError(null)}
                          onAskAI={(payload) => {
                            window.dispatchEvent(new CustomEvent('askAIFromLogs', {
                              detail: payload
                            }));
                          }}
                        />
                      )}
                    </div>
                  ) : compilationStatus ? (
                    <div className="preview-loading" style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000, background: 'rgba(0,0,0,0.8)', padding: '20px'}}>
                      <div className="loading-spinner" />
                      <p>
                        {compilationStatus === 'syncing' && 'Syncing files to preview...'}
                        {compilationStatus === 'compiling' && 'Compiling your changes...'}
                        {compilationStatus === 'ready' && '✓ Compilation complete!'}
                      </p>
                    </div>
                  ) : previewError ? (
                    (() => {
                      if (previewError.startsWith('__GIT_TOOL_DISABLED__:') || previewError.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                        const isDisabled = previewError.startsWith('__GIT_TOOL_DISABLED__:');
                        const pName = previewError.split(':')[1];
                        const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                        return (
                          <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                            <div className="preview-git-blocked-icon">
                              <Icon name={pIconName} size={48} glow={true} />
                            </div>
                            <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Account Not Connected`}</h3>
                            <p>{isDisabled
                              ? `The ${pName} integration is disabled. Enable it in Tools to load this preview.`
                              : `Your ${pName} account is not connected. Connect it to load this preview.`}
                            </p>
                            <div className="preview-git-blocked-steps">
                              {isDisabled ? (<>
                                <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                <div className="preview-blocked-step"><span>2</span><span>Enable <strong>{pName}</strong></span></div>
                                <div className="preview-blocked-step"><span>3</span><span>Reload this page</span></div>
                              </>) : (<>
                                <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                <div className="preview-blocked-step"><span>2</span><span>Connect your <strong>{pName}</strong> account</span></div>
                                <div className="preview-blocked-step"><span>3</span><span>Come back — preview will load automatically</span></div>
                              </>)}
                            </div>
                            <button
                              className="preview-git-blocked-btn"
                              onClick={() => { window.location.href = '/tools'; }}
                              type="button"
                            >
                              {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="preview-error">
                          <span className="error-icon"><Icon className="warning" name="warning" size={24} /></span>
                          <p>{previewError}</p>
                        </div>
                      );
                    })()
                  ) : previewUrl ? (
                    <>
                      <div className="preview-navbar">
                        <button
                          className="nav-btn"
                          onClick={() => setIframeKey(Date.now())}
                          title="Force Refresh"
                          type="button"
                        >
                          <Icon name="refresh" size={12} glow={false} />
                        </button>
                        {isEditingPreviewUrl ? (
                          <form
                            className="url-form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const baseUrl = getBaseUrl(previewUrl);
                              const newFullUrl = constructFullUrl(baseUrl, previewUrlInput);
                              setPreviewUrlInput(newFullUrl);
                              setIsEditingPreviewUrl(false);
                              setIframeKey(Date.now());
                            }}
                          >
                            <input
                              className="url-input"
                              type="text"
                              value={previewUrlInput !== '' ? previewUrlInput : getUrlPath(previewUrl)}
                              onChange={(e) => setPreviewUrlInput(e.target.value)}
                              onBlur={() => setIsEditingPreviewUrl(false)}
                              placeholder="/path"
                              autoFocus
                            />
                            <button type="submit" className="nav-btn" title="Go">
                              <Icon name="arrow-right" size={12} glow={false} />
                            </button>
                          </form>
                        ) : (
                          <div
                            className="url-display"
                            onClick={() => {
                              setPreviewUrlInput(getUrlPath(previewUrlInput || previewUrl));
                              setIsEditingPreviewUrl(true);
                            }}
                            title="Click to edit URL path"
                          >
                            <Icon name="compass" size={11} glow={false} />
                            <span className="url-text">{getUrlPath(previewUrlInput || previewUrl)}</span>
                          </div>
                        )}
                        <button
                          className="nav-btn"
                          onClick={() => window.open(previewUrlInput || previewUrl, '_blank')}
                          title="Open in new tab"
                          type="button"
                        >
                          <Icon name="link" size={12} glow={false} />
                        </button>
                      </div>
                      <iframe
                        className="frontend-preview-iframe"
                        key={iframeKey}
                        ref={iframeRef}
                        sandbox="allow-scripts allow-same-origin allow-forms"
                        src={previewUrlInput || previewUrl}
                        title="Frontend Preview"
                        onLoad={(e) => handleIframeLoad(e, {serviceName: selectedResource?.data?.name, url: previewUrl, service: selectedResource?.data})}
                      />                     
                      {/* Preview Logs Component */}
                      <PreviewLogs
                        enabled={true}
                        position="bottom"
                        sessionId={previewSessionId}
                        containerPort={previewContainerPort}
                        isExpanded={logsExpanded}
                        onToggleExpanded={() => setLogsExpanded(prev => !prev)}
                        onBuildError={(errorText) => {
                          const shownKey = `${selectedResource?.data?.name || 'preview'}:${previewSessionId || 'no-session'}`;
                          if (buildErrorShownRef.current.has(shownKey)) return;
                          buildErrorShownRef.current.add(shownKey);
                          setPendingFixError({
                            error: errorText,
                            serviceName: selectedResource?.data?.name || selectedResource?.name || 'Preview',
                          });
                        }}
                        hasPendingError={Boolean(pendingFixError)}
                        pendingErrorText={pendingFixError?.error}
                        onPendingErrorConsumed={() => setPendingFixError(null)}
                        hideUI={!logsExpanded}
                        onAskAI={(payload) => {
                          window.dispatchEvent(new CustomEvent('askAIFromLogs', {
                            detail: payload
                          }));
                        }}
                      />
                    </>
                  ) : (
                    <div className="preview-placeholder">
                      <span className="placeholder-icon"><Icon name="design" size={48} /></span>
                      <p>{'Select a frontend or backend service to preview'}</p>
                    </div>
                  )}
                </div>
              ) : contentType === 'image' ? (
                <div className="canvas-image-container">
                  <div className="canvas-image-wrapper">
                    <img 
                      alt={content.title || 'Generated image'}
                      className="canvas-image"
                      src={content.content}
                    />
                  </div>
                  {content.prompt && (
                    <div className="canvas-image-prompt">
                      <strong>{'Prompt: '}</strong>
                      {content.prompt}
                    </div>
                  )}
                </div>
              ) : contentType === 'video' ? (
                <div className="canvas-video-container">
                  <div className="canvas-video-wrapper">
                    <video 
                      className="canvas-video"
                      controls
                      src={content.content}
                    >
                      {'Your browser does not support the video tag.'}
                    </video>
                  </div>
                  {content.prompt && (
                    <div className="canvas-video-prompt">
                      <strong>{'Prompt: '}</strong>
                      {content.prompt}
                    </div>
                  )}
                </div>
              ) : detectedLanguage === 'html' ? (
                <iframe
                  className="html-preview-iframe"
                  sandbox="allow-scripts allow-same-origin"
                  srcDoc={extractedCode || content.content}
                  style={{width: '100%', height: '100%', border: 'none'}}
                  title="HTML Preview"
                />
              ) : (
                <div className="canvas-document">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({node, inline, className, children, ...props}) {
                        const match = (className || '').match(/language-mermaid/);
                        const chartCode = String(children).replace(/\n$/, '');
                        return !inline && match ? (
                          <React.Suspense fallback={<pre><code>{chartCode}</code></pre>}><Mermaid chart={chartCode} /></React.Suspense>
                        ) : (
                          <code className={className} {...props}>{children}</code>
                        );
                      }
                    }}
                  >
                    {content.content}
                  </ReactMarkdown>
                </div>
              )}
            </>
          )}

          {/* CODE TAB - Source Code with IndexedDB and Git Integration */}
          {activeTab === 'code' && (
            <div className={`canvas-code-view ${splitViewMode === 'split' ? 'split-view-mode' : ''}`}>
              {/* Code Layout with Split View Support */}
              <div className={`code-layout ${splitViewMode === 'split' ? 'split-layout' : ''}`}>
                {/* Source Code View with Logger */}
                <div 
                  className="code-content-area" 
                  style={splitViewMode === 'split' ? {width: `${splitRatio}%`} : {}}
                >
                  {(selectedResource?.type === 'service' || selectedResource?.type === 'configuration') && (repositoryUrl || selectedResource?.data?.vfsBacked) ? (
                    <CodeViewWithLogger
                      ref={codeViewRef}
                      projectId={selectedProject?.id || selectedProject?._id}
                      serviceName={selectedResource.data?.name}
                      repositoryUrl={repositoryUrl}
                      branch={selectedRevision?.name || 'main'}
                      onFileSelect={handleFileSelect}
                      onFileChange={(filePath, content) => {
                        devLog('File changed:', filePath);
                      }}
                      // Project context for git operations
                      selectedProject={selectedProject}
                      selectedRevision={selectedRevision}
                      selectedTenant={selectedTenant}
                      selectedResource={selectedResource}
                      configuration={configuration}
                      collections={collections}
                      onProjectContextChange={onProjectContextChange}
                      // Preview control
                      previewUrl={previewUrl}
                      setPreviewUrl={setPreviewUrl}
                      setPreviewError={setPreviewError}
                      setForceGitFetch={setForceGitFetch}
                      autoTriggeredPullRef={autoTriggeredPullRef}
                      onSwitchToChat={handleSwitchToChat}
                      readOnly={isViewOnly}
                      // The file in the URL (`.../code/<file>#L42`: a reload, a shared link, a notification): opened in
                      // the editor with the tree unfolded to it, or "isn't in <service> anymore" when it is gone.
                      autoOpenPath={selectedResource?.type === 'service' ? routeCodeFile.path : null}
                      autoOpenLine={selectedResource?.type === 'service' ? routeCodeFile.line : null}
                      autoOpenStrict
                      // The bottom panel's Terminal tab, when vfs-service allows it for this person here.
                      terminal
                    />
                  ) : selectedResource?.type === 'service' ? (
                    <div className="code-service-details">
                      <div className="code-editor-header">
                        <span className="file-path">{selectedResource.data?.name || 'Service'}</span>
                        <div className="header-actions">
                          <button
                            className="view-mode-btn"
                            onClick={() => setActiveTab('service')}
                            title="View full service details"
                            type="button"
                          >
                            <Icon name="package" size={16} />
                            {' Full Details'}
                          </button>
                        </div>
                      </div>
                      <div className="code-service-info">
                        <p>No source code repository configured for this service.</p>
                        <button
                          className="view-mode-btn"
                          onClick={() => setActiveTab('service')}
                          type="button"
                        >
                          View Service Details
                        </button>
                      </div>
                    </div>
                  ) : params.microservice && !selectedResource ? (
                    // The URL names a service that is not loaded yet (a reload, a shared link): loading, not "select one".
                    <div className="loading-state" data-testid="code-tab-loading">
                      <Icon name="loading" size={24} />
                      <p>Loading file...</p>
                    </div>
                  ) : (
                    <div className="code-editor-empty">
                      <Icon name="code" size={48} />
                      <h3>Source Code</h3>
                      <p>Select a source code microservice from the dropdown above to browse its repository</p>
                    </div>
                  )}
                </div>

                {/* Split View Preview Panel - Only shown in split mode */}
                {splitViewMode === 'split' && previewTabs.length > 0 && (
                  <>
                    {/* Split Resize Handle */}
                    <div 
                      className={`resize-handle ${isResizingSplit ? 'active' : ''}`}
                      onMouseDown={() => setIsResizingSplit(true)}
                    />
                    <div className="split-preview-panel" style={{width: `${100 - splitRatio}%`}}>
                      <div className="split-preview-header">
                        <span className="preview-title">
                          <Icon name="eye" size={16} />
                          Live Preview
                        </span>
                        <div className="preview-actions">
                          <button
                            className="preview-action-btn"
                            onClick={handleCaptureThumbnail}
                            disabled={isCapturingThumbnail || !selectedProject}
                            title={isCapturingThumbnail ? "Capturing thumbnail..." : "Capture thumbnail"}
                            type="button"
                          >
                            {isCapturingThumbnail ? (
                              <span className="capture-spinner">⏳</span>
                            ) : (
                              <Icon name="camera" size={14} />
                            )}
                          </button>
                          <button
                            className="preview-action-btn"
                            onClick={() => {
                              const activeTab = previewTabs[activePreviewTab];
                              if (activeTab?.url) {
                                window.open(activeTab.url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={!previewTabs[activePreviewTab]?.url}
                            title="Open in new tab"
                            type="button"
                          >
                            <Icon name="link" size={14} />
                          </button>
                          <button
                            className="preview-action-btn"
                            onClick={() => setSplitViewMode('editor')}
                            title="Close preview panel"
                            type="button"
                          >
                            <Icon name="close" size={14} />
                          </button>
                        </div>
                      </div>
                    <div className="split-preview-content">
                      {previewTabs[activePreviewTab]?.loading ? (
                        <div className="preview-loading">
                          <div className="loading-spinner" />
                          <p>Loading preview...</p>
                        </div>
                      ) : previewTabs[activePreviewTab]?.error ? (
                        (() => {
                          const errMsg = previewTabs[activePreviewTab].error;
                          if (errMsg.startsWith('__GIT_TOOL_DISABLED__:') || errMsg.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                            const isDisabled = errMsg.startsWith('__GIT_TOOL_DISABLED__:');
                            const pName = errMsg.split(':')[1];
                            const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                            return (
                              <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                                <div className="preview-git-blocked-icon">
                                  <Icon name={pIconName} size={48} glow={true} />
                                </div>
                                <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Not Connected`}</h3>
                                <p>{isDisabled ? `Enable the ${pName} integration in Tools.` : `Connect your ${pName} account in Connectors.`}</p>
                                <button className="preview-git-blocked-btn" onClick={() => { window.location.href = '/tools'; }} type="button">
                                  {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                                </button>
                              </div>
                            );
                          }
                          return (
                            <div className="preview-error">
                              <Icon name="warning" size={24} />
                              <p>{errMsg}</p>
                            </div>
                          );
                        })()
                      ) : previewTabs[activePreviewTab]?.url ? (
                        <iframe
                          className="split-preview-iframe"
                          key={previewTabs[activePreviewTab].iframeKey}
                          sandbox="allow-scripts allow-same-origin allow-forms"
                          src={previewTabs[activePreviewTab].url}
                          title="Split View Preview"
                          onLoad={(e) => handleIframeLoad(e, previewTabs[activePreviewTab])}
                        />
                      ) : (
                        <div className="preview-empty">
                          <Icon name="eye" size={32} />
                          <p>No preview available</p>
                          <p className="hint">Start a service to see live preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* AGENT TAB — the real "Door 3" background-workflow-run experience
              (src/pages/Agents.jsx), embedded here per explicit user request
              to merge Chat/Code/Agent into one screen behind a single toggle,
              rather than a separate top-level /agents route. Distinct from
              the old, now-deleted single-purpose "Background Agents" AI tab
              this file's own comment above documents removing — this reuses
              the real Agents door component itself, not a duplicate.
              readOnly (task #134): the real chat panel to the left already
              has /run, /runs, /cancel slash commands wired to the exact same
              WorkflowsService this view uses — a second live input here
              would just be a duplicate chat. This shows run status/history
              read-only; triggering/cancelling a run happens from the one
              real chat input via /run <workflow>. */}
          {activeTab === 'agent' && (
            <div className="canvas-agent-view">
              <Agents readOnly />
            </div>
          )}
        </div>
        {inProjectAvailable && inProjectOpen && (
          <InThisProjectPanel
            onClose={() => setInProjectOpen(false)}
            onLeaveNote={() => openNotes('leave')}
            onOpenNotes={() => openNotes('handoff')}
            onOpenSuggestions={() => openNotes('suggestions')}
            suggestionsPending={suggestionsPending}
            onManageSharing={() => setSharingFromPanel(true)}
            projectId={sharedPreviewProjectId}
            snapshot={inProjectPresence}
          />
        )}
        {sharingFromPanel && sharedPreviewProjectId && (
          <ShareDialog
            onAccessChange={setProjectAccess}
            onClose={() => setSharingFromPanel(false)}
            projectId={sharedPreviewProjectId}
            projectName={selectedProject?.name || sharedPreviewProjectId}
          />
        )}
        </div>

        {/* Canvas Footer - This is the word-count/Open/Download/Copy action bar
            for the generated-text/image/video content viewer (AI chat output
            shown via the `content` prop), NOT anything related to a live
            container preview. It must never render while the Preview tab is
            showing (or about to show) that live app UI — reuses the same
            isShowingLivePreview check that picks which of the two UIs to
            render above, so they can never disagree. Also hidden for code
            mode, summary tab, and configuration. */}
        {!(activeTab === 'preview' && isShowingLivePreview) &&
         activeTab !== 'code' &&
         activeTab !== 'summary' &&
         selectedResource?.type !== 'configuration' &&
         !brandNewWorkspace &&
         // Only under a selected resource's generated content: never under "Loading project…", "No services yet" or a
         // project coming back (App.jsx always passes a placeholder `content`, "# Ready to Flow!?! …", 15 words).
         Boolean(selectedResource) &&
         // Only with generated content to act on: never under "Loading project…" or a project coming back.
         Boolean(content?.content) &&
         activeTab === 'preview' && (
          <div className="canvas-footer">
            <div className="canvas-footer-left">
              <span className="canvas-word-count">
                {contentType === 'image' ? 
                  `${content.aspect_ratio || 'Custom'} aspect ratio` : 
                  contentType === 'video' ?
                  `${content.duration_seconds || '?'}s · ${content.aspect_ratio || 'Custom'}` :
                  `${content.content?.split(/\s+/).length || 0} words`
                }
              </span>
            </div>
          
          <div className="canvas-footer-right">
            {contentType === 'image' ? (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => {
                    // For images, open in new tab
                    const win = window.open();
                    win.document.write(`<img src="${content.content}" alt="Generated image" style="max-width: 100%; height: auto;" />`);
                  }}
                  type="button"
                >
                  {'Open'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    // Download image
                    const link = document.createElement('a');
                    link.href = content.content;
                    link.download = `${content.title || 'generated-image'}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            ) : contentType === 'video' ? (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => {
                    // For videos, open in new tab
                    const win = window.open();
                    win.document.write(`<video src="${content.content}" controls style="max-width: 100%; height: auto;"></video>`);
                  }}
                  type="button"
                >
                  {'Open'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    // Download video
                    const link = document.createElement('a');
                    link.href = content.content;
                    link.download = `${content.title || 'generated-video'}.mp4`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => navigator.clipboard.writeText(content.content)}
                  type="button"
                >
                  {'Copy'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    const blob = new Blob([content.content], {type: 'text/plain'});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${content.title || 'document'}.${contentType === 'code' ? detectedLanguage : 'md'}`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            )}
          </div>
        </div>
        )}



      {/* Deployment Modal */}
      <Modal
        confirmText={deploymentPipelineId ? 'Close' : 'Deploy'}
        icon="🚀"
        isDangerous={false}
        isLoading={deploymentLoading}
        isOpen={showDeploymentModal}
        onCancel={handleDeploymentClose}
        onClose={handleDeploymentClose}
        onConfirm={deploymentPipelineId ? handleDeploymentClose : handleDeploymentConfirm}
        showCancel={!deploymentPipelineId}
        title={`Deploy to ${selectedEnvironment?.label || selectedEnvironment?.envId || 'Environment'}`}
        type="custom"
      >
        <div className="deployment-modal-content">
          {deploymentError && (
            <div className="deployment-error">
              <span className="error-icon">{'❌'}</span>
              <span className="error-message">{deploymentError}</span>
            </div>
          )}

          {!deploymentPipelineId && !deploymentError && (
            <div className="deployment-info">
              <p className="deployment-message">
                {'This will deploy all services using the '}
                <strong>{selectedRevision?.name || selectedRevision?.id || 'current'}</strong>
                {' revision to the '}
                <strong>{selectedEnvironment?.label || selectedEnvironment?.envId}</strong>
                {' environment.'}
              </p>
              <div className="deployment-details">
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Project:'}</span>
                  <span className="detail-value">{selectedProject?.name}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Environment:'}</span>
                  <span className="detail-value">{selectedEnvironment?.label || selectedEnvironment?.envId}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Namespace:'}</span>
                  <span className="detail-value">{selectedEnvironment?.cluster?.namespace || 'N/A'}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Revision:'}</span>
                  <span className="detail-value">{selectedRevision?.name || selectedRevision?.id}</span>
                </div>
              </div>
            </div>
          )}

          {deploymentPipelineId && (
            <div className="deployment-status">
              <div className="pipeline-info">
                <div className="pipeline-detail-item">
                  <span className="detail-label">{'Pipeline ID:'}</span>
                  <span className="detail-value">{'#'}{deploymentPipelineId}</span>
                </div>
                {pipelineStatus?.status && (
                  <div className="pipeline-status-display">
                    <span className={`pipeline-status-badge ${getPipelineStatusClass(pipelineStatus.status)}`}>
                      {getPipelineStatusIcon(pipelineStatus.status)} {pipelineStatus.status}
                    </span>
                    {pipelinePolling && (
                      <span className="pipeline-polling-indicator">{'⏳ Polling...'}</span>
                    )}
                  </div>
                )}
              </div>

              {pipelineStatus?.webUrl && (
                <div className="pipeline-actions">
                  <ExternalLinkButton
                    href={pipelineStatus.webUrl}
                    variant="primary"
                  >
                    {'View Pipeline in GitLab'}
                  </ExternalLinkButton>
                </div>
              )}

              {pipelineStatus?.stages && pipelineStatus.stages.length > 0 && (
                <div className="pipeline-stages">
                  <h4>{'Pipeline Stages'}</h4>
                  <div className="stages-list">
                    {pipelineStatus.stages.map((stage, idx) => (
                      <div className="stage-item" key={idx}>
                        <span className="stage-icon">{getPipelineStatusIcon(stage.status)}</span>
                        <span className="stage-name">{stage.name}</span>
                        <span className={`stage-status ${getPipelineStatusClass(stage.status)}`}>
                          {stage.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!pipelineStatus && !deploymentError && (
                <p className="loading-message">{'⏳ Loading pipeline status...'}</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Pods Status Modal */}
      <Modal
        confirmText="Close"
        icon="📦"
        isOpen={showPodsModal}
        onClose={handlePodsModalClose}
        showCancel={false}
        title={`Services in ${podsEnvironment?.label || podsEnvironment?.envId || 'Environment'}`}
        type="custom"
      >
        <div className="pods-modal-content">
          {podsError && (
            <div className="deployment-error">
              <span className="error-icon">{'❌'}</span>
              <span className="error-message">{podsError}</span>
            </div>
          )}

          {podsLoading && (
            <p className="loading-message">{'⏳ Loading services status...'}</p>
          )}

          {!podsLoading && !podsError && podsData.length === 0 && (
            <p className="loading-message">{'No services found in this environment'}</p>
          )}

          {!podsLoading && !podsError && podsData.length > 0 && (
            <div className="pods-list">
              {podsData.map((pod, idx) => (
                <div className="pod-item" key={idx}>
                  <div className="pod-header">
                    <div className="pod-name-status">
                      <span className="pod-icon">{getPodStatusIcon(pod.status?.phase)}</span>
                      <span className="pod-name">{pod.metadata?.name || 'Unknown'}</span>
                    </div>
                    <span className={`pod-status-badge ${getPodStatusClass(pod.status?.phase)}`}>
                      {pod.status?.phase || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="pod-details">
                    {pod.metadata?.labels?.['mia-platform.eu/name'] && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Service:'}</span>
                        <span className="pod-detail-value">{pod.metadata.labels['mia-platform.eu/name']}</span>
                      </div>
                    )}
                    {pod.spec?.nodeName && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Node:'}</span>
                        <span className="pod-detail-value">{pod.spec.nodeName}</span>
                      </div>
                    )}
                    {pod.status?.podIP && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'IP:'}</span>
                        <span className="pod-detail-value">{pod.status.podIP}</span>
                      </div>
                    )}
                    {pod.status?.startTime && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Started:'}</span>
                        <span className="pod-detail-value">
                          {new Date(pod.status.startTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {pod.status?.containerStatuses && pod.status.containerStatuses.length > 0 && (
                      <div className="pod-containers">
                        <span className="pod-detail-label">{'Containers:'}</span>
                        <div className="container-list">
                          {pod.status.containerStatuses.map((container, cIdx) => (
                            <div className="container-item" key={cIdx}>
                              <span className="container-icon">
                                {container.ready ? '✅' : '⏳'}
                              </span>
                              <span className="container-name">{container.name}</span>
                              <span className="container-restarts">
                                {container.restartCount > 0 && `🔄 ${container.restartCount}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Pipeline Status Modal */}
      <Modal
        confirmText="Close"
        icon="🚀"
        isOpen={showPipelineModal}
        onClose={() => setShowPipelineModal(false)}
        showCancel={false}
        title="Pipeline Status"
        type="custom"
      >
        <div className="pipeline-status-modal">
          {currentPipelineStatus ? (
            <>
              <div className="pipeline-info">
                <div className="pipeline-detail">
                  <strong>Status:</strong>
                  <span className={`pipeline-status-badge status-${currentPipelineStatus.status}`}>
                    {currentPipelineStatus.status}
                  </span>
                </div>
                {currentPipelineStatus.id && (
                  <div className="pipeline-detail">
                    <strong>Pipeline ID:</strong>
                    <span>#{currentPipelineStatus.id}</span>
                  </div>
                )}
                {currentPipelineStatus.ref && (
                  <div className="pipeline-detail">
                    <strong>Branch:</strong>
                    <span>{currentPipelineStatus.ref}</span>
                  </div>
                )}
                {currentPipelineStatus.sha && (
                  <div className="pipeline-detail">
                    <strong>Commit:</strong>
                    <span>{currentPipelineStatus.sha.substring(0, 8)}</span>
                  </div>
                )}
              </div>
              
              {currentPipelineStatus.webUrl && (
                <div className="pipeline-actions">
                  <ExternalLinkButton
                    href={currentPipelineStatus.webUrl}
                    variant="primary"
                  >
                    View in Tool
                  </ExternalLinkButton>
                </div>
              )}

              {currentPipelineStatus.stages && currentPipelineStatus.stages.length > 0 && (
                <div className="pipeline-stages">
                  <h4>Pipeline Stages</h4>
                  <div className="stages-list">
                    {currentPipelineStatus.stages.map((stage, idx) => (
                      <div className="stage-item" key={idx}>
                        <span className="stage-name">{stage.name}</span>
                        <span className={`stage-status status-${stage.status}`}>
                          {stage.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="modal-message">No pipeline information available</p>
          )}
        </div>
      </Modal>

      {/* Create Project Modal: in sandbox-first just a name (a skeleton vfs project, then Code) */}
      {config.sandboxFirst ? (
        <NewProjectDialog
          onClose={() => setIsCreateProjectModalOpen(false)}
          open={isCreateProjectModalOpen}
          selectedTenant={selectedTenant}
        />
      ) : (
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => setIsCreateProjectModalOpen(false)}
          onCreateProject={handleCreateProject}
          selectedTenant={selectedTenant}
        />
      )}

      <AddServiceChooser
        onAdd={addServiceFlow.add}
        onClose={() => setIsCreateServiceModalOpen(false)}
        open={isCreateServiceModalOpen}
      />
      {addServiceFlow.dialogs}

      {/* Team ▾ › Notes: a comment's file opens as Open in Code does (`flow:open-code`, UnifiedTopBar). */}
      {(selectedProject?.id || selectedProject?._id) && (
        <NotesModal
          focusId={notesDialog?.focusId || null}
          onClose={() => setNotesDialog(null)}
          onOpenFile={({ path, line }) => window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path, line } }))}
          open={Boolean(notesDialog)}
          projectId={selectedProject?.id || selectedProject?._id}
          projectName={collabProjectName}
          since={arrivedAt(selectedProject?.id) || undefined}
          suggestionsPending={suggestionsPending}
          tab={notesDialog?.tab || 'comments'}
        />
      )}


      {/* Push Modal */}
      <PushModal
        isOpen={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        projectId={selectedProject?.id || selectedProject?._id}
        serviceName={selectedResource?.data?.name || selectedResource?.name}
        repositoryUrl={selectedResource?.data?.repositoryUrl || 
                       selectedResource?.data?.repoUrl || 
                       selectedResource?.data?.sshUrl ||
                       selectedResource?.repositoryUrl ||
                       selectedResource?.repoUrl ||
                       selectedResource?.sshUrl}
        gitProjectId={(() => {
          const repoUrl = selectedResource?.data?.repositoryUrl || 
                         selectedResource?.data?.repoUrl || 
                         selectedResource?.data?.sshUrl ||
                         selectedResource?.repositoryUrl ||
                         selectedResource?.repoUrl ||
                         selectedResource?.sshUrl;
          if (!repoUrl) return null;
          try {
            const url = new URL(repoUrl.replace('.git', ''));
            return url.pathname.substring(1); // Remove leading /
          } catch {
            // Fallback: extract project path from URL string
            return repoUrl.split('/').slice(-2).join('/').replace('.git', '');
          }
        })()}
        branch={getResourceGitBranch(selectedResource, selectedProject?.id, 'main')}
      />

      {/* Build Error Modal */}
      <Modal
        isOpen={buildErrorModal.isOpen}
        onClose={() => setBuildErrorModal({ isOpen: false, error: null, serviceName: null })}
        title={`❌ Build Failed: ${buildErrorModal.serviceName || 'Preview'}`}
        type="custom"
        showCancel={false}
        confirmText="Close"
        className="build-error-modal"
        icon="⚠️"
      >
        <div
          className="build-error-output"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '16px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap',
          }}
        >
          {buildErrorModal.error}
        </div>
        <div
          className="build-error-next-steps"
          style={{
            marginTop: '16px',
            padding: '12px',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <strong>💡 Next Steps:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
            <li>Review the error message above for specific file and line numbers</li>
            <li>Fix the issues in your code (check exports, syntax, imports)</li>
            <li>The preview will automatically rebuild after you save your changes</li>
          </ul>
        </div>
        <div style={{
          marginTop: '16px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          {(() => {
            // Extract file paths from error message
            // Look for patterns like "src/App.tsx:3:7" or "at src/main.tsx:3:7"
            const fileMatches = buildErrorModal.error?.match(/(?:at\s+)?(?:src\/|\.\/)?([A-Za-z0-9_\-./]+\.(tsx?|jsx?|css|json|html))(?::(\d+))?/g) || [];
            const files = [...new Set(fileMatches.map(match => {
              const cleaned = match.replace(/^at\s+/, '').replace(/^\.\//, '');
              return cleaned.includes('src/') ? cleaned.split(':')[0] : `src/${cleaned.split(':')[0]}`;
            }))];
            
            return files.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  // Close the modal first so the user sees immediate feedback
                  setBuildErrorModal({ isOpen: false, error: null, serviceName: null });

                  // Then navigate to the file in the code editor
                  const targetFile = generatedFiles?.find(f =>
                    files.some(errorFile => f.path.endsWith(errorFile) || errorFile.endsWith(f.path))
                  );
                  if (targetFile) {
                    setSelectedFile(targetFile);
                    setSelectedResource(null);
                  }
                  setActiveTab('code');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#16825d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.target.style.background = '#0e6b4d'}
                onMouseOut={(e) => e.target.style.background = '#16825d'}
              >
                📄 Open File in Code
              </button>
            );
          })()}
          {onAskAI && (
            <button
              type="button"
              onClick={() => {
                const errorContext = `When building ${buildErrorModal.serviceName || 'React'} I'm getting the following errors:\n\n${buildErrorModal.error}\n\nFix them in the canvas.`;

                // Close the modal first so the user sees immediate feedback
                setBuildErrorModal({ isOpen: false, error: null, serviceName: null });

                // Then perform the side-effects
                setPreviewTabs([]);
                setActiveTab('code');
                onAskAI(errorContext);
              }}
              style={{
                padding: '10px 20px',
                background: '#007acc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => e.target.style.background = '#005a9e'}
              onMouseOut={(e) => e.target.style.background = '#007acc'}
            >
              🤖 Ask AI for Help
            </button>
          )}
          <button
            className="build-error-close-btn"
            onClick={() => setBuildErrorModal({ isOpen: false, error: null, serviceName: null })}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
})

Canvas.displayName = 'Canvas';

Canvas.propTypes = {
  chatSidebarWidth: PropTypes.number,
  content: PropTypes.shape({
    aspect_ratio: PropTypes.string,
    content: PropTypes.string.isRequired,
    duration_seconds: PropTypes.number,
    prompt: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.oneOf(['document', 'code', 'image', 'video']),
  }),
  generatedFiles: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  })),
  initialBranch: PropTypes.string,
  initialMicroservice: PropTypes.string,
  initialTab: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigateHome: PropTypes.func,
  onNewConversation: PropTypes.func,
  onProjectContextChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onShowHistory: PropTypes.func,
  onAskAI: PropTypes.func,
  onWebLLMStatusChange: PropTypes.func,
  projectContext: PropTypes.object,
};

export { clearAllPreviewSessions };
export default Canvas;
